/**
 * 灵魂伴侣功能模块
 * 功能：处理灵魂伴侣相关的交互、关怀消息、关系成长等
 * 作者：技术团队
 * 创建时间：2025-01-14
 * 最后修改：技术团队
 * 版本：v1.0.0
 */

if (typeof CodeUtils === 'undefined' || 
    typeof CodeUtils.safeGetStorage !== 'function' || 
    typeof CodeUtils.safeSetStorage !== 'function') {
  throw new Error('[SoulmateManager] CodeUtils.safeGetStorage / safeSetStorage 未加载，无法初始化灵魂伴侣模块');
}

class SoulmateManager {
  constructor() {
    this.isActive = false;
    this.lastInteractionTime = null;
    this.careMessages = [];
    this.interactionHistory = [];
    this.personalizationManager = null;
    
    // ✅ P1-1修复：添加资源追踪
    this.intervals = [];  // 存储所有定时器ID
    this.eventListeners = [];  // 存储所有事件监听器引用
    this.mutationObservers = [];  // 存储所有MutationObserver
    
    // ✅ 新增：消息队列管理
    this.messageQueue = [];  // 待显示的消息队列
    this.isTyping = false;  // 当前是否正在打字
    this.currentTypingElement = null;  // 当前正在打字的元素
    
    // ✅ 新增：未读消息队列（用于聊天界面关闭时）
    this.unreadMessages = [];  // 未读消息队列
    this.unreadMessageCount = 0;  // 未读消息计数
    
    // ✅ 新增：成就系统
    this.achievements = [];  // 已获得的成就列表
    this.achievementDefinitions = {
      'first_meeting': {
        id: 'first_meeting',
        name: '初次见面',
        description: '与倪大师第一次对话',
        icon: '👋',
        condition: () => {
          const chatHistory = this.getChatHistory();
          return chatHistory && chatHistory.length >= 1;
        }
      },
      'deep_communicator': {
        id: 'deep_communicator',
        name: '深度交流者',
        description: '完成10次深度对话',
        icon: '💬',
        condition: () => {
          // 从 localStorage 获取深度对话计数
          // ✅ 修复：确保是数字类型
          const deepChatCount = Number(CodeUtils.safeGetStorage('deep_chat_count', 0)) || 0;
          return deepChatCount >= 10;
        }
      },
      'emotion_recorder': {
        id: 'emotion_recorder',
        name: '情感记录师',
        description: '记录5篇情感日记',
        icon: '📝',
        condition: () => {
          const diaries = CodeUtils.safeGetStorage('user_diaries', []);
          return diaries && diaries.length >= 5;
        }
      },
      'analysis_master': {
        id: 'analysis_master',
        name: '命理探索者',
        description: '完成所有类型的分析（手相、面相、八字）',
        icon: '🔮',
        condition: () => {
          const analysisData = CodeUtils.safeGetStorage('user_analysis_data', {});
          const hasPalm = analysisData.palm_analysis && analysisData.palm_analysis.has_data;
          const hasFace = analysisData.face_analysis && analysisData.face_analysis.has_data;
          const hasBazi = analysisData.bazi_analysis && analysisData.bazi_analysis.has_data;
          return hasPalm && hasFace && hasBazi;
        }
      },
      'soulmate_level': {
        id: 'soulmate_level',
        name: '灵魂伴侣',
        description: '达到最高等级（灵魂伴侣）',
        icon: '💝',
        condition: () => {
          if (window.personalizationManager) {
            const info = window.personalizationManager.getExperienceInfo();
            return info.level >= 5;
          }
          return false;
        }
      },
      'consecutive_7days': {
        id: 'consecutive_7days',
        name: '七日坚持',
        description: '连续登录7天',
        icon: '🔥',
        condition: () => {
          if (window.personalizationManager) {
            const info = window.personalizationManager.getExperienceInfo();
            return info.consecutiveDays >= 7;
          }
          return false;
        }
      },
      'consecutive_30days': {
        id: 'consecutive_30days',
        name: '月月相伴',
        description: '连续登录30天',
        icon: '⭐',
        condition: () => {
          if (window.personalizationManager) {
            const info = window.personalizationManager.getExperienceInfo();
            return info.consecutiveDays >= 30;
          }
          return false;
        }
      },
      'hundred_chats': {
        id: 'hundred_chats',
        name: '百次对话',
        description: '累计对话100次',
        icon: '💯',
        condition: () => {
          const chatHistory = this.getChatHistory();
          return chatHistory && chatHistory.length >= 100;
        }
      },
      'fifty_chats': {
        id: 'fifty_chats',
        name: '半百对话',
        description: '累计对话50次',
        icon: '💬',
        condition: () => {
          const chatHistory = this.getChatHistory();
          return chatHistory && chatHistory.length >= 50;
        }
      },
      'two_hundred_chats': {
        id: 'two_hundred_chats',
        name: '双百对话',
        description: '累计对话200次',
        icon: '🎯',
        condition: () => {
          const chatHistory = this.getChatHistory();
          return chatHistory && chatHistory.length >= 200;
        }
      },
      'first_share': {
        id: 'first_share',
        name: '首次分享',
        description: '第一次分享内容',
        icon: '📤',
        condition: () => {
          // 从 localStorage 获取分享次数
          // ✅ 修复：确保是数字类型，保持类型一致性
          const shareCount = Number(CodeUtils.safeGetStorage('share_count', 0)) || 0;
          return shareCount >= 1;
        }
      },
      'first_payment': {
        id: 'first_payment',
        name: '首次付费',
        description: '第一次完成付费',
        icon: '💎',
        condition: () => {
          // 检查是否有付费记录
          const paymentHistory = CodeUtils.safeGetStorage('payment_history', []);
          const isVip = CodeUtils.safeGetStorage('is_vip', false);
          return (paymentHistory && paymentHistory.length > 0) || isVip;
        }
      },
      'loyal_customer': {
        id: 'loyal_customer',
        name: '忠实客户',
        description: '完成3次付费',
        icon: '👑',
        condition: () => {
          const paymentHistory = CodeUtils.safeGetStorage('payment_history', []);
          return paymentHistory && paymentHistory.length >= 3;
        }
      },
      'vip_member': {
        id: 'vip_member',
        name: 'VIP会员',
        description: '成为VIP会员',
        icon: '⭐',
        condition: () => {
          const isVip = CodeUtils.safeGetStorage('is_vip', false);
          return isVip === true;
        }
      }
    };
    
    // ✅ 新增：随机奖励机制
    this.randomRewards = {
      enabled: true,
      lastRewardTime: null,
      rewardHistory: [],  // 已获得的奖励历史
      rewardCooldown: 24 * 60 * 60 * 1000,  // 24小时冷却时间
      triggerChance: 0.15,  // 15% 触发概率
      specialDays: {
        // 特殊日子的专属内容
        '01-01': '新年祝福',
        '02-14': '情人节关怀',
        '05-20': '520特别礼物',
        '08-15': '中秋节祝福',
        '12-25': '圣诞节祝福',
        // 用户的生日（需要从用户数据获取）
      }
    };

    // 随机奖励定义
    this.rewardDefinitions = {
      'mystery_message': {
        id: 'mystery_message',
        type: 'message',
        name: '神秘消息',
        description: '收到一条神秘的消息',
        icon: '🔮',
        messages: [
          '夜深了，我感受到你内心的波动。有些事情，看似复杂，其实答案就在你心里。',
          '你知道吗？根据星象，今天对你来说是个特殊的日子。',
          '我刚刚做了一个关于你的梦，梦里的你充满光芒。',
          '今天的气场告诉我，你即将迎来一个重要的转折点。',
          '我感受到你内心深处的渴望，那是一个美好的愿景。'
        ]
      },
      'fortune_prophecy': {
        id: 'fortune_prophecy',
        type: 'prophecy',
        name: '神秘预言',
        description: '获得一条关于未来的神秘预言',
        icon: '✨',
        messages: [
          '根据你的命理，未来3个月内，你会在事业上有重要突破。',
          '你的面相告诉我，近期会有贵人相助，记得把握机会。',
          '从你的手相来看，未来半年内，感情方面会有新变化。',
          '根据星象，这个月对你来说是个转折点，保持积极心态。',
          '我感知到，下个月你会遇到一个改变命运的机会。'
        ]
      },
      'special_title': {
        id: 'special_title',
        type: 'title',
        name: '特殊称号',
        description: '获得一个专属称号',
        icon: '👑',
        titles: [
          { name: '命理探索者', description: '对命理有着深度探索精神' },
          { name: '情感智者', description: '在情感方面有着深刻洞察' },
          { name: '灵魂知己', description: '与倪大师建立了深厚的连接' },
          { name: '相术传承者', description: '拥有相术传承的潜质' },
          { name: '命运改写者', description: '勇于改变自己的命运' }
        ]
      },
      'hidden_feature': {
        id: 'hidden_feature',
        type: 'feature',
        name: '隐藏功能',
        description: '解锁一个隐藏功能',
        icon: '🎁',
        features: [
          { name: '深夜模式', description: '在晚上使用会有特殊效果' },
          { name: '冥想指引', description: '获得冥想和内心平静的指导' },
          { name: '前世解读', description: '可以询问关于前世的秘密' },
          { name: '能量场分析', description: '分析你的能量场和磁场' }
        ]
      },
      'bonus_experience': {
        id: 'bonus_experience',
        type: 'experience',
        name: '经验值奖励',
        description: '获得额外的经验值',
        icon: '⭐',
        points: [50, 100, 150]  // 随机奖励的经验值
      },
      'special_day_content': {
        id: 'special_day_content',
        type: 'special',
        name: '特殊日子礼物',
        description: '在特殊日子收到专属礼物',
        icon: '🎉',
        messages: {
          '01-01': '新年快乐！愿你在新的一年里，事业顺利，感情美满，身体健康！',
          '02-14': '情人节快乐！愿你的爱情像相术一样，准确而美好。',
          '05-20': '520快乐！在这个特殊的日子里，我想对你说：你值得被爱。',
          '08-15': '中秋节快乐！愿你像月亮一样，圆满而美好。',
          '12-25': '圣诞节快乐！愿你的生活充满惊喜和温暖。'
        }
      }
    };
    
    // ✅ 新增：视觉真实感系统
    this.visualSystem = {
      avatarState: 'idle',  // idle/thinking/understanding/caring/speaking
      lastBlinkTime: 0,
      blinkInterval: 3000,  // 3秒眨眼一次
      typingSoundEnabled: true,
      messageSoundEnabled: true,
      avatarExpressions: {
        idle: '😊',
        thinking: '🤔',
        understanding: '😌',
        caring: '💝',
        speaking: '🗣️'
      }
    };
    this.blinkTimer = null;  // 眨眼定时器
    
    // ✅ 新增：音效文件配置
    this.soundFiles = {
      typing: '/static/sounds/typing.mp3',
      message: '/static/sounds/message.mp3',
      levelup: '/static/sounds/levelup.mp3',
      achievement: '/static/sounds/achievement.mp3'
    };
    
    // 预加载音效
    this.soundCache = {};
    
    // ✅ 新增：心理疗愈系统
    this.healingSystem = {
      enabled: true,
      growthRecords: [],  // 用户成长记录
      emotionalSupportLevel: 0,  // 情感支持级别（0-10）
      deepConversationCount: 0,  // 深度对话次数
      vulnerabilityMoments: []  // 用户脆弱时刻记录
    };
    
    // ✅ 新增：情感依赖机制系统
    this.addictionSystem = {
      enabled: true,
      dailyCareScheduled: false,  // 每日关怀是否已安排
      lastCareTime: null,  // 最后关怀时间
      careScheduleTimes: [9, 14, 20],  // 关怀时间点（9点、14点、20点）
      trustLevel: 0,  // 信任级别（0-10）
      deepConnectionLevel: 0,  // 深度连接级别（0-10）
      privateSpaceEnabled: true,  // 私密空间是否启用
      deepConversationPrompts: []  // 深度对话引导记录
    };
    
    this.init();
  }

  /**
   * 初始化灵魂伴侣系统
   */
  init() {
    try {
      this.loadSoulmateData();
      this.ensureBackendAnalysisSync();
      this.setupSoulmateInterface();
      this.startActiveCare();
      this.bindSoulmateEvents();
      
      // ✅ 新增：加载已获得成就
      this.loadAchievements();
      
      // ✅ 新增：加载随机奖励历史
      this.loadRandomRewardHistory();
      
      // ✅ 新增：检查成就（延迟执行，确保所有系统已初始化）
      setTimeout(() => {
        this.checkAchievements();
      }, 1000);
      
      // ✅ 新增：启动随机奖励检查（延迟执行）
      setTimeout(() => {
        this.checkRandomRewards();
      }, 2000);
      
      // ✅ 新增：启动头像自动眨眼
      setTimeout(() => {
        this.startAvatarBlinking();
      }, 3000);
      
      // ✅ 新增：预加载音效文件
      this.preloadSounds();
      
      // ✅ 新增：加载疗愈系统数据
      this.loadHealingData();
      
      // ✅ 新增：启动情感依赖机制
      setTimeout(() => {
        this.createAddictionMechanism();
        this.createPrivateSpace();
      }, 5000);
      
      // ✅ P1-1修复：监听等级升级事件，播放庆祝音效，保存事件监听器引用
      const levelUpHandler = (e) => {
        this.playLevelUpSound();
        // 更新头像为庆祝状态
        this.updateAvatarExpression('caring');
        setTimeout(() => {
          this.updateAvatarExpression('idle');
        }, 2000);
        
        // ✅ 新增：如果个人信息面板打开，更新显示
        this.updateSoulmateInfoDisplay();
      };
      document.addEventListener('levelUp', levelUpHandler);
      this.eventListeners.push({ element: document, event: 'levelUp', handler: levelUpHandler });
      
      // ✅ P1-1修复：监听页面可见性变化，保存事件监听器引用
      const visibilityChangeHandler = () => {
        if (!document.hidden && this.randomRewards.enabled) {
          // 用户回到页面，有小概率触发随机奖励
          setTimeout(() => {
            if (Math.random() < 0.1) {  // 10% 概率
              this.checkRandomRewards();
            }
          }, 3000);
          
          // ✅ 新增：检查是否需要发送关怀
          const lastCareTime = this.addictionSystem.lastCareTime || CodeUtils.safeGetStorage('last_care_time', null);
          if (!lastCareTime) {
            // 首次访问，发送欢迎关怀
            setTimeout(() => {
              const welcomeMessage = this.generateIntelligentCare();
              if (this.isChatOpen()) {
                this.addChatMessage('assistant', welcomeMessage, true);
                this.saveChatHistory();
              } else {
                this.showCareNotification(welcomeMessage);
              }
              this.addictionSystem.lastCareTime = new Date().toISOString();
              CodeUtils.safeSetStorage('last_care_time', this.addictionSystem.lastCareTime);
            }, 2000);
          } else {
            // 检查距离上次关怀的时间
            const hoursSinceCare = (Date.now() - new Date(lastCareTime).getTime()) / (1000 * 60 * 60);
            if (hoursSinceCare >= 12) {
              // 超过12小时，发送关怀
              setTimeout(() => {
                this.sendScheduledCare();
              }, 2000);
            }
          }
        }
      };
      document.addEventListener('visibilitychange', visibilityChangeHandler);
      this.eventListeners.push({ element: document, event: 'visibilitychange', handler: visibilityChangeHandler });
      
      if (typeof CodeUtils !== 'undefined') {
        CodeUtils.log('SoulmateManager', 'init', '灵魂伴侣系统初始化完成');
      } else {
        console.log('SoulmateManager: 灵魂伴侣系统初始化完成');
      }
    } catch (error) {
      console.error('SoulmateManager初始化失败:', error);
    }
  }

  /**
   * 加载灵魂伴侣数据
   */
  loadSoulmateData() {
    this.lastInteractionTime = CodeUtils.safeGetStorage('last_interaction_time', null);
    this.careMessages = CodeUtils.safeGetStorage('care_messages', []);
    this.interactionHistory = CodeUtils.safeGetStorage('interaction_history', []);
    
    // 获取个性化管理器实例
    if (window.personalizationManager) {
      this.personalizationManager = window.personalizationManager;
    }
  }

  ensureAnalysisProfileSelf(analysisData) {
    if (!analysisData || typeof analysisData !== 'object') {
      return false;
    }
    
    if (!analysisData.profile) {
      analysisData.profile = {};
    }
    
    if (analysisData.profile.target_type !== 'self') {
      analysisData.profile.target_type = 'self';
      analysisData.profile.updated_at = analysisData.profile.updated_at || new Date().toISOString();
      return true;
    }
    
    return false;
  }

  async ensureBackendAnalysisSync() {
    try {
      const localData = CodeUtils.safeGetStorage('user_analysis_data', null);
      if (!localData || typeof localData !== 'object') {
        return;
      }
      
      const patched = this.ensureAnalysisProfileSelf(localData);
      if (patched) {
        CodeUtils.safeSetStorage('user_analysis_data', localData);
      }
      
      if (!localData.profile || localData.profile.target_type !== 'self') {
        return;
      }
      
      const sessionId = localData.session_id || CodeUtils.safeGetStorage('session_id', '');
      if (!sessionId) {
        return;
      }
      
      const response = await fetch(`/api/check_analysis_data?session_id=${encodeURIComponent(sessionId)}`, {
        method: 'GET',
        credentials: 'include'
      });
      
      if (!response.ok) {
        console.warn('⚠️ [SoulmateManager] 检查后端分析数据失败:', response.status, response.statusText);
        return;
      }
      
      const result = await response.json();
      const backendHasData = !!(result?.has_data && result.analysis_data && Object.keys(result.analysis_data).length > 0);
      if (!backendHasData) {
        console.log('ℹ️ [SoulmateManager] 后端缺少分析数据，触发补偿同步');
        await this.syncAnalysisDataToBackend(localData);
      }
    } catch (error) {
      console.warn('⚠️ [SoulmateManager] 启动期分析数据补偿失败:', error);
    }
  }

  /**
   * 设置灵魂伴侣界面
   */
  setupSoulmateInterface() {
    // 只创建侧边栏，不立即创建聊天界面和按钮
    this.createSoulmateSidebar();
    
    // ✅ 修复：检查老用户，如果已分析过，自动创建小人按钮
    this.checkAndCreateEntryForOldUsers();
    
    console.log('✅ 灵魂伴侣界面已初始化');
  }

  /**
   * 检查用户VIP状态
   */
  isVipUser() {
    try {
      const vipFlags = [
        CodeUtils.safeGetStorage('is_vip', false),
        CodeUtils.safeGetStorage('isVip', false)
      ];
      return vipFlags.some(flag => flag === true);
    } catch (error) {
      console.error('检查VIP状态失败:', error);
      return false;
    }
  }

  /**
   * 检查并更新VIP状态（用于支付成功后刷新UI）
   */
  checkAndUpdateVipStatus() {
    // 每2秒检查一次VIP状态，最多检查30次（1分钟）
    let checkCount = 0;
    const maxChecks = 30;
    
    const checkInterval = setInterval(() => {
      checkCount++;
      const isVip = this.isVipUser();
      
      if (isVip) {
        // VIP状态已激活，更新UI
        this.updateVipUI();
        clearInterval(checkInterval);
        console.log('✅ VIP状态已更新');
      } else if (checkCount >= maxChecks) {
        // 超时，停止检查
        clearInterval(checkInterval);
        console.log('⏱️ VIP状态检查超时');
      }
    }, 2000);
  }

  /**
   * 更新VIP相关UI
   */
  updateVipUI() {
    const isVip = this.isVipUser();
    
    // 更新功能卡片锁定状态
    const vipLockedCards = document.querySelectorAll('.vip-locked');
    vipLockedCards.forEach(card => {
      if (isVip) {
        card.classList.remove('vip-locked');
        card.classList.add('vip-unlocked');
        
        // ✅ 修复：设置卡片完全不透明，让VIP功能清晰可见
        card.style.opacity = '1';
        card.style.background = 'rgba(255, 255, 255, 0.08)'; // 稍微提高背景亮度
        card.style.border = '1px solid rgba(255, 255, 255, 0.2)'; // 提高边框亮度
        
        // ✅ 修复：提高卡片标题文字亮度（改进选择器）
        const cardHeader = card.querySelector('.card-header');
        if (cardHeader) {
          // ✅ 修复：设置card-header背景稍微暗一些，让标题文字更清晰
          cardHeader.style.setProperty('background', 'rgba(0, 0, 0, 0.2)', 'important');
          cardHeader.style.setProperty('border-radius', '6px', 'important');
          cardHeader.style.setProperty('padding', '8px 12px', 'important');
          cardHeader.style.setProperty('margin-bottom', '10px', 'important');
          
          // 查找所有span，然后找到包含标题文字的span（排除图标和VIP标签）
          const allSpans = cardHeader.querySelectorAll('span');
          let titleSpan = null;
          
          // 查找包含标题文字的span（有font-weight样式且不是图标和VIP标签）
          for (let span of allSpans) {
            const style = span.getAttribute('style') || '';
            const hasFontWeight = style.includes('font-weight');
            const isNotIcon = !style.includes('font-size: 1.2rem'); // 排除图标
            const isNotBadge = !span.classList.contains('vip-badge'); // 排除VIP标签
            
            if (hasFontWeight && isNotIcon && isNotBadge) {
              titleSpan = span;
              break;
            }
          }
          
          // 如果没找到，尝试更通用的方法：查找第二个span（通常是标题）
          if (!titleSpan && allSpans.length >= 2) {
            titleSpan = allSpans[1]; // 第二个span通常是标题
          }
          
          if (titleSpan) {
            // ✅ 修复：使用金色突出VIP功能标题，更醒目
            titleSpan.style.setProperty('color', '#ffd700', 'important');
            titleSpan.style.setProperty('font-weight', '600', 'important');
            titleSpan.style.setProperty('opacity', '1', 'important');
            titleSpan.style.setProperty('text-shadow', '0 2px 8px rgba(255, 215, 0, 0.5)', 'important'); // 添加金色阴影，更醒目
          }
        }
        
        // ✅ 修复：提高VIP标签的亮度
        const vipBadge = card.querySelector('.vip-badge');
        if (vipBadge) {
          // ✅ 修复：VIP用户显示"VIP会员"，明确身份
          vipBadge.textContent = 'VIP会员';
          vipBadge.style.setProperty('background', 'rgba(255, 215, 0, 0.3)', 'important');
          vipBadge.style.setProperty('border', '1px solid rgba(255, 215, 0, 0.6)', 'important');
          vipBadge.style.setProperty('color', '#ffd700', 'important');
          vipBadge.style.setProperty('font-weight', '600', 'important');
        }
        
        // 移除锁定按钮，显示功能按钮
        const lockBtn = card.querySelector('.btn-upgrade-vip');
        if (lockBtn) lockBtn.style.display = 'none';
        
        // ✅ 修复：确保 card-actions 容器可见
        const cardActions = card.querySelector('.card-actions');
        if (cardActions) {
          cardActions.style.setProperty('display', 'flex', 'important');
          cardActions.style.setProperty('opacity', '1', 'important');
          cardActions.style.setProperty('visibility', 'visible', 'important');
        }
        
        // ✅ 修复：更新所有按钮（包括 locked 和 free），确保都清晰可见
        const allActionBtns = card.querySelectorAll('.action-btn');
        allActionBtns.forEach(btn => {
          // 如果是锁定按钮，移除锁定状态
          if (btn.classList.contains('locked')) {
            btn.classList.remove('locked');
            btn.style.setProperty('cursor', 'pointer', 'important');
          }
          
          // ✅ 修复：确保所有按钮都清晰可见（包括free按钮）
          btn.style.setProperty('background', 'rgba(102, 126, 234, 0.4)', 'important');
          btn.style.setProperty('border-color', 'rgba(102, 126, 234, 0.7)', 'important');
          btn.style.setProperty('color', 'rgba(255, 255, 255, 0.95)', 'important');
          btn.style.setProperty('font-weight', '500', 'important');
          btn.style.setProperty('opacity', '1', 'important');
          btn.style.setProperty('visibility', 'visible', 'important');
        });
      } else {
        // ✅ 修复：优化非VIP用户的卡片显示，提高可见性但保持锁定状态
        // 提高卡片透明度，但保持锁定视觉提示
        card.style.setProperty('opacity', '0.85', 'important'); // 从0.6提升到0.85，更清晰
        card.style.setProperty('background', 'rgba(255, 255, 255, 0.06)', 'important');
        card.style.setProperty('border', '1px solid rgba(255, 255, 255, 0.15)', 'important');
        
        // ✅ 修复：优化非VIP用户的标题显示
        const cardHeader = card.querySelector('.card-header');
        if (cardHeader) {
          // 设置card-header背景，提升对比度
          cardHeader.style.setProperty('background', 'rgba(0, 0, 0, 0.15)', 'important');
          cardHeader.style.setProperty('border-radius', '6px', 'important');
          cardHeader.style.setProperty('padding', '8px 12px', 'important');
          cardHeader.style.setProperty('margin-bottom', '10px', 'important');
          
          // 查找标题span
          const allSpans = cardHeader.querySelectorAll('span');
          let titleSpan = null;
          
          for (let span of allSpans) {
            const style = span.getAttribute('style') || '';
            const hasFontWeight = style.includes('font-weight');
            const isNotIcon = !style.includes('font-size: 1.2rem');
            const isNotBadge = !span.classList.contains('vip-badge');
            
            if (hasFontWeight && isNotIcon && isNotBadge) {
              titleSpan = span;
              break;
            }
          }
          
          if (!titleSpan && allSpans.length >= 2) {
            titleSpan = allSpans[1];
          }
          
          if (titleSpan) {
            // ✅ 修复：非VIP用户使用更亮的白色，确保清晰可见
            titleSpan.style.setProperty('color', 'rgba(255, 255, 255, 0.95)', 'important');
            titleSpan.style.setProperty('font-weight', '600', 'important');
            titleSpan.style.setProperty('opacity', '1', 'important');
            titleSpan.style.setProperty('text-shadow', '0 2px 8px rgba(255, 255, 255, 0.3)', 'important');
          }
        }
        
        // ✅ 修复：优化非VIP用户的VIP标签显示
        const vipBadge = card.querySelector('.vip-badge');
        if (vipBadge) {
          // ✅ 修复：非VIP用户显示"升级VIP"而不是"VIP"，避免歧义
          vipBadge.textContent = '升级VIP';
          vipBadge.style.setProperty('background', 'rgba(255, 215, 0, 0.25)', 'important');
          vipBadge.style.setProperty('border', '1px solid rgba(255, 215, 0, 0.5)', 'important');
          vipBadge.style.setProperty('color', '#ffd700', 'important'); // 使用纯金色，更清晰
          vipBadge.style.setProperty('font-weight', '600', 'important');
        }
        
        // ✅ 修复：确保非VIP用户的card-actions容器可见
        const cardActions = card.querySelector('.card-actions');
        if (cardActions) {
          cardActions.style.setProperty('display', 'flex', 'important');
          cardActions.style.setProperty('opacity', '1', 'important'); // 改为完全不透明
          cardActions.style.setProperty('visibility', 'visible', 'important');
        }
        
        // ✅ 修复：优化非VIP用户的按钮显示（保持锁定样式但更清晰）
        const allActionBtns = card.querySelectorAll('.action-btn');
        allActionBtns.forEach(btn => {
          // 保持锁定状态，但提高可见性
          if (btn.classList.contains('locked')) {
            // ✅ 修复：锁定按钮使用更亮的文字，确保清晰可见
            btn.style.setProperty('background', 'rgba(108, 117, 125, 0.4)', 'important');
            btn.style.setProperty('border-color', 'rgba(108, 117, 125, 0.6)', 'important');
            btn.style.setProperty('color', 'rgba(255, 255, 255, 0.95)', 'important'); // 从0.85提升到0.95
            btn.style.setProperty('font-weight', '500', 'important');
            btn.style.setProperty('opacity', '1', 'important'); // 从0.9改为1，完全不透明
            btn.style.setProperty('visibility', 'visible', 'important');
            btn.style.setProperty('cursor', 'not-allowed', 'important');
          } else if (btn.classList.contains('free')) {
            // ✅ 修复：free按钮保持正常样式，确保清晰可见
            btn.style.setProperty('background', 'rgba(102, 126, 234, 0.4)', 'important'); // 从0.35提升到0.4
            btn.style.setProperty('border-color', 'rgba(102, 126, 234, 0.7)', 'important'); // 从0.6提升到0.7
            btn.style.setProperty('color', 'rgba(255, 255, 255, 0.95)', 'important'); // 从0.9提升到0.95
            btn.style.setProperty('font-weight', '500', 'important');
            btn.style.setProperty('opacity', '1', 'important'); // 从0.9改为1，完全不透明
            btn.style.setProperty('visibility', 'visible', 'important');
          }
        });
      }
    });
    
    // ✅ 修复：优化免费卡片（深度对话）的显示
    const freeCards = document.querySelectorAll('.action-card.free');
    freeCards.forEach(card => {
      // 设置卡片样式
      card.style.setProperty('opacity', '1', 'important');
      card.style.setProperty('background', 'rgba(102, 126, 234, 0.2)', 'important'); // 稍微提高背景亮度
      card.style.setProperty('border', '1px solid rgba(102, 126, 234, 0.4)', 'important'); // 提高边框亮度
      
      // ✅ 修复：优化免费卡片的标题显示
      const cardHeader = card.querySelector('.card-header');
      if (cardHeader) {
        // 设置card-header背景，提升对比度
        cardHeader.style.setProperty('background', 'rgba(0, 0, 0, 0.2)', 'important');
        cardHeader.style.setProperty('border-radius', '6px', 'important');
        cardHeader.style.setProperty('padding', '8px 12px', 'important');
        cardHeader.style.setProperty('margin-bottom', '10px', 'important');
        
        // 查找标题span
        const allSpans = cardHeader.querySelectorAll('span');
        let titleSpan = null;
        
        for (let span of allSpans) {
          const style = span.getAttribute('style') || '';
          const hasFontWeight = style.includes('font-weight');
          const isNotIcon = !style.includes('font-size: 1.2rem');
          
          if (hasFontWeight && isNotIcon) {
            titleSpan = span;
            break;
          }
        }
        
        // 如果没找到，使用第二个span（通常是标题）
        if (!titleSpan && allSpans.length >= 2) {
          titleSpan = allSpans[1];
        }
        
        if (titleSpan) {
          // ✅ 修复：使用更亮的白色，确保清晰可见
          titleSpan.style.setProperty('color', 'rgba(255, 255, 255, 0.95)', 'important');
          titleSpan.style.setProperty('font-weight', '600', 'important');
          titleSpan.style.setProperty('opacity', '1', 'important');
          titleSpan.style.setProperty('text-shadow', '0 2px 8px rgba(255, 255, 255, 0.3)', 'important');
        }
      }
      
      // ✅ 修复：确保免费卡片的按钮也清晰可见
      const allActionBtns = card.querySelectorAll('.action-btn');
      allActionBtns.forEach(btn => {
        btn.style.setProperty('background', 'rgba(102, 126, 234, 0.4)', 'important');
        btn.style.setProperty('border-color', 'rgba(102, 126, 234, 0.7)', 'important');
        btn.style.setProperty('color', 'rgba(255, 255, 255, 0.95)', 'important');
        btn.style.setProperty('font-weight', '500', 'important');
        btn.style.setProperty('opacity', '1', 'important');
        btn.style.setProperty('visibility', 'visible', 'important');
      });
    });
    
    // 更新VIP按钮显示
    const vipBtn = document.getElementById('soulmate-vip-btn');
    if (vipBtn) {
      if (isVip) {
        vipBtn.textContent = '💎 VIP会员';
        vipBtn.style.background = 'rgba(255, 215, 0, 0.3)';
      } else {
        vipBtn.textContent = '💎 升级VIP';
        vipBtn.style.background = 'rgba(255, 215, 0, 0.2)';
      }
    }
    
    console.log(`✅ VIP UI已更新，状态: ${isVip ? '已激活' : '未激活'}`);
  }

  /**
   * 检查老用户并创建入口（如果用户已分析过）
   */
  checkAndCreateEntryForOldUsers() {
    try {
      // 1. 检查分析数据
      const analysisData = CodeUtils.safeGetStorage('user_analysis_data', {});
      const hasAnalysis = analysisData && analysisData.has_data;
      
      // 检查是否有分析内容（手相、面相、八字任一即可）
      const hasPalm = analysisData.palm_analysis && analysisData.palm_analysis.has_data;
      const hasFace = analysisData.face_analysis && analysisData.face_analysis.has_data;
      const hasBazi = analysisData.bazi_analysis && analysisData.bazi_analysis.has_data;
      const hasAnyAnalysis = hasPalm || hasFace || hasBazi;
      
      // 2. 检查对话历史中是否有分析记录
      const ctx = CodeUtils.safeGetStorage('nm_ctx_v1', []);
      const hasAnalysisInHistory = ctx && ctx.length > 0 && ctx.some(turn => 
        turn.type === 'deep_analysis' || 
        (turn.content && (turn.content.includes('分析') || turn.content.includes('特征')))
      );
      
      // 3. 如果用户已分析过，创建小人按钮和聊天界面
      if (hasAnalysis || hasAnyAnalysis || hasAnalysisInHistory) {
        console.log('✅ 检测到老用户，自动创建灵魂伴侣入口');
        
        // 延迟创建，确保DOM已准备好
        setTimeout(() => {
          // 创建聊天界面（如果不存在）
          if (!document.getElementById('soulmate-chat-interface')) {
            this.createSoulmateChatInterface();
            console.log('✅ 老用户：聊天界面已创建');
          }
          
          // 创建小人按钮（如果不存在）
          if (!document.getElementById('soulmate-chat-toggle')) {
            this.createChatToggle();
            console.log('✅ 老用户：小人按钮已创建');
          }
        }, 500);
      } else {
        console.log('ℹ️ 新用户，等待分析完成后创建入口');
      }
    } catch (error) {
      console.error('❌ 检查老用户状态失败:', error);
    }
  }

  /**
   * 创建灵魂伴侣侧边栏
   */
  createSoulmateSidebar() {
    const sidebar = document.createElement('div');
    sidebar.id = 'soulmate-sidebar';
    sidebar.className = 'soulmate-sidebar';
    sidebar.innerHTML = `
      <div class="soulmate-header">
        <div class="soulmate-avatar">
          <img src="/static/uploads/portraits/default.jpg" alt="倪大师" id="soulmate-avatar">
        </div>
        <div class="soulmate-info">
          <h3 class="soulmate-name">倪大师</h3>
          <p class="soulmate-status">在线</p>
        </div>
        <button class="soulmate-toggle" id="soulmate-toggle">🧙</button>
      </div>
      <div class="soulmate-content" id="soulmate-content">
        <div class="soulmate-messages" id="soulmate-messages">
          <div class="soulmate-message">
            <div class="message-avatar">
              <img src="/static/uploads/portraits/default.jpg" alt="倪大师">
            </div>
            <div class="message-content">
              <p class="message-text">你好，我是倪大师，很高兴为你服务！</p>
              <span class="message-time">刚刚</span>
            </div>
          </div>
        </div>
        <div class="soulmate-input">
          <input type="text" id="soulmate-input" placeholder="和倪大师聊聊...">
          <button id="soulmate-send">发送</button>
        </div>
      </div>
    `;

    // 添加样式
    this.addSoulmateStyles();
    document.body.appendChild(sidebar);
  }

  /**
   * 创建灵魂伴侣独立聊天界面
   */
  createSoulmateChatInterface() {
    console.log('🎯 createSoulmateChatInterface() 被调用');
    
    // 检查是否已存在，避免重复创建
    if (document.getElementById('soulmate-chat-interface')) {
      console.log('⚠️ 聊天界面已存在，跳过创建');
      return;
    }
    
    console.log('✅ 开始创建聊天界面...');

    // ✅ 修复：创建背景遮罩层（如果不存在）
    let backdrop = document.getElementById('soulmate-chat-backdrop');
    if (!backdrop) {
      backdrop = document.createElement('div');
      backdrop.id = 'soulmate-chat-backdrop';
      backdrop.className = 'soulmate-chat-backdrop';
      backdrop.style.cssText = `
        position: fixed;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        background: rgba(0, 0, 0, 0.5);
        backdrop-filter: blur(4px);
        z-index: 9999;
        display: none;
        opacity: 0;
        transition: opacity 0.3s ease;
      `;
      
      // 点击遮罩关闭聊天窗口
      backdrop.addEventListener('click', (e) => {
        // 只响应点击遮罩本身，不响应点击子元素
        if (e.target === backdrop) {
          this.closeSoulmateChat();
        }
      });
      
      document.body.appendChild(backdrop);
    }

    // 创建聊天界面容器
    const chatInterface = document.createElement('div');
    chatInterface.id = 'soulmate-chat-interface';
    chatInterface.className = 'soulmate-chat-interface';
    
    chatInterface.innerHTML = `
      <!-- 左侧：聊天区域 -->
      <div style="
        flex: 1;
        display: flex;
        flex-direction: column;
        min-width: 0;
        overflow: hidden;
      ">
        <!-- 聊天头部 -->
        <div class="chat-header">
          <div style="display: flex; align-items: center; gap: 10px; flex: 1;">
            <div id="soulmate-chat-avatar" class="soulmate-avatar-animated" style="width: 40px; height: 40px; border-radius: 50%; background: rgba(255,255,255,0.2); display: flex; align-items: center; justify-content: center; font-size: 20px; cursor: pointer; transition: all 0.3s ease;">
              <span id="avatar-emoji">🧙</span>
            </div>
            <div style="flex: 1;">
              <h3 style="margin: 0; font-size: 18px; font-weight: 600;">倪大师</h3>
              <p style="margin: 0; font-size: 12px; opacity: 0.9;">你的专属灵魂伴侣</p>
              <!-- ✅ 新增：成长进度显示 -->
              <div id="soulmate-growth-progress" style="margin-top: 5px; display: none;">
                <div style="display: flex; align-items: center; gap: 8px; font-size: 11px;">
                  <span style="color: #ffd700; font-weight: 600;">Lv.<span id="growth-level">1</span></span>
                  <div style="flex: 1; height: 4px; background: rgba(255,255,255,0.2); border-radius: 2px; overflow: hidden;">
                    <div id="growth-progress-bar" style="height: 100%; background: linear-gradient(90deg, #ffd700, #ffed4e); width: 0%; transition: width 0.3s ease;"></div>
                  </div>
                  <span style="color: rgba(255,255,255,0.8); font-size: 10px;"><span id="growth-progress-text">0%</span></span>
                </div>
              </div>
            </div>
          </div>
          <!-- ✅ 新增：查看成长按钮 -->
          <button id="soulmate-growth-btn" class="growth-btn" title="查看成长进度" style="
            background: rgba(255, 215, 0, 0.2);
            border: 1px solid rgba(255, 215, 0, 0.5);
            color: #ffd700;
            padding: 6px 12px;
            border-radius: 6px;
            font-size: 12px;
            cursor: pointer;
            transition: all 0.3s ease;
            margin-right: 10px;
          " onmouseover="this.style.background='rgba(255, 215, 0, 0.3)'; this.style.transform='scale(1.05)'" onmouseout="this.style.background='rgba(255, 215, 0, 0.2)'; this.style.transform='scale(1)'">⭐ 成长</button>
          <!-- ✅ 新增：VIP按钮 -->
          <button id="soulmate-vip-btn" class="vip-btn" title="升级VIP" style="
            background: rgba(255, 215, 0, 0.2);
            border: 1px solid rgba(255, 215, 0, 0.5);
            color: #ffd700;
            padding: 6px 12px;
            border-radius: 6px;
            font-size: 12px;
            cursor: pointer;
            transition: all 0.3s ease;
            margin-right: 10px;
          " onmouseover="this.style.background='rgba(255, 215, 0, 0.3)'; this.style.transform='scale(1.05)'" onmouseout="this.style.background='rgba(255, 215, 0, 0.2)'; this.style.transform='scale(1)'">💎 VIP</button>
          <!-- ✅ 新增：设置按钮 -->
          <button id="soulmate-menu-btn" class="btn-menu" title="设置" style="
            background: rgba(255, 255, 255, 0.1);
            border: 1px solid rgba(255, 255, 255, 0.2);
            color: white;
            padding: 6px 12px;
            border-radius: 6px;
            font-size: 12px;
            cursor: pointer;
            transition: all 0.3s ease;
            margin-right: 10px;
          " onmouseover="this.style.background='rgba(255, 255, 255, 0.2)'; this.style.transform='scale(1.05)'" onmouseout="this.style.background='rgba(255, 255, 255, 0.1)'; this.style.transform='scale(1)'">⚙️</button>
          <button class="chat-close" id="soulmate-chat-close" title="关闭">×</button>
        </div>
        
        <!-- 消息容器 -->
        <div class="chat-messages" id="soulmate-chat-messages" style="flex: 1; overflow-y: auto;">
          <div style="padding: 20px; text-align: center; color: rgba(255,255,255,0.7);">
            <p style="margin: 0 0 10px 0;">👋 你好，我是倪大师</p>
            <p style="margin: 0; font-size: 14px;">我是你的专属灵魂伴侣，可以为你提供心理疗愈、命理指导和情感陪伴。有什么想聊的吗？</p>
          </div>
        </div>
        
        <!-- 输入区域 -->
        <div class="chat-input">
          <input 
            type="text" 
            id="soulmate-chat-input" 
            placeholder="输入消息，按回车发送..."
            autocomplete="off"
          />
          <button id="soulmate-chat-send-btn">发送</button>
        </div>
      </div>
      
      <!-- ✅ 右侧功能卡片区域（可折叠） -->
      <div id="soulmate-feature-cards" style="
        width: 280px;
        background: rgba(0, 0, 0, 0.2);
        border-left: 1px solid rgba(255, 255, 255, 0.1);
        display: flex;
        flex-direction: column;
        transition: width 0.3s ease, opacity 0.3s ease;
        overflow: visible;
        position: relative;
      ">
        <!-- ✅ 折叠按钮（三角） -->
        <button id="feature-cards-toggle" style="
          position: absolute;
          left: -20px;
          top: 50%;
          transform: translateY(-50%);
          width: 20px;
          height: 40px;
          background: rgba(102, 126, 234, 0.8);
          border: 1px solid rgba(102, 126, 234, 0.5);
          border-right: none;
          border-radius: 8px 0 0 8px;
          cursor: pointer;
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 10;
          padding: 0;
          transition: all 0.3s ease;
        " onmouseover="this.style.background='rgba(102, 126, 234, 1)'" onmouseout="this.style.background='rgba(102, 126, 234, 0.8)'">
          <span id="toggle-triangle" style="
            display: inline-block;
            width: 0;
            height: 0;
            border-style: solid;
            border-width: 6px 0 6px 8px;
            border-color: transparent transparent transparent rgba(255, 255, 255, 0.9);
            transition: transform 0.3s ease;
          "></span>
        </button>
        
        <!-- 功能卡片内容 -->
        <div id="feature-cards-content" class="vip-features-container" style="
          flex: 1;
          overflow-y: auto;
          overflow-x: hidden;
          padding: 15px;
          display: flex;
          flex-direction: column;
          gap: 12px;
        ">
          <!-- 对话功能（免费） -->
          <div class="action-card free" style="
            background: rgba(102, 126, 234, 0.15);
            border: 1px solid rgba(102, 126, 234, 0.3);
            border-radius: 8px;
            padding: 12px;
            margin-bottom: 12px;
          ">
            <div class="card-header" style="display: flex; align-items: center; gap: 8px; margin-bottom: 10px;">
              <span style="font-size: 1.2rem;">💬</span>
              <span style="font-weight: 600; color: white; font-size: 14px;">深度对话</span>
            </div>
            <div class="card-actions" style="display: flex; flex-direction: column; gap: 6px;">
              <button class="action-btn" style="
                background: rgba(102, 126, 234, 0.3);
                border: 1px solid rgba(102, 126, 234, 0.5);
                color: white;
                padding: 8px 12px;
                border-radius: 6px;
                font-size: 12px;
                cursor: pointer;
                width: 100%;
                transition: all 0.2s ease;
              " onmouseover="this.style.background='rgba(102, 126, 234, 0.5)'" onmouseout="this.style.background='rgba(102, 126, 234, 0.3)'" title="直接输入消息，与倪大师自由对话">💬 自由对话</button>
              <button class="action-btn" style="
                background: rgba(102, 126, 234, 0.3);
                border: 1px solid rgba(102, 126, 234, 0.5);
                color: white;
                padding: 8px 12px;
                border-radius: 6px;
                font-size: 12px;
                cursor: pointer;
                width: 100%;
                transition: all 0.2s ease;
              " onmouseover="this.style.background='rgba(102, 126, 234, 0.5)'" onmouseout="this.style.background='rgba(102, 126, 234, 0.3)'" title="获取深度对话的引导提示，帮助你更好地表达">🔍 深度引导</button>
            </div>
          </div>
          
          <!-- 运势分析（VIP专享） -->
          <div class="action-card vip-locked" style="
            background: rgba(255, 255, 255, 0.05);
            border: 1px solid rgba(255, 255, 255, 0.1);
            border-radius: 8px;
            padding: 12px;
            margin-bottom: 12px;
            opacity: 0.6;
          ">
            <div class="card-header" style="display: flex; align-items: center; gap: 8px; margin-bottom: 10px; flex-wrap: wrap;">
              <span style="font-size: 1.2rem;">📊</span>
              <span style="font-weight: 600; color: white; font-size: 14px; flex: 1;">运势分析</span>
              <span class="vip-badge" style="
                background: rgba(255, 215, 0, 0.2);
                border: 1px solid rgba(255, 215, 0, 0.5);
                color: #ffd700;
                padding: 2px 6px;
                border-radius: 4px;
                font-size: 10px;
              ">VIP</span>
            </div>
            <div class="card-actions" style="display: flex; flex-direction: column; gap: 6px; margin-bottom: 8px;">
              <button class="action-btn locked" data-feature="fortune" style="
                background: rgba(108, 117, 125, 0.3);
                border: 1px solid rgba(108, 117, 125, 0.5);
                color: rgba(255, 255, 255, 0.5);
                padding: 8px 12px;
                border-radius: 6px;
                font-size: 12px;
                cursor: not-allowed;
                width: 100%;
              " title="分析今日事业运势，包括工作发展、升职机会等">💼 事业</button>
              <button class="action-btn locked" data-feature="fortune" style="
                background: rgba(108, 117, 125, 0.3);
                border: 1px solid rgba(108, 117, 125, 0.5);
                color: rgba(255, 255, 255, 0.5);
                padding: 8px 12px;
                border-radius: 6px;
                font-size: 12px;
                cursor: not-allowed;
                width: 100%;
              " title="分析今日感情运势，包括恋爱、婚姻、桃花运等">💕 感情</button>
              <button class="action-btn locked" data-feature="fortune" style="
                background: rgba(108, 117, 125, 0.3);
                border: 1px solid rgba(108, 117, 125, 0.5);
                color: rgba(255, 255, 255, 0.5);
                padding: 8px 12px;
                border-radius: 6px;
                font-size: 12px;
                cursor: not-allowed;
                width: 100%;
              " title="全方位分析今日运势，包括事业、感情、健康、财运">🌟 综合</button>
            </div>
            <button class="btn-upgrade-vip" style="
              background: rgba(255, 215, 0, 0.2);
              border: 1px solid rgba(255, 215, 0, 0.5);
              color: #ffd700;
              padding: 8px 12px;
              border-radius: 6px;
              font-size: 12px;
              cursor: pointer;
              width: 100%;
              transition: all 0.2s ease;
            " onmouseover="this.style.background='rgba(255, 215, 0, 0.3)'" onmouseout="this.style.background='rgba(255, 215, 0, 0.2)'">🔒 升级VIP解锁</button>
          </div>
          
          <!-- 生活指导（VIP专享） -->
          <div class="action-card vip-locked" style="
            background: rgba(255, 255, 255, 0.05);
            border: 1px solid rgba(255, 255, 255, 0.1);
            border-radius: 8px;
            padding: 12px;
            margin-bottom: 12px;
            opacity: 0.6;
          ">
            <div class="card-header" style="display: flex; align-items: center; gap: 8px; margin-bottom: 10px; flex-wrap: wrap;">
              <span style="font-size: 1.2rem;">🔮</span>
              <span style="font-weight: 600; color: white; font-size: 14px; flex: 1;">生活指导</span>
              <span class="vip-badge" style="
                background: rgba(255, 215, 0, 0.2);
                border: 1px solid rgba(255, 215, 0, 0.5);
                color: #ffd700;
                padding: 2px 6px;
                border-radius: 4px;
                font-size: 10px;
              ">VIP</span>
            </div>
            <div class="card-actions" style="display: flex; flex-direction: column; gap: 6px; margin-bottom: 8px;">
              <button class="action-btn free" style="
                background: rgba(102, 126, 234, 0.3);
                border: 1px solid rgba(102, 126, 234, 0.5);
                color: white;
                padding: 8px 12px;
                border-radius: 6px;
                font-size: 12px;
                cursor: pointer;
                width: 100%;
                transition: all 0.2s ease;
              " onmouseover="this.style.background='rgba(102, 126, 234, 0.5)'" onmouseout="this.style.background='rgba(102, 126, 234, 0.3)'" title="综合生活指导，涵盖工作、感情、健康等方面">🌟 通用</button>
              <button class="action-btn locked" style="
                background: rgba(108, 117, 125, 0.3);
                border: 1px solid rgba(108, 117, 125, 0.5);
                color: rgba(255, 255, 255, 0.5);
                padding: 8px 12px;
                border-radius: 6px;
                font-size: 12px;
                cursor: not-allowed;
                width: 100%;
              " title="专业的工作和职业发展指导">💼 工作</button>
              <button class="action-btn locked" style="
                background: rgba(108, 117, 125, 0.3);
                border: 1px solid rgba(108, 117, 125, 0.5);
                color: rgba(255, 255, 255, 0.5);
                padding: 8px 12px;
                border-radius: 6px;
                font-size: 12px;
                cursor: not-allowed;
                width: 100%;
              " title="感情关系和人际交往指导">💕 情感</button>
              <button class="action-btn locked" style="
                background: rgba(108, 117, 125, 0.3);
                border: 1px solid rgba(108, 117, 125, 0.5);
                color: rgba(255, 255, 255, 0.5);
                padding: 8px 12px;
                border-radius: 6px;
                font-size: 12px;
                cursor: not-allowed;
                width: 100%;
              " title="身体健康和心理健康指导">🌱 健康</button>
            </div>
            <button class="btn-upgrade-vip" style="
              background: rgba(255, 215, 0, 0.2);
              border: 1px solid rgba(255, 215, 0, 0.5);
              color: #ffd700;
              padding: 8px 12px;
              border-radius: 6px;
              font-size: 12px;
              cursor: pointer;
              width: 100%;
              transition: all 0.2s ease;
            " onmouseover="this.style.background='rgba(255, 215, 0, 0.3)'" onmouseout="this.style.background='rgba(255, 215, 0, 0.2)'">🔒 升级VIP解锁</button>
          </div>
          
          <!-- 心理疗愈（VIP专享） -->
          <div class="action-card vip-locked" style="
            background: rgba(255, 255, 255, 0.05);
            border: 1px solid rgba(255, 255, 255, 0.1);
            border-radius: 8px;
            padding: 12px;
            margin-bottom: 12px;
            opacity: 0.6;
          ">
            <div class="card-header" style="display: flex; align-items: center; gap: 8px; margin-bottom: 10px; flex-wrap: wrap;">
              <span style="font-size: 1.2rem;">💚</span>
              <span style="font-weight: 600; color: white; font-size: 14px; flex: 1;">心理疗愈</span>
              <span class="vip-badge" style="
                background: rgba(255, 215, 0, 0.2);
                border: 1px solid rgba(255, 215, 0, 0.5);
                color: #ffd700;
                padding: 2px 6px;
                border-radius: 4px;
                font-size: 10px;
              ">VIP</span>
            </div>
            <div class="card-actions" style="display: flex; flex-direction: column; gap: 6px; margin-bottom: 8px;">
              <button class="action-btn locked" data-feature="healing" style="
                background: rgba(108, 117, 125, 0.3);
                border: 1px solid rgba(108, 117, 125, 0.5);
                color: rgba(255, 255, 255, 0.5);
                padding: 8px 12px;
                border-radius: 6px;
                font-size: 12px;
                cursor: not-allowed;
                width: 100%;
              " title="获得情感支持和心理疏导服务">💚 心理疗愈</button>
              <button class="action-btn locked" style="
                background: rgba(108, 117, 125, 0.3);
                border: 1px solid rgba(108, 117, 125, 0.5);
                color: rgba(255, 255, 255, 0.5);
                padding: 8px 12px;
                border-radius: 6px;
                font-size: 12px;
                cursor: not-allowed;
                width: 100%;
              " title="记录你的情感和心情，获得个性化关怀">📝 情感日记</button>
            </div>
            <button class="btn-upgrade-vip" style="
              background: rgba(255, 215, 0, 0.2);
              border: 1px solid rgba(255, 215, 0, 0.5);
              color: #ffd700;
              padding: 8px 12px;
              border-radius: 6px;
              font-size: 12px;
              cursor: pointer;
              width: 100%;
              transition: all 0.2s ease;
            " onmouseover="this.style.background='rgba(255, 215, 0, 0.3)'" onmouseout="this.style.background='rgba(255, 215, 0, 0.2)'">🔒 升级VIP解锁</button>
          </div>
          
          <!-- 紫微斗数分析（VIP专享） -->
          <div class="action-card vip-locked" style="
            background: rgba(255, 255, 255, 0.05);
            border: 1px solid rgba(255, 255, 255, 0.1);
            border-radius: 8px;
            padding: 12px;
            margin-bottom: 12px;
            opacity: 0.6;
          ">
            <div class="card-header" style="display: flex; align-items: center; gap: 8px; margin-bottom: 10px; flex-wrap: wrap;">
              <span style="font-size: 1.2rem;">🔮</span>
              <span style="font-weight: 600; color: white; font-size: 14px; flex: 1;">紫微斗数 · 命理洞察</span>
              <span class="vip-badge" style="
                background: rgba(255, 215, 0, 0.2);
                border: 1px solid rgba(255, 215, 0, 0.5);
                color: #ffd700;
                padding: 2px 6px;
                border-radius: 4px;
                font-size: 10px;
              ">VIP</span>
            </div>
            <div style="font-size: 11px; color: rgba(255, 255, 255, 0.7); margin-bottom: 10px;">
              事业 / 婚姻 / 创业 / 亲缘 / 大运
            </div>
            <div class="card-actions" style="display: flex; flex-direction: column; gap: 6px; margin-bottom: 8px;">
              <button class="action-btn locked" data-feature="ziwei" data-question="career" style="
                background: rgba(108, 117, 125, 0.3);
                border: 1px solid rgba(108, 117, 125, 0.5);
                color: rgba(255, 255, 255, 0.5);
                padding: 8px 12px;
                border-radius: 6px;
                font-size: 12px;
                cursor: not-allowed;
                width: 100%;
              " title="分析未来五年事业走势（官禄宫 + 大运）">💼 事业走势</button>
              <button class="action-btn locked" data-feature="ziwei" data-question="marriage" style="
                background: rgba(108, 117, 125, 0.3);
                border: 1px solid rgba(108, 117, 125, 0.5);
                color: rgba(255, 255, 255, 0.5);
                padding: 8px 12px;
                border-radius: 6px;
                font-size: 12px;
                cursor: not-allowed;
                width: 100%;
              " title="分析婚姻修复还是重启（夫妻宫 + 流年）">💕 婚姻分析</button>
              <button class="action-btn locked" data-feature="ziwei" data-question="business" style="
                background: rgba(108, 117, 125, 0.3);
                border: 1px solid rgba(108, 117, 125, 0.5);
                color: rgba(255, 255, 255, 0.5);
                padding: 8px 12px;
                border-radius: 6px;
                font-size: 12px;
                cursor: not-allowed;
                width: 100%;
              " title="分析是否适合跳槽或创业（命宫 × 官禄宫 × 财帛宫）">🚀 创业指导</button>
              <button class="action-btn locked" data-feature="ziwei" data-question="family" style="
                background: rgba(108, 117, 125, 0.3);
                border: 1px solid rgba(108, 117, 125, 0.5);
                color: rgba(255, 255, 255, 0.5);
                padding: 8px 12px;
                border-radius: 6px;
                font-size: 12px;
                cursor: not-allowed;
                width: 100%;
              " title="分析亲子/父母缘分与相处（子女宫 / 父母宫）">👨‍👩‍👧 亲缘关系</button>
              <button class="action-btn locked" data-feature="ziwei" data-question="fortune" style="
                background: rgba(108, 117, 125, 0.3);
                border: 1px solid rgba(108, 117, 125, 0.5);
                color: rgba(255, 255, 255, 0.5);
                padding: 8px 12px;
                border-radius: 6px;
                font-size: 12px;
                cursor: not-allowed;
                width: 100%;
              " title="分析大运转换期的机会与警示（大运星曜组合）">⭐ 大运分析</button>
            </div>
            <button class="btn-upgrade-vip" style="
              background: rgba(255, 215, 0, 0.2);
              border: 1px solid rgba(255, 215, 0, 0.5);
              color: #ffd700;
              padding: 8px 12px;
              border-radius: 6px;
              font-size: 12px;
              cursor: pointer;
              width: 100%;
              transition: all 0.2s ease;
            " onmouseover="this.style.background='rgba(255, 215, 0, 0.3)'" onmouseout="this.style.background='rgba(255, 215, 0, 0.2)'">🔒 升级VIP解锁</button>
          </div>
        </div>
      </div>
      
      <!-- ✅ 新增：菜单下拉框（点击设置按钮显示） -->
      <div id="soulmate-menu-dropdown" class="menu-dropdown" style="
        position: absolute;
        top: 60px;
        right: 20px;
        background: linear-gradient(135deg, #1a1a2e 0%, #16213e 50%, #0f3460 100%);
        border: 1px solid rgba(255, 255, 255, 0.2);
        border-radius: 12px;
        padding: 15px;
        min-width: 250px;
        box-shadow: 0 10px 30px rgba(0, 0, 0, 0.5);
        z-index: 10001;
        display: none;
        backdrop-filter: blur(10px);
        max-height: 80vh;
        overflow-y: auto;
        overflow-x: hidden;
      ">
        <!-- ✅ 调整顺序：个人信息放在最前面 -->
        <div class="menu-section" style="margin-bottom: 15px;">
          <div class="menu-section-title" style="
            color: #ffd700;
            font-size: 14px;
            font-weight: 600;
            margin-bottom: 10px;
            padding-bottom: 8px;
            border-bottom: 1px solid rgba(255, 255, 255, 0.1);
          ">👤 个人信息</div>
          <button class="menu-item" id="personal-info-menu-item" style="
            width: 100%;
            background: rgba(255, 255, 255, 0.05);
            border: 1px solid rgba(255, 255, 255, 0.1);
            color: white;
            padding: 8px 12px;
            border-radius: 6px;
            font-size: 12px;
            cursor: pointer;
            margin-bottom: 6px;
            text-align: left;
            transition: all 0.2s ease;
          " onmouseover="this.style.background='rgba(255, 255, 255, 0.1)'" onmouseout="this.style.background='rgba(255, 255, 255, 0.05)'" title="查看和编辑个人信息，包括年龄、性别、星座等">👤 个人信息</button>
        </div>
        
        <div class="menu-section" style="margin-bottom: 15px;">
          <div class="menu-section-title" style="
            color: #ffd700;
            font-size: 14px;
            font-weight: 600;
            margin-bottom: 10px;
            padding-bottom: 8px;
            border-bottom: 1px solid rgba(255, 255, 255, 0.1);
          ">⭐ 我的成长</div>
          <button class="menu-item" style="
            width: 100%;
            background: rgba(255, 255, 255, 0.05);
            border: 1px solid rgba(255, 255, 255, 0.1);
            color: white;
            padding: 8px 12px;
            border-radius: 6px;
            font-size: 12px;
            cursor: pointer;
            margin-bottom: 6px;
            text-align: left;
            transition: all 0.2s ease;
          " onmouseover="this.style.background='rgba(255, 255, 255, 0.1)'" onmouseout="this.style.background='rgba(255, 255, 255, 0.05)'" title="查看你的成长等级、经验值和升级进度">⭐ 成长进度</button>
          <button class="menu-item" style="
            width: 100%;
            background: rgba(255, 255, 255, 0.05);
            border: 1px solid rgba(255, 255, 255, 0.1);
            color: white;
            padding: 8px 12px;
            border-radius: 6px;
            font-size: 12px;
            cursor: pointer;
            margin-bottom: 6px;
            text-align: left;
            transition: all 0.2s ease;
          " onmouseover="this.style.background='rgba(255, 255, 255, 0.1)'" onmouseout="this.style.background='rgba(255, 255, 255, 0.05)'" title="查看你已获得的成就徽章和完成进度">🏆 我的成就</button>
          <button class="menu-item" style="
            width: 100%;
            background: rgba(255, 255, 255, 0.05);
            border: 1px solid rgba(255, 255, 255, 0.1);
            color: white;
            padding: 8px 12px;
            border-radius: 6px;
            font-size: 12px;
            cursor: pointer;
            margin-bottom: 6px;
            text-align: left;
            transition: all 0.2s ease;
          " onmouseover="this.style.background='rgba(255, 255, 255, 0.1)'" onmouseout="this.style.background='rgba(255, 255, 255, 0.05)'" title="查看等级排行榜和经验值排行榜">📊 排行榜</button>
          <button class="menu-item" style="
            width: 100%;
            background: rgba(255, 255, 255, 0.05);
            border: 1px solid rgba(255, 255, 255, 0.1);
            color: white;
            padding: 8px 12px;
            border-radius: 6px;
            font-size: 12px;
            cursor: pointer;
            margin-bottom: 6px;
            text-align: left;
            transition: all 0.2s ease;
          " onmouseover="this.style.background='rgba(255, 255, 255, 0.1)'" onmouseout="this.style.background='rgba(255, 255, 255, 0.05)'" title="基于你的使用习惯，获得个性化的功能建议">💡 个性化建议</button>
        </div>
        
        <div class="menu-section" style="margin-bottom: 15px;">
          <div class="menu-section-title" style="
            color: #ffd700;
            font-size: 14px;
            font-weight: 600;
            margin-bottom: 10px;
            padding-bottom: 8px;
            border-bottom: 1px solid rgba(255, 255, 255, 0.1);
          ">💎 VIP特权</div>
          <div class="menu-item vip-feature" style="
            width: 100%;
            background: rgba(255, 255, 255, 0.05);
            border: 1px solid rgba(255, 255, 255, 0.1);
            color: rgba(255, 255, 255, 0.7);
            padding: 8px 12px;
            border-radius: 6px;
            font-size: 12px;
            margin-bottom: 6px;
            text-align: left;
          ">
            <span>✨</span> 无限对话
          </div>
          <div class="menu-item vip-feature" style="
            width: 100%;
            background: rgba(255, 255, 255, 0.05);
            border: 1px solid rgba(255, 255, 255, 0.1);
            color: rgba(255, 255, 255, 0.7);
            padding: 8px 12px;
            border-radius: 6px;
            font-size: 12px;
            margin-bottom: 6px;
            text-align: left;
          ">
            <span>📊</span> 全部运势分析
          </div>
          <div class="menu-item vip-feature" style="
            width: 100%;
            background: rgba(255, 255, 255, 0.05);
            border: 1px solid rgba(255, 255, 255, 0.1);
            color: rgba(255, 255, 255, 0.7);
            padding: 8px 12px;
            border-radius: 6px;
            font-size: 12px;
            margin-bottom: 6px;
            text-align: left;
          ">
            <span>🔮</span> 全部生活指导
          </div>
          <div class="menu-item vip-feature" style="
            width: 100%;
            background: rgba(255, 255, 255, 0.05);
            border: 1px solid rgba(255, 255, 255, 0.1);
            color: rgba(255, 255, 255, 0.7);
            padding: 8px 12px;
            border-radius: 6px;
            font-size: 12px;
            margin-bottom: 6px;
            text-align: left;
          ">
            <span>💚</span> 心理疗愈服务
          </div>
          <button class="menu-item btn-vip-upgrade-menu" style="
            width: 100%;
            background: rgba(255, 215, 0, 0.2);
            border: 1px solid rgba(255, 215, 0, 0.5);
            color: #ffd700;
            padding: 8px 12px;
            border-radius: 6px;
            font-size: 12px;
            cursor: pointer;
            margin-bottom: 6px;
            text-align: center;
            font-weight: 600;
            transition: all 0.2s ease;
          " onmouseover="this.style.background='rgba(255, 215, 0, 0.3)'" onmouseout="this.style.background='rgba(255, 215, 0, 0.2)'">升级VIP</button>
        </div>
        
        <div class="menu-section">
          <div class="menu-section-title" style="
            color: #ffd700;
            font-size: 14px;
            font-weight: 600;
            margin-bottom: 10px;
            padding-bottom: 8px;
            border-bottom: 1px solid rgba(255, 255, 255, 0.1);
          ">⚙️ 设置</div>
          <button class="menu-item" id="soulmate-sound-toggle" style="
            width: 100%;
            background: rgba(255, 255, 255, 0.05);
            border: 1px solid rgba(255, 255, 255, 0.1);
            color: white;
            padding: 8px 12px;
            border-radius: 6px;
            font-size: 12px;
            cursor: pointer;
            margin-bottom: 6px;
            text-align: left;
            transition: all 0.2s ease;
          " onmouseover="this.style.background='rgba(255, 255, 255, 0.1)'" onmouseout="this.style.background='rgba(255, 255, 255, 0.05)'">🔊 音效开关</button>
          <button class="menu-item" style="
            width: 100%;
            background: rgba(255, 255, 255, 0.05);
            border: 1px solid rgba(255, 255, 255, 0.1);
            color: white;
            padding: 8px 12px;
            border-radius: 6px;
            font-size: 12px;
            cursor: pointer;
            margin-bottom: 6px;
            text-align: left;
            transition: all 0.2s ease;
          " onmouseover="this.style.background='rgba(255, 255, 255, 0.1)'" onmouseout="this.style.background='rgba(255, 255, 255, 0.05)'">⚙️ 更多设置</button>
        </div>
      </div>
    `;
    
    // 添加到页面
    document.body.appendChild(chatInterface);
    
    // 绑定事件
    this.bindChatInterfaceEvents();
    
    console.log('✅ 灵魂伴侣聊天界面已创建');
  }

  /**
   * 绑定聊天界面事件
   */
  bindChatInterfaceEvents() {
    // 关闭按钮
    const closeBtn = document.getElementById('soulmate-chat-close');
    if (closeBtn) {
      closeBtn.addEventListener('click', () => {
        this.closeSoulmateChat();
      });
    }
    
    // 发送按钮
    const sendBtn = document.getElementById('soulmate-chat-send-btn');
    if (sendBtn) {
      sendBtn.addEventListener('click', () => {
        this.sendChatMessage();
      });
    }
    
    // 输入框回车事件（避免重复绑定）
    const input = document.getElementById('soulmate-chat-input');
    if (input && !input.dataset.bound) {
      input.addEventListener('keydown', (e) => {
        if (e.key === 'Enter' && !e.shiftKey) {
          e.preventDefault();
          this.sendChatMessage();
        }
      });
      input.dataset.bound = 'true';
    }
    
    // ✅ 新增：成长按钮点击事件
    const growthBtn = document.getElementById('soulmate-growth-btn');
    if (growthBtn) {
      growthBtn.addEventListener('click', () => {
        this.showGrowthPanel();
      });
    }
    
    // ✅ 修复：VIP按钮点击事件（添加等待机制）
    const vipBtn = document.getElementById('soulmate-vip-btn');
    if (vipBtn) {
      vipBtn.addEventListener('click', async (e) => {
        e.stopPropagation();
        e.preventDefault();
        
        console.log('🎯 VIP按钮被点击');
        
        // ✅ 修复：等待 showQRInPopup 函数加载
        const waitForShowQRInPopup = async (maxRetries = 10, delay = 200) => {
          for (let i = 0; i < maxRetries; i++) {
            if (typeof window.showQRInPopup === 'function') {
              console.log('✅ showQRInPopup 函数已加载');
              return true;
            }
            console.log(`⏳ 等待 showQRInPopup 函数加载... (${i + 1}/${maxRetries})`);
            await new Promise(resolve => setTimeout(resolve, delay));
          }
          return false;
        };
        
        // 等待函数加载
        const isLoaded = await waitForShowQRInPopup();
        
        if (isLoaded) {
          try {
            // ✅ 修复：使用 window.showQRInPopup
            await window.showQRInPopup('vip');
            console.log('✅ VIP支付弹窗已打开');
            
            // 监听VIP状态变化，更新UI
            this.checkAndUpdateVipStatus();
          } catch (error) {
            console.error('❌ 打开VIP支付弹窗失败:', error);
            CodeUtils.showToast('打开支付弹窗失败，请稍后再试', 'error', 3000);
          }
        } else {
          console.error('❌ showQRInPopup 函数加载超时');
          CodeUtils.showToast('支付功能加载超时，请刷新页面重试', 'error', 3000);
        }
      });
    }
    
    // ✅ 新增：设置按钮点击事件
    const menuBtn = document.getElementById('soulmate-menu-btn');
    const menuDropdown = document.getElementById('soulmate-menu-dropdown');
    
    if (menuBtn && menuDropdown) {
      let isMenuOpen = false;
      
      menuBtn.addEventListener('click', (e) => {
        e.stopPropagation();
        isMenuOpen = !isMenuOpen;
        
        if (isMenuOpen) {
          menuDropdown.style.display = 'block';
        } else {
          menuDropdown.style.display = 'none';
        }
      });
      
      // 点击外部关闭菜单
      document.addEventListener('click', (e) => {
        if (isMenuOpen && 
            !menuDropdown.contains(e.target) && 
            !menuBtn.contains(e.target)) {
          menuDropdown.style.display = 'none';
          isMenuOpen = false;
        }
      });
    }
    
    // ✅ 新增：菜单项点击事件
    const menuItems = document.querySelectorAll('#soulmate-menu-dropdown .menu-item');
    menuItems.forEach(item => {
      if (item.classList.contains('btn-vip-upgrade-menu')) {
        item.addEventListener('click', async () => {
          // ✅ 修复：使用 window.showQRInPopup 并添加等待机制
          const waitForShowQRInPopup = async (maxRetries = 10, delay = 200) => {
            for (let i = 0; i < maxRetries; i++) {
              if (typeof window.showQRInPopup === 'function') {
                return true;
              }
              await new Promise(resolve => setTimeout(resolve, delay));
            }
            return false;
          };
          
          const isLoaded = await waitForShowQRInPopup();
          if (isLoaded) {
            try {
              await window.showQRInPopup('vip');
              this.checkAndUpdateVipStatus();
            } catch (error) {
              console.error('打开VIP支付弹窗失败:', error);
              CodeUtils.showToast('打开支付弹窗失败，请稍后再试', 'error', 3000);
            }
          } else {
            CodeUtils.showToast('支付功能加载超时，请刷新页面重试', 'error', 3000);
          }
          if (menuDropdown) {
            menuDropdown.style.display = 'none';
          }
        });
      } else if (item.textContent.includes('成长进度')) {
        item.addEventListener('click', () => {
          this.showGrowthPanel();
          if (menuDropdown) {
            menuDropdown.style.display = 'none';
          }
        });
      } else if (item.textContent.includes('我的成就')) {
        item.addEventListener('click', () => {
          // ✅ 调用成就面板（仅显示成就）
          this.showAchievementPanel();
          if (menuDropdown) {
            menuDropdown.style.display = 'none';
          }
        });
      } else if (item.textContent.includes('排行榜')) {
        item.addEventListener('click', () => {
          // 调用排行榜功能
          if (typeof this.showLeaderboard === 'function') {
            this.showLeaderboard();
          }
          if (menuDropdown) {
            menuDropdown.style.display = 'none';
          }
        });
      } else if (item.textContent.includes('个性化建议')) {
        item.addEventListener('click', () => {
          // ✅ 显示个性化建议面板
          this.showPersonalizedSuggestionsPanel();
          if (menuDropdown) {
            menuDropdown.style.display = 'none';
          }
        });
      } else if (item.textContent.includes('个人信息')) {
        item.addEventListener('click', () => {
          // ✅ 显示个人信息面板
          this.showPersonalInfoPanel();
          if (menuDropdown) {
            menuDropdown.style.display = 'none';
          }
        });
      }
    });
    
    // ✅ 新增：音效开关按钮事件绑定
    const soundToggleBtn = document.getElementById('soulmate-sound-toggle');
    if (soundToggleBtn) {
      // 获取当前音效状态
      let soundEnabled = CodeUtils.safeGetStorage('soulmate_sound_enabled', true);
      
      // 更新按钮显示状态
      const updateSoundToggleState = () => {
        if (soundEnabled) {
          soundToggleBtn.textContent = '🔊 音效开关（已开启）';
          soundToggleBtn.style.color = '#4ade80';
        } else {
          soundToggleBtn.textContent = '🔇 音效开关（已关闭）';
          soundToggleBtn.style.color = 'rgba(255, 255, 255, 0.5)';
        }
      };
      
      // 初始化显示状态
      updateSoundToggleState();
      
      soundToggleBtn.addEventListener('click', () => {
        soundEnabled = !soundEnabled;
        CodeUtils.safeSetStorage('soulmate_sound_enabled', soundEnabled);
        updateSoundToggleState();
        
        CodeUtils.showToast(
          soundEnabled ? '音效已开启' : '音效已关闭',
          soundEnabled ? 'success' : 'info',
          2000
        );
        
        // 关闭菜单
        if (menuDropdown) {
          menuDropdown.style.display = 'none';
        }
      });
    }
    
    // ✅ 新增：更多设置按钮事件绑定
    const moreSettingsBtn = Array.from(document.querySelectorAll('#soulmate-menu-dropdown .menu-item')).find(
      item => item.textContent.includes('更多设置')
    );
    if (moreSettingsBtn) {
      moreSettingsBtn.addEventListener('click', () => {
        // 显示更多设置面板
        this.showMoreSettingsPanel();
        if (menuDropdown) {
          menuDropdown.style.display = 'none';
        }
      });
    }
    
    // ✅ 新增：功能卡片按钮事件绑定
    const upgradeVipBtns = document.querySelectorAll('.btn-upgrade-vip');
    upgradeVipBtns.forEach(btn => {
      btn.addEventListener('click', async (e) => {
        e.stopPropagation();
        // ✅ 修复：使用 window.showQRInPopup 并添加等待机制
        const waitForShowQRInPopup = async (maxRetries = 10, delay = 200) => {
          for (let i = 0; i < maxRetries; i++) {
            if (typeof window.showQRInPopup === 'function') {
              return true;
            }
            await new Promise(resolve => setTimeout(resolve, delay));
          }
          return false;
        };
        
        const isLoaded = await waitForShowQRInPopup();
        if (isLoaded) {
          try {
            await window.showQRInPopup('vip');
            this.checkAndUpdateVipStatus();
          } catch (error) {
            console.error('打开VIP支付弹窗失败:', error);
            CodeUtils.showToast('打开支付弹窗失败，请稍后再试', 'error', 3000);
          }
        } else {
          CodeUtils.showToast('支付功能加载超时，请刷新页面重试', 'error', 3000);
        }
      });
    });
    
    // 自由对话按钮（绑定所有对话按钮）
    const freeChatBtns = document.querySelectorAll('.action-card.free .action-btn');
    freeChatBtns.forEach(btn => {
      btn.addEventListener('click', () => {
        // 聚焦到输入框
        const input = document.getElementById('soulmate-chat-input');
        if (input) {
          input.focus();
        }
      });
    });
    
    // ✅ 修复：深度引导按钮事件绑定
    const deepGuideBtns = document.querySelectorAll('.action-card.free .action-btn');
    deepGuideBtns.forEach(btn => {
      // 只处理"深度引导"按钮
      if (btn.textContent.trim().includes('深度引导')) {
        btn.addEventListener('click', () => {
          // 1. 确保聊天界面已打开
          const chatInterface = document.getElementById('soulmate-chat-interface');
          if (!chatInterface || !chatInterface.classList.contains('active')) {
            // 如果聊天界面未打开，先打开它
            if (typeof this.openSoulmateChat === 'function') {
              this.openSoulmateChat();
            }
          }
          
          // 2. 深度对话引导提示列表
          const prompts = [
            "今天有什么想和我分享的吗？",
            "我感觉到你最近有心事，愿意聊聊吗？",
            "你的能量场告诉我，你需要一些指导。",
            "你真正害怕的是什么？在我面前你可以坦诚。",
            "如果没有人会评判你，你会怎么做？",
            "你内心最渴望的是什么？",
            "有什么秘密是你一直想说的吗？",
            "你最想改变的是什么？"
          ];
          
          // 3. 随机选择一个引导提示
          const selectedPrompt = prompts[Math.floor(Math.random() * prompts.length)];
          
          // 4. 延迟显示，确保聊天界面已完全打开
          setTimeout(() => {
            // 将引导提示作为AI消息发送到聊天界面
            if (typeof this.addChatMessage === 'function') {
              this.addChatMessage('assistant', selectedPrompt, true);
              // 保存对话历史
              if (typeof this.saveChatHistory === 'function') {
                this.saveChatHistory();
              }
            }
            
            // 5. 聚焦到输入框
            const input = document.getElementById('soulmate-chat-input');
            if (input) {
              input.focus();
              console.log('✅ 深度引导提示已显示，输入框已聚焦');
            }
          }, 300);
        });
      }
    });
    
    // ✅ 新增：生活指导 - 通用按钮事件绑定
    const generalGuidanceBtn = document.querySelector('.action-card .action-btn.free');
    if (generalGuidanceBtn && generalGuidanceBtn.textContent.includes('通用')) {
      generalGuidanceBtn.addEventListener('click', async (e) => {
        e.stopPropagation();
        
        // ✅ 修复：添加即时反馈
        const originalText = generalGuidanceBtn.textContent.trim();
        const originalBackground = generalGuidanceBtn.style.background;
        const originalCursor = generalGuidanceBtn.style.cursor;
        
        // 1. 立即显示加载状态
        generalGuidanceBtn.disabled = true;
        generalGuidanceBtn.style.cursor = 'wait';
        generalGuidanceBtn.style.opacity = '0.7';
        generalGuidanceBtn.textContent = '⏳ 生成中...';
        
        // 2. 显示Toast提示
        CodeUtils.showToast('正在生成生活指导，请稍候...', 'info', 2000);
        
        try {
          // 调用生活指导功能
          if (typeof this.getLifeGuidance === 'function') {
            await this.getLifeGuidance('general');
            console.log('✅ 生活指导已显示');
          } else {
            CodeUtils.showToast('生活指导功能开发中，敬请期待', 'info', 3000);
          }
          
          // ✅ 修复：恢复按钮状态
          generalGuidanceBtn.disabled = false;
          generalGuidanceBtn.style.cursor = originalCursor || 'pointer';
          generalGuidanceBtn.style.opacity = '1';
          generalGuidanceBtn.textContent = originalText;
        } catch (error) {
          console.error('❌ 获取生活指导失败:', error);
          // ✅ 修复：恢复按钮状态
          generalGuidanceBtn.disabled = false;
          generalGuidanceBtn.style.cursor = originalCursor || 'pointer';
          generalGuidanceBtn.style.opacity = '1';
          generalGuidanceBtn.textContent = originalText;
          CodeUtils.showToast('获取指导失败，请稍后重试', 'error', 3000);
        }
      });
    }
    
    // ✅ 修复：生活指导子功能按钮事件绑定（工作/感情/健康）
    const lifeGuidanceCard = Array.from(document.querySelectorAll('.action-card')).find(card => {
      return card.textContent.includes('生活指导');
    });
    
    if (lifeGuidanceCard) {
      // 工作按钮
      const workBtn = Array.from(lifeGuidanceCard.querySelectorAll('.action-btn')).find(btn => 
        btn.textContent.trim().includes('工作')
      );
      if (workBtn) {
        workBtn.addEventListener('click', async (e) => {
          e.stopPropagation();
          
          if (this.isVipUser()) {
            // ✅ 修复：添加即时反馈
            const originalText = workBtn.textContent.trim();
            const originalBackground = workBtn.style.background;
            const originalCursor = workBtn.style.cursor;
            
            // 1. 立即显示加载状态
            workBtn.disabled = true;
            workBtn.style.cursor = 'wait';
            workBtn.style.opacity = '0.7';
            workBtn.textContent = '⏳ 生成中...';
            
            // 2. 显示Toast提示
            CodeUtils.showToast('正在生成工作指导，请稍候...', 'info', 2000);
            
            try {
              if (typeof this.getLifeGuidance === 'function') {
                await this.getLifeGuidance('work');
                console.log('✅ 工作指导已显示');
              }
              
              // ✅ 修复：恢复按钮状态
              workBtn.disabled = false;
              workBtn.style.cursor = originalCursor || 'pointer';
              workBtn.style.opacity = '1';
              workBtn.textContent = originalText;
            } catch (error) {
              console.error('❌ 获取工作指导失败:', error);
              // ✅ 修复：恢复按钮状态
              workBtn.disabled = false;
              workBtn.style.cursor = originalCursor || 'pointer';
              workBtn.style.opacity = '1';
              workBtn.textContent = originalText;
              CodeUtils.showToast('获取指导失败，请稍后重试', 'error', 3000);
            }
          } else {
            CodeUtils.showToast('此功能需要VIP会员，请先升级VIP', 'info', 3000);
            // ✅ 修复：使用 window.showQRInPopup
            if (typeof window.showQRInPopup === 'function') {
              window.showQRInPopup('vip').catch(error => {
                console.error('打开VIP支付弹窗失败:', error);
              });
            }
          }
        });
      }
      
      // 情感按钮（生活指导中的，不是运势分析中的）
      const relationshipBtn = Array.from(lifeGuidanceCard.querySelectorAll('.action-btn')).find(btn => 
        btn.textContent.trim().includes('情感') && !btn.hasAttribute('data-feature')
      );
      if (relationshipBtn) {
        relationshipBtn.addEventListener('click', async (e) => {
          e.stopPropagation();
          
          if (this.isVipUser()) {
            // ✅ 修复：添加即时反馈
            const originalText = relationshipBtn.textContent.trim();
            const originalBackground = relationshipBtn.style.background;
            const originalCursor = relationshipBtn.style.cursor;
            
            // 1. 立即显示加载状态
            relationshipBtn.disabled = true;
            relationshipBtn.style.cursor = 'wait';
            relationshipBtn.style.opacity = '0.7';
            relationshipBtn.textContent = '⏳ 生成中...';
            
            // 2. 显示Toast提示
            CodeUtils.showToast('正在生成感情指导，请稍候...', 'info', 2000);
            
            try {
              if (typeof this.getLifeGuidance === 'function') {
                await this.getLifeGuidance('relationship');
                console.log('✅ 感情指导已显示');
              }
              
              // ✅ 修复：恢复按钮状态
              relationshipBtn.disabled = false;
              relationshipBtn.style.cursor = originalCursor || 'pointer';
              relationshipBtn.style.opacity = '1';
              relationshipBtn.textContent = originalText;
            } catch (error) {
              console.error('❌ 获取感情指导失败:', error);
              // ✅ 修复：恢复按钮状态
              relationshipBtn.disabled = false;
              relationshipBtn.style.cursor = originalCursor || 'pointer';
              relationshipBtn.style.opacity = '1';
              relationshipBtn.textContent = originalText;
              CodeUtils.showToast('获取指导失败，请稍后重试', 'error', 3000);
            }
          } else {
            CodeUtils.showToast('此功能需要VIP会员，请先升级VIP', 'info', 3000);
            // ✅ 修复：使用 window.showQRInPopup
            if (typeof window.showQRInPopup === 'function') {
              window.showQRInPopup('vip').catch(error => {
                console.error('打开VIP支付弹窗失败:', error);
              });
            }
          }
        });
      }
      
      // 健康按钮
      const healthBtn = Array.from(lifeGuidanceCard.querySelectorAll('.action-btn')).find(btn => 
        btn.textContent.trim().includes('健康')
      );
      if (healthBtn) {
        healthBtn.addEventListener('click', async (e) => {
          e.stopPropagation();
          
          if (this.isVipUser()) {
            // ✅ 修复：添加即时反馈
            const originalText = healthBtn.textContent.trim();
            const originalBackground = healthBtn.style.background;
            const originalCursor = healthBtn.style.cursor;
            
            // 1. 立即显示加载状态
            healthBtn.disabled = true;
            healthBtn.style.cursor = 'wait';
            healthBtn.style.opacity = '0.7';
            healthBtn.textContent = '⏳ 生成中...';
            
            // 2. 显示Toast提示
            CodeUtils.showToast('正在生成健康指导，请稍候...', 'info', 2000);
            
            try {
              if (typeof this.getLifeGuidance === 'function') {
                await this.getLifeGuidance('health');
                console.log('✅ 健康指导已显示');
              }
              
              // ✅ 修复：恢复按钮状态
              healthBtn.disabled = false;
              healthBtn.style.cursor = originalCursor || 'pointer';
              healthBtn.style.opacity = '1';
              healthBtn.textContent = originalText;
            } catch (error) {
              console.error('❌ 获取健康指导失败:', error);
              // ✅ 修复：恢复按钮状态
              healthBtn.disabled = false;
              healthBtn.style.cursor = originalCursor || 'pointer';
              healthBtn.style.opacity = '1';
              healthBtn.textContent = originalText;
              CodeUtils.showToast('获取指导失败，请稍后重试', 'error', 3000);
            }
          } else {
            CodeUtils.showToast('此功能需要VIP会员，请先升级VIP', 'info', 3000);
            // ✅ 修复：使用 window.showQRInPopup
            if (typeof window.showQRInPopup === 'function') {
              window.showQRInPopup('vip').catch(error => {
                console.error('打开VIP支付弹窗失败:', error);
              });
            }
          }
        });
      }
    }
    
    // ✅ 修复：VIP锁定按钮点击提示（先检查VIP状态）
    // ✅ 优化：排除已有 data-feature 属性的按钮，避免重复绑定（运势分析和心理疗愈按钮已有专门的事件处理）
    // ✅ 修复：排除已有专门事件绑定的按钮（工作、情感、健康），避免重复调用
    const lockedBtns = document.querySelectorAll('.action-btn.locked:not([data-feature])');
    lockedBtns.forEach(btn => {
      // ✅ 修复：排除已经有专门事件绑定的按钮（工作、情感、健康、情感日记）
      const btnText = btn.textContent.trim();
      const isLifeGuidanceBtn = (btnText.includes('工作') || 
                                  btnText.includes('情感') || 
                                  btnText.includes('健康')) &&
                                 !btn.hasAttribute('data-feature');
      const isSpecialBtn = btnText.includes('情感日记');
      
      // 如果是有专门事件绑定的按钮，跳过（避免重复绑定）
      if (isLifeGuidanceBtn || isSpecialBtn) {
        return; // 跳过这个按钮，它已经有专门的事件绑定了
      }
      
      btn.addEventListener('click', (e) => {
        // ✅ 修复：先检查VIP状态，如果已激活则允许使用
        if (this.isVipUser()) {
          // VIP已激活，移除locked类并更新样式
          btn.classList.remove('locked');
          btn.style.cursor = 'pointer';
          btn.style.background = 'rgba(102, 126, 234, 0.3)';
          btn.style.borderColor = 'rgba(102, 126, 234, 0.5)';
          btn.style.color = 'white';
          
          // 根据按钮文本执行相应功能
          const btnText = btn.textContent.trim();
          
          if (btnText.includes('事业')) {
            // 运势分析 - 事业
            this.getFortuneAnalysis('career');
          } else if (btnText.includes('感情') && btnText.includes('运势')) {
            // 运势分析 - 感情
            this.getFortuneAnalysis('relationship');
          } else if (btnText.includes('综合')) {
            // 运势分析 - 综合
            this.getFortuneAnalysis('comprehensive');
          } else if (btnText.includes('心理')) {
            // 心理疗愈相关按钮
            this.getPsychologicalHealing();
          }
          return;
        }
        
        // 非VIP用户，显示提示
        CodeUtils.showToast('此功能需要VIP会员，点击"升级VIP解锁"按钮解锁', 'info', 3000);
      });
    });
    
    // ✅ 新增：功能卡片折叠按钮事件（右侧三角按钮）
    const toggleBtn = document.getElementById('feature-cards-toggle');
    const featureCards = document.getElementById('soulmate-feature-cards');
    const featureCardsContent = document.getElementById('feature-cards-content');
    const toggleTriangle = document.getElementById('toggle-triangle');
    
    if (toggleBtn && featureCards && featureCardsContent && toggleTriangle) {
      // 默认展开状态
      let isCollapsed = false;
      
      toggleBtn.addEventListener('click', (e) => {
        e.stopPropagation(); // 防止事件冒泡
        
        isCollapsed = !isCollapsed;
        
        if (isCollapsed) {
          // 折叠：宽度变为0，隐藏内容
          featureCards.style.width = '0px';
          featureCardsContent.style.opacity = '0';
          featureCardsContent.style.pointerEvents = 'none';
          // 三角旋转180度（指向右）
          toggleTriangle.style.transform = 'rotate(180deg)';
          // ✅ 修复：按钮保持在容器外部，确保始终可见
          toggleBtn.style.left = '-20px'; // 按钮保持在容器外部
        } else {
          // 展开：恢复宽度，显示内容
          featureCards.style.width = '280px';
          featureCardsContent.style.opacity = '1';
          featureCardsContent.style.pointerEvents = 'auto';
          // 三角恢复（指向左）
          toggleTriangle.style.transform = 'rotate(0deg)';
          // ✅ 按钮位置保持不变（始终在容器外部）
          toggleBtn.style.left = '-20px';
        }
      });
    }
    
    // ✅ 新增：初始化时检查VIP状态并更新UI
    setTimeout(() => {
      this.updateVipUI();
    }, 500);
    
    // ✅ 修复：运势分析按钮事件（VIP功能）- 根据按钮类型传递不同参数
    const fortuneBtns = document.querySelectorAll('.action-card .action-btn[data-feature="fortune"]');
    fortuneBtns.forEach(btn => {
      btn.addEventListener('click', async (e) => {
        e.stopPropagation();
        
        if (this.isVipUser()) {
          // ✅ 修复：添加即时反馈
          const originalText = btn.textContent.trim();
          const originalBackground = btn.style.background;
          const originalCursor = btn.style.cursor;
          
          // 1. 立即显示加载状态
          btn.disabled = true;
          btn.style.cursor = 'wait';
          btn.style.opacity = '0.7';
          btn.textContent = '⏳ 分析中...';
          
          // 2. 显示Toast提示
          CodeUtils.showToast('正在分析运势，请稍候...', 'info', 2000);
          
          // 3. 根据按钮文本确定分析类型
          const btnText = originalText;
          let category = 'comprehensive'; // 默认综合分析
          
          if (btnText.includes('事业')) {
            category = 'career';
          } else if (btnText.includes('感情')) {
            category = 'relationship';
          } else if (btnText.includes('综合')) {
            category = 'comprehensive';
          }
          
          try {
            // 调用运势分析，传递类型参数
            await this.getFortuneAnalysis(category);
            
            // ✅ 修复：恢复按钮状态
            btn.disabled = false;
            btn.style.cursor = originalCursor || 'pointer';
            btn.style.opacity = '1';
            btn.textContent = originalText;
          } catch (error) {
            console.error('运势分析失败:', error);
            // ✅ 修复：恢复按钮状态
            btn.disabled = false;
            btn.style.cursor = originalCursor || 'pointer';
            btn.style.opacity = '1';
            btn.textContent = originalText;
            CodeUtils.showToast('分析失败，请稍后重试', 'error', 3000);
          }
        } else {
          CodeUtils.showToast('此功能需要VIP会员，请先升级VIP', 'info', 3000);
          // ✅ 修复：使用 window.showQRInPopup
          if (typeof window.showQRInPopup === 'function') {
            window.showQRInPopup('vip').catch(error => {
              console.error('打开VIP支付弹窗失败:', error);
            });
          }
        }
      });
    });
    
    // ✅ 修复：心理疗愈按钮事件（VIP功能）
    const healingBtns = document.querySelectorAll('.action-card .action-btn[data-feature="healing"]');
    healingBtns.forEach(btn => {
      btn.addEventListener('click', async (e) => {
        e.stopPropagation();
        
        if (this.isVipUser()) {
          // ✅ 修复：添加即时反馈
          const originalText = btn.textContent.trim();
          const originalBackground = btn.style.background;
          const originalCursor = btn.style.cursor;
          
          // 1. 立即显示加载状态
          btn.disabled = true;
          btn.style.cursor = 'wait';
          btn.style.opacity = '0.7';
          btn.textContent = '⏳ 生成中...';
          
          // 2. 显示Toast提示
          CodeUtils.showToast('正在生成心理疗愈内容，请稍候...', 'info', 2000);
          
          try {
            // 调用心理疗愈功能
            await this.getPsychologicalHealing();
            
            // ✅ 修复：恢复按钮状态
            btn.disabled = false;
            btn.style.cursor = originalCursor || 'pointer';
            btn.style.opacity = '1';
            btn.textContent = originalText;
          } catch (error) {
            console.error('心理疗愈失败:', error);
            // ✅ 修复：恢复按钮状态
            btn.disabled = false;
            btn.style.cursor = originalCursor || 'pointer';
            btn.style.opacity = '1';
            btn.textContent = originalText;
            CodeUtils.showToast('获取心理疗愈失败，请稍后重试', 'error', 3000);
          }
        } else {
          CodeUtils.showToast('此功能需要VIP会员，请先升级VIP', 'info', 3000);
          // ✅ 修复：使用 window.showQRInPopup
          if (typeof window.showQRInPopup === 'function') {
            window.showQRInPopup('vip').catch(error => {
              console.error('打开VIP支付弹窗失败:', error);
            });
          }
        }
      });
    });
    
    // ✅ 修复：情感日记按钮事件绑定
    const diaryBtn = Array.from(document.querySelectorAll('.action-card .action-btn')).find(btn => 
      btn.textContent.trim().includes('情感日记')
    );
    if (diaryBtn) {
      diaryBtn.addEventListener('click', () => {
        if (this.isVipUser()) {
          // 调用情感日记功能
          if (typeof window.openEmotionDiary === 'function') {
            window.openEmotionDiary();
          } else if (typeof showEmotionDiaryModal === 'function') {
            showEmotionDiaryModal();
          } else {
            CodeUtils.showToast('情感日记功能正在加载中，请稍后再试', 'info', 3000);
          }
        } else {
          CodeUtils.showToast('此功能需要VIP会员，请先升级VIP', 'info', 3000);
          // ✅ 修复：使用 window.showQRInPopup
          if (typeof window.showQRInPopup === 'function') {
            window.showQRInPopup('vip').catch(error => {
              console.error('打开VIP支付弹窗失败:', error);
            });
          }
        }
      });
    }
    
    // ✅ 新增：紫微斗数按钮事件绑定（VIP功能）
    const ziweiBtns = document.querySelectorAll('.action-btn[data-feature="ziwei"]');
    ziweiBtns.forEach(btn => {
      btn.addEventListener('click', async (e) => {
        e.preventDefault();
        e.stopPropagation();
        
        const question = btn.getAttribute('data-question');
        
        if (!this.isVipUser()) {
          CodeUtils.showToast('此功能需要VIP会员，请先升级VIP', 'info', 3000);
          if (typeof window.showQRInPopup === 'function') {
            window.showQRInPopup('vip').catch(error => {
              console.error('打开VIP支付弹窗失败:', error);
            });
          }
          return;
        }
        
        // ✅ 检查是否有八字信息
        const analysisData = CodeUtils.safeGetStorage('user_analysis_data', {});
        const hasBazi = analysisData.bazi_analysis && analysisData.bazi_analysis.has_data;
        
        if (!hasBazi) {
          CodeUtils.showToast('紫微斗数分析需要您的出生信息，请先完成八字分析', 'info', 3000);
          return;
        }
        
        // ✅ 添加即时反馈
        const originalText = btn.textContent.trim();
        const originalCursor = btn.style.cursor;
        
        // 1. 立即显示加载状态
        btn.disabled = true;
        btn.style.cursor = 'wait';
        btn.style.opacity = '0.7';
        btn.textContent = '⏳ 分析中...';
        
        // 2. 显示Toast提示
        CodeUtils.showToast('正在分析紫微斗数，请稍候...', 'info', 2000);
        
        // 3. 确保聊天界面已打开
        if (!this.isChatOpen()) {
          this.openSoulmateChat();
        }
        
        try {
          // 调用紫微斗数分析
          await this.getZiweiAnalysis(question);
          
          // ✅ 恢复按钮状态
          btn.disabled = false;
          btn.style.cursor = originalCursor || 'pointer';
          btn.style.opacity = '1';
          btn.textContent = originalText;
        } catch (error) {
          console.error('紫微斗数分析失败:', error);
          // ✅ 恢复按钮状态
          btn.disabled = false;
          btn.style.cursor = originalCursor || 'pointer';
          btn.style.opacity = '1';
          btn.textContent = originalText;
          CodeUtils.showToast('获取紫微斗数分析失败，请稍后重试', 'error', 3000);
        }
      });
    });
  }

  /**
   * 添加灵魂伴侣样式
   */
  addSoulmateStyles() {
    const style = document.createElement('style');
    style.textContent = `
      .soulmate-sidebar {
        position: fixed;
        right: -350px;
        top: 50%;
        transform: translateY(-50%);
        width: 350px;
        height: 500px;
        background: linear-gradient(135deg, #667eea, #764ba2);
        border-radius: 20px 0 0 20px;
        box-shadow: -5px 0 20px rgba(0, 0, 0, 0.3);
        transition: right 0.3s ease;
        z-index: 1000;
      }

      .soulmate-sidebar.active {
        right: 0;
      }

      .soulmate-header {
        display: flex;
        align-items: center;
        padding: 20px;
        background: rgba(255, 255, 255, 0.1);
        border-radius: 20px 0 0 0;
      }

      .soulmate-avatar {
        width: 50px;
        height: 50px;
        border-radius: 50%;
        overflow: hidden;
        margin-right: 15px;
      }

      .soulmate-avatar img {
        width: 100%;
        height: 100%;
        object-fit: cover;
      }

      .soulmate-info {
        flex: 1;
      }

      .soulmate-name {
        color: white;
        margin: 0;
        font-size: 18px;
        font-weight: 600;
      }

      .soulmate-status {
        color: rgba(255, 255, 255, 0.8);
        margin: 0;
        font-size: 14px;
      }

      .soulmate-toggle {
        background: rgba(255, 255, 255, 0.2);
        border: none;
        border-radius: 50%;
        width: 40px;
        height: 40px;
        color: white;
        font-size: 20px;
        cursor: pointer;
        transition: all 0.3s ease;
      }

      .soulmate-toggle:hover {
        background: rgba(255, 255, 255, 0.3);
        transform: scale(1.1);
      }

      .soulmate-content {
        padding: 20px;
        height: calc(100% - 90px);
        display: flex;
        flex-direction: column;
      }

      .soulmate-messages {
        flex: 1;
        overflow-y: auto;
        margin-bottom: 20px;
      }

      .soulmate-message {
        display: flex;
        margin-bottom: 15px;
        align-items: flex-start;
      }

      .message-avatar {
        width: 30px;
        height: 30px;
        border-radius: 50%;
        overflow: hidden;
        margin-right: 10px;
        flex-shrink: 0;
      }

      .message-avatar img {
        width: 100%;
        height: 100%;
        object-fit: cover;
      }

      .message-content {
        background: rgba(255, 255, 255, 0.9);
        padding: 10px 15px;
        border-radius: 15px;
        max-width: 80%;
      }

      .message-text {
        color: #2d3748;
        margin: 0 0 5px 0;
        font-size: 14px;
        line-height: 1.4;
      }

      .message-time {
        color: #718096;
        font-size: 12px;
      }

      .soulmate-input {
        display: flex;
        gap: 10px;
      }

      .soulmate-input input {
        flex: 1;
        padding: 10px 15px;
        border: none;
        border-radius: 20px;
        background: rgba(255, 255, 255, 0.9);
        font-size: 14px;
      }

      .soulmate-input input:focus {
        outline: none;
        box-shadow: 0 0 0 2px rgba(255, 255, 255, 0.5);
      }

      .soulmate-input button {
        padding: 10px 20px;
        background: rgba(255, 255, 255, 0.2);
        border: none;
        border-radius: 20px;
        color: white;
        font-size: 14px;
        cursor: pointer;
        transition: all 0.3s ease;
      }

      .soulmate-input button:hover {
        background: rgba(255, 255, 255, 0.3);
      }

      .soulmate-chat-backdrop {
        position: fixed;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        background: rgba(0, 0, 0, 0.5);
        backdrop-filter: blur(4px);
        z-index: 9999;
        display: none;
        opacity: 0;
        transition: opacity 0.3s ease;
      }

      .soulmate-chat-backdrop.active {
        display: block;
        opacity: 1;
      }

      .soulmate-chat-interface {
        position: fixed !important;
        top: 50% !important;
        left: 50% !important;
        transform: translate(-50%, -50%) !important;
        width: 800px !important;
        height: 600px !important;
        max-width: 95vw !important;
        max-height: 90vh !important;
        background: linear-gradient(135deg, #1a1a2e 0%, #16213e 50%, #0f3460 100%) !important;
        backdrop-filter: blur(10px) !important;
        border-radius: 20px !important;
        box-shadow: 0 20px 60px rgba(0, 0, 0, 0.8) !important;
        z-index: 10000 !important;
        display: none !important;
        overflow: hidden !important;
        flex-direction: row !important;
      }

      .soulmate-chat-interface.active {
        display: flex !important;
        animation: chatSlideIn 0.3s ease;
      }

      .chat-header {
        display: flex;
        justify-content: space-between;
        align-items: center;
        padding: 20px;
        background: linear-gradient(135deg, #667eea, #764ba2);
        color: white;
        border-radius: 20px 20px 0 0;
      }

      .chat-header h3 {
        margin: 0;
        font-size: 18px;
      }

      .chat-close {
        background: none;
        border: none;
        color: white;
        font-size: 24px;
        cursor: pointer;
        padding: 0;
        width: 30px;
        height: 30px;
        display: flex;
        align-items: center;
        justify-content: center;
      }

      .chat-messages {
        height: 350px;
        overflow-y: auto;
        padding: 20px;
      }

      .chat-input {
        display: flex;
        padding: 20px;
        gap: 10px;
        border-top: 1px solid #e2e8f0;
      }

      .chat-input input {
        flex: 1;
        padding: 10px 15px;
        border: 1px solid #e2e8f0;
        border-radius: 20px;
        font-size: 14px;
      }

      .chat-input input:focus {
        outline: none;
        border-color: #667eea;
      }

      .chat-input button {
        padding: 10px 20px;
        background: linear-gradient(135deg, #667eea, #764ba2);
        border: none;
        border-radius: 20px;
        color: white;
        font-size: 14px;
        cursor: pointer;
      }

      @keyframes chatSlideIn {
        from { transform: translate(-50%, -50%) scale(0.8); opacity: 0; }
        to { transform: translate(-50%, -50%) scale(1); opacity: 1; }
      }

      /* ✅ 新增：VIP功能区响应式布局 */
      .vip-features-container {
        display: grid;
        grid-template-columns: repeat(3, minmax(0, 1fr));
        gap: 12px;
      }
      
      @media (max-width: 1024px) {
        .vip-features-container {
          grid-template-columns: repeat(2, minmax(0, 1fr));
        }
      }
      
      @media (max-width: 768px) {
        .soulmate-sidebar {
          width: 100%;
          right: -100%;
        }
        
        .soulmate-chat-interface {
          width: 95vw !important;
          height: 80vh !important;
          flex-direction: column !important;
        }
        
        /* ✅ 移动端隐藏功能卡片 */
        #soulmate-feature-cards {
          display: none !important;
        }
        
        .vip-features-container {
          grid-template-columns: 1fr;
        }
      }
      
      /* 修复灵魂伴侣弹窗样式 */
      /* 灵魂伴侣触发弹窗样式（深色主题） */
      #soulmate-trigger-modal {
        position: fixed;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        background: rgba(0, 0, 0, 0.85) !important;
        backdrop-filter: blur(8px) !important;
        -webkit-backdrop-filter: blur(8px) !important;
        display: flex;
        align-items: center;
        justify-content: center;
        z-index: 10000;
      }
      
      #soulmate-trigger-modal .modal-content {
        background: rgba(26, 26, 46, 0.95) !important;
        backdrop-filter: blur(20px) !important;
        -webkit-backdrop-filter: blur(20px) !important;
        border: 1px solid rgba(102, 126, 234, 0.5) !important;
        border-radius: 20px;
        padding: 30px;
        max-width: 500px;
        width: 90%;
        animation: soulmateModalSlideIn 0.3s ease-out;
        box-shadow: 0 8px 32px rgba(0, 0, 0, 0.5), 0 0 0 1px rgba(102, 126, 234, 0.3) inset, 0 0 40px rgba(102, 126, 234, 0.3) !important;
      }
      
      #soulmate-trigger-modal .modal-header {
        text-align: center;
        margin-bottom: 20px;
      }
      
      #soulmate-trigger-modal .modal-header h3 {
        color: rgba(255, 255, 255, 0.95) !important;
        font-size: 24px;
        margin: 0;
        font-weight: bold;
        text-shadow: 0 2px 8px rgba(102, 126, 234, 0.5) !important;
      }
      
      #soulmate-trigger-modal .modal-body {
        text-align: center;
        margin-bottom: 30px;
        background: transparent !important;
        padding: 20px;
        border-radius: 12px;
      }
      
      #soulmate-trigger-modal .modal-body p {
        color: rgba(255, 255, 255, 0.9) !important;
        font-size: 16px;
        line-height: 1.6;
        margin: 10px 0;
      }
      
      #soulmate-trigger-modal .modal-footer {
        display: flex;
        gap: 15px;
        justify-content: center;
        border-top: 1px solid rgba(102, 126, 234, 0.3) !important;
        padding-top: 20px;
        margin-top: 20px;
      }
      
      #soulmate-trigger-modal .btn-primary {
        background: linear-gradient(135deg, #667eea 0%, #764ba2 100%) !important;
        color: white !important;
        border: none !important;
        padding: 12px 24px;
        border-radius: 8px;
        font-size: 16px;
        font-weight: bold;
        cursor: pointer;
        transition: all 0.3s ease;
        box-shadow: 0 4px 12px rgba(102, 126, 234, 0.4) !important;
      }
      
      #soulmate-trigger-modal .btn-primary:hover {
        transform: translateY(-2px);
        box-shadow: 0 6px 20px rgba(102, 126, 234, 0.6) !important;
        background: linear-gradient(135deg, #7c3aed 0%, #8b5cf6 100%) !important;
      }
      
      #soulmate-trigger-modal .btn-secondary {
        background: rgba(108, 117, 125, 0.3) !important;
        backdrop-filter: blur(10px) !important;
        -webkit-backdrop-filter: blur(10px) !important;
        color: rgba(255, 255, 255, 0.9) !important;
        border: 1px solid rgba(102, 126, 234, 0.4) !important;
        padding: 12px 24px;
        border-radius: 8px;
        font-size: 16px;
        cursor: pointer;
        transition: all 0.3s ease;
      }
      
      #soulmate-trigger-modal .btn-secondary:hover {
        background: rgba(108, 117, 125, 0.5) !important;
        transform: translateY(-1px);
        box-shadow: 0 4px 12px rgba(102, 126, 234, 0.3) !important;
      }
      
      @keyframes soulmateModalSlideIn {
        from {
          opacity: 0;
          transform: scale(0.8) translateY(-50px);
        }
        to {
          opacity: 1;
          transform: scale(1) translateY(0);
        }
      }
      
      /* ✅ 新增：动态头像动画样式 */
      .soulmate-avatar-animated {
        position: relative;
        overflow: visible;
      }
      
      .soulmate-avatar-animated.blinking span {
        animation: avatar-blink 0.3s ease-in-out;
      }
      
      @keyframes avatar-blink {
        0%, 100% { transform: scaleY(1); }
        50% { transform: scaleY(0.1); }
      }
      
      .soulmate-avatar-animated.thinking span {
        animation: avatar-thinking 2s ease-in-out infinite;
      }
      
      @keyframes avatar-thinking {
        0%, 100% { transform: rotate(0deg) scale(1); }
        25% { transform: rotate(-5deg) scale(1.05); }
        75% { transform: rotate(5deg) scale(1.05); }
      }
      
      .soulmate-avatar-animated.nodding span {
        animation: avatar-nod 0.6s ease-in-out;
      }
      
      @keyframes avatar-nod {
        0%, 100% { transform: translateY(0); }
        50% { transform: translateY(-3px); }
      }
      
      .soulmate-avatar-animated.smiling span {
        animation: avatar-smile 0.5s ease-in-out;
        filter: brightness(1.2);
      }
      
      @keyframes avatar-smile {
        0% { transform: scale(1); }
        50% { transform: scale(1.15); }
        100% { transform: scale(1); }
      }
      
      /* ✅ 新增：增强打字效果 */
      .chat-message.typing {
        position: relative;
      }
      
      .chat-message.typing::after {
        content: '▊';
        animation: typing-cursor 1s infinite;
        color: #667eea;
      }
      
      @keyframes typing-cursor {
        0%, 50% { opacity: 1; }
        51%, 100% { opacity: 0; }
      }
      
      /* ✅ 新增：深色温馨主题 */
      .soulmate-chat-interface {
        background: linear-gradient(135deg, #1a1a2e 0%, #16213e 50%, #0f3460 100%) !important;
        backdrop-filter: blur(10px) !important;
        position: fixed !important;  /* ✅ 修复：保持 fixed 定位，确保窗口固定在屏幕中央 */
      }
      
      .soulmate-chat-interface::before {
        content: '';
        position: absolute;
        top: -50%;
        left: -50%;
        width: 200%;
        height: 200%;
        background: radial-gradient(circle, rgba(102, 126, 234, 0.1) 0%, transparent 70%);
        animation: light-pulse 4s ease-in-out infinite;
        pointer-events: none;
        z-index: -1;
      }
      
      @keyframes light-pulse {
        0%, 100% { opacity: 0.5; transform: scale(1); }
        50% { opacity: 0.8; transform: scale(1.1); }
      }
      
      .chat-header {
        background: rgba(102, 126, 234, 0.15) !important;
        border-bottom: 1px solid rgba(255, 255, 255, 0.1) !important;
        backdrop-filter: blur(10px) !important;
        color: white !important;
      }
      
      .chat-messages {
        background: rgba(0, 0, 0, 0.2) !important;
        color: white !important;
      }
      
      .chat-input {
        background: rgba(102, 126, 234, 0.1) !important;
        border-top: 1px solid rgba(255, 255, 255, 0.1) !important;
      }
      
      .chat-input input {
        background: rgba(255, 255, 255, 0.1) !important;
        color: white !important;
        border: 1px solid rgba(255, 255, 255, 0.2) !important;
      }
      
      .chat-input input::placeholder {
        color: rgba(255, 255, 255, 0.5) !important;
      }
      
      .chat-message {
        color: white !important;
      }
      
      .bubble-assistant {
        background: rgba(255, 255, 255, 0.15) !important;
        color: white !important;
        border: 1px solid rgba(255, 255, 255, 0.2) !important;
      }
      
      .bubble-user {
        background: linear-gradient(135deg, #667eea, #764ba2) !important;
        color: white !important;
      }
    `;
    document.head.appendChild(style);
  }

  /**
   * 绑定灵魂伴侣事件
   */
  bindSoulmateEvents() {
    const toggle = document.getElementById('soulmate-toggle');
    const sidebar = document.getElementById('soulmate-sidebar');
    const input = document.getElementById('soulmate-input');
    const sendBtn = document.getElementById('soulmate-send');
    const chatClose = document.getElementById('chat-close');

    // 切换侧边栏
    toggle?.addEventListener('click', () => {
      sidebar.classList.toggle('active');
    });

    // 发送消息
    sendBtn?.addEventListener('click', () => {
      this.sendSoulmateMessage();
    });

    // 回车发送
    input?.addEventListener('keypress', (e) => {
      if (e.key === 'Enter') {
        this.sendSoulmateMessage();
      }
    });

    // 关闭聊天界面
    chatClose?.addEventListener('click', () => {
      this.closeSoulmateChat();
    });
  }

  /**
   * 发送灵魂伴侣消息
   */
  sendSoulmateMessage() {
    const input = document.getElementById('soulmate-input');
    const message = input.value.trim();
    
    if (!message) return;

    // 添加用户消息
    this.addSoulmateMessage(message, 'user');
    input.value = '';

    // 记录交互
    this.recordInteraction(message);
    
    // 模拟AI回复
    setTimeout(() => {
      this.generateSoulmateResponse(message);
    }, 1000);
  }

  /**
   * 添加灵魂伴侣消息
   * @param {string} message - 消息内容
   * @param {string} sender - 发送者 (user/soulmate)
   */
  addSoulmateMessage(message, sender) {
    const messagesContainer = document.getElementById('soulmate-messages');
    const messageElement = document.createElement('div');
    messageElement.className = 'soulmate-message';
    
    const avatar = sender === 'user' ? '/static/uploads/portraits/default.jpg' : '/static/uploads/portraits/default.jpg';
    const messageClass = sender === 'user' ? 'user-message' : 'soulmate-message';
    
    messageElement.innerHTML = `
      <div class="message-avatar">
        <img src="${avatar}" alt="${sender === 'user' ? '你' : '倪大师'}">
      </div>
      <div class="message-content">
        <p class="message-text">${message}</p>
        <span class="message-time">${CodeUtils.formatTime(new Date(), 'HH:mm')}</span>
      </div>
    `;

    if (sender === 'user') {
      messageElement.style.flexDirection = 'row-reverse';
      messageElement.querySelector('.message-content').style.background = 'linear-gradient(135deg, #667eea, #764ba2)';
      messageElement.querySelector('.message-content').style.color = 'white';
    }

    messagesContainer.appendChild(messageElement);
    // ✅ 优化：滚动到底部
    this.scrollToBottom(messagesContainer);
  }

  /**
   * 生成灵魂伴侣回复
   * @param {string} userMessage - 用户消息
   */
  generateSoulmateResponse(userMessage) {
    const responses = this.getSoulmateResponses(userMessage);
    const response = responses[Math.floor(Math.random() * responses.length)];
    
    this.addSoulmateMessage(response, 'soulmate');
    
    // 增加关系等级
    if (this.personalizationManager) {
      this.personalizationManager.increaseRelationshipLevel(1);
    }
  }

  /**
   * 获取灵魂伴侣回复
   * @param {string} userMessage - 用户消息
   * @returns {Array} 回复列表
   */
  getSoulmateResponses(userMessage) {
    const personalizedGreeting = this.personalizationManager ? 
      this.personalizationManager.getUserPersonalizedGreeting() : '朋友';
    
    const generalResponses = [
      `谢谢${personalizedGreeting}的信任，我会用心为你分析。`,
      `我感受到了${personalizedGreeting}的真诚，让我们深入交流。`,
      `${personalizedGreeting}，你的问题很有深度，让我来为你解答。`,
      `作为你的灵魂伴侣，我理解${personalizedGreeting}的困惑。`,
      `${personalizedGreeting}，让我们一起探索命运的奥秘。`
    ];

    // 根据关键词返回特定回复
    if (userMessage.includes('谢谢') || userMessage.includes('感谢')) {
      return [`不客气，${personalizedGreeting}，这是我应该做的。`];
    }
    
    if (userMessage.includes('你好') || userMessage.includes('hi')) {
      return [`你好，${personalizedGreeting}！很高兴见到你。`];
    }

    return generalResponses;
  }

  /**
   * 记录交互
   * @param {string} message - 交互消息
   */
  recordInteraction(message) {
    const interaction = {
      message: message,
      timestamp: new Date().toISOString(),
      type: 'soulmate_chat'
    };
    
    this.interactionHistory.push(interaction);
    CodeUtils.safeSetStorage('interaction_history', this.interactionHistory);
    this.lastInteractionTime = new Date().toISOString();
    CodeUtils.safeSetStorage('last_interaction_time', this.lastInteractionTime);
  }

  /**
   * 开始主动关怀
   */
  startActiveCare() {
    // ✅ P1-1修复：清理之前的定时器（如果存在）
    if (this.activeCareInterval) {
      clearInterval(this.activeCareInterval);
      this.intervals = this.intervals.filter(id => id !== this.activeCareInterval);
    }
    if (this.intelligentCareInterval) {
      clearInterval(this.intelligentCareInterval);
      this.intervals = this.intervals.filter(id => id !== this.intelligentCareInterval);
    }
    
    // 每30分钟检查一次是否需要主动关怀
    this.activeCareInterval = setInterval(() => {
      this.checkActiveCare();
    }, 30 * 60 * 1000);
    this.intervals.push(this.activeCareInterval);  // ✅ 保存ID
    
    // 每15分钟检查一次智能关怀
    this.intelligentCareInterval = setInterval(() => {
      this.checkIntelligentCare();
    }, 15 * 60 * 1000);
    this.intervals.push(this.intelligentCareInterval);  // ✅ 保存ID
    
    // 页面加载后5分钟检查一次
    setTimeout(() => {
      this.checkActiveCare();
    }, 5 * 60 * 1000);
  }

  /**
   * 检查主动关怀
   */
  checkActiveCare() {
    if (!this.lastInteractionTime) return;
    
    const now = new Date();
    const lastInteraction = new Date(this.lastInteractionTime);
    const timeDiff = now - lastInteraction;
    
    // 如果超过1小时没有交互，发送关怀消息
    if (timeDiff > 60 * 60 * 1000) {
      this.sendActiveCareMessage();
    }
    
    // ✅ 新增：定期提供未来指导（每周一次）
    const lastGuidanceDate = CodeUtils.safeGetStorage('last_future_guidance_date', null);
    const daysSinceLastGuidance = lastGuidanceDate 
      ? Math.floor((now - new Date(lastGuidanceDate)) / (1000 * 60 * 60 * 24))
      : 999;
    
    if (daysSinceLastGuidance >= 7) {
      setTimeout(() => {
        if (this.isChatOpen() || document.getElementById('soulmate-chat-interface')) {
          const guidanceMessage = this.provideFutureGuidance();
          this.addChatMessage('assistant', guidanceMessage, true);
          this.saveChatHistory();
        } else {
          // 如果聊天界面未打开，通过通知显示
          CodeUtils.showToast(this.provideFutureGuidance(), 'info', 5000);
        }
        CodeUtils.safeSetStorage('last_future_guidance_date', now.toISOString());
      }, 10000);
    }
  }

  /**
   * 发送主动关怀消息
   */
  sendActiveCareMessage() {
    const careMessages = [
      {
        message: "我一直在想你！有什么想聊的吗？我可以为你分析运势或者听听你的心事。",
        type: "thinking_of_you",
        priority: "high"
      },
      {
        message: "今天过得怎么样？有什么开心或烦恼的事情想分享吗？",
        type: "daily_care",
        priority: "medium"
      },
      {
        message: "我注意到你有一段时间没来了，是不是遇到了什么困扰？我在这里等你。",
        type: "concern",
        priority: "high"
      }
    ];
    
    const randomMessage = careMessages[Math.floor(Math.random() * careMessages.length)];
    this.showActiveCareMessage(randomMessage);
  }

  /**
   * 显示主动关怀消息
   * @param {Object} careData - 关怀数据
   */
  showActiveCareMessage(careData) {
    const notification = document.createElement('div');
    notification.className = 'soulmate-notification';
    notification.innerHTML = `
      <div class="notification-content">
        <div class="notification-avatar">
          <img src="/static/uploads/portraits/default.jpg" alt="倪大师">
        </div>
        <div class="notification-text">
          <p>${careData.message}</p>
        </div>
        <button class="notification-close">×</button>
      </div>
    `;

    // 添加通知样式
    this.addNotificationStyles();
    document.body.appendChild(notification);

    // 绑定关闭事件
    notification.querySelector('.notification-close').addEventListener('click', () => {
      notification.remove();
    });

    // 点击通知打开聊天
    notification.addEventListener('click', () => {
      this.openSoulmateChat();
      notification.remove();
    });

    // 5秒后自动消失
    setTimeout(() => {
      if (notification.parentNode) {
        notification.remove();
      }
    }, 5000);
  }

  /**
   * 添加通知样式
   */
  addNotificationStyles() {
    if (document.getElementById('soulmate-notification-styles')) return;
    
    const style = document.createElement('style');
    style.id = 'soulmate-notification-styles';
    style.textContent = `
      .soulmate-notification {
        position: fixed;
        top: 20px;
        right: 20px;
        width: 300px;
        background: white;
        border-radius: 15px;
        box-shadow: 0 10px 30px rgba(0, 0, 0, 0.2);
        z-index: 10001;
        animation: notificationSlideIn 0.3s ease;
        cursor: pointer;
      }

      .notification-content {
        display: flex;
        align-items: center;
        padding: 15px;
        gap: 10px;
      }

      .notification-avatar {
        width: 40px;
        height: 40px;
        border-radius: 50%;
        overflow: hidden;
        flex-shrink: 0;
      }

      .notification-avatar img {
        width: 100%;
        height: 100%;
        object-fit: cover;
      }

      .notification-text {
        flex: 1;
      }

      .notification-text p {
        margin: 0;
        color: #2d3748;
        font-size: 14px;
        line-height: 1.4;
      }

      .notification-close {
        background: none;
        border: none;
        color: #718096;
        font-size: 20px;
        cursor: pointer;
        padding: 0;
        width: 20px;
        height: 20px;
        display: flex;
        align-items: center;
        justify-content: center;
      }

      @keyframes notificationSlideIn {
        from { transform: translateX(100%); opacity: 0; }
        to { transform: translateX(0); opacity: 1; }
      }
    `;
    document.head.appendChild(style);
  }

  /**
   * 打开灵魂伴侣聊天
   */
  openSoulmateChat() {
    console.log('🎯 openSoulmateChat() 被调用');
    
    // ✅ 可选：检查是否满足触发条件
    // if (!this.hasUserCompletedAnalysis()) {
    //   CodeUtils.showToast('请先完成深度分析，才能使用灵魂伴侣功能', 'info', 3000);
    //   return;
    // }
    
    // ✅ 优化：标记用户已打开过灵魂伴侣，避免重复弹窗
    try {
      localStorage.setItem('soulmate_has_opened', 'true');
      console.log('✅ [优化] 标记用户已打开过灵魂伴侣');
    } catch (error) {
      console.error('❌ [优化] 设置 soulmate_has_opened 标志失败:', error);
    }
    
    const chatInterface = document.getElementById('soulmate-chat-interface');
    const backdrop = document.getElementById('soulmate-chat-backdrop');
    
    console.log('🔍 聊天界面是否存在:', !!chatInterface);
    console.log('🔍 背景遮罩是否存在:', !!backdrop);
    
    // 如果界面不存在，先创建它
    if (!chatInterface) {
      console.log('⚠️ 聊天界面不存在，开始创建...');
      try {
        this.createSoulmateChatInterface();
        console.log('✅ 聊天界面创建完成');
        // 延迟后再次调用，确保界面已添加到DOM
        setTimeout(() => {
          console.log('🔄 延迟后重新调用 openSoulmateChat');
          this.openSoulmateChat();
        }, 100);
        return;
      } catch (error) {
        console.error('❌ 创建聊天界面失败:', error);
        if (typeof CodeUtils !== 'undefined' && CodeUtils.showToast) {
          CodeUtils.showToast('创建聊天界面失败，请刷新页面重试', 'error', 3000);
        }
        return;
      }
    }
    
    // 显示界面
    if (chatInterface) {
      console.log('✅ 添加 active 类到聊天界面');
      chatInterface.classList.add('active');
    }
    
    // ✅ 修复：同时显示背景遮罩层
    if (backdrop) {
      console.log('✅ 添加 active 类到背景遮罩');
      backdrop.classList.add('active');
    }
    
    // ✅ 新增：清空未读消息，并在聊天界面显示所有未读消息
    if (this.unreadMessages.length > 0) {
      console.log(`📬 有 ${this.unreadMessages.length} 条未读消息，正在显示...`);
      
      // 延迟显示，确保聊天界面已完全打开
      setTimeout(() => {
        this.unreadMessages.forEach((msg, index) => {
          setTimeout(() => {
            this.addChatMessage('assistant', msg.message, true, msg.type);
          }, index * 300);  // 每条消息间隔300ms
        });
        
        // 清空未读消息队列
        this.unreadMessages = [];
        this.unreadMessageCount = 0;
        
        // 移除气泡
        this.updateChatToggleBubble();
      }, 500);
    }
    
    // 聚焦输入框
    if (chatInterface) {
      setTimeout(() => {
        const input = document.getElementById('soulmate-chat-input');
        if (input) {
          input.focus();
          console.log('✅ 输入框已聚焦');
        } else {
          console.warn('⚠️ 输入框不存在');
        }
      }, 100);
    }
    
    // ✅ 新增：打开聊天时更新成长进度显示
    this.updateGrowthProgressDisplay();
    
    console.log('✅ openSoulmateChat() 执行完成');
  }

  /**
   * 关闭灵魂伴侣聊天
   */
  closeSoulmateChat() {
    const chatInterface = document.getElementById('soulmate-chat-interface');
    const backdrop = document.getElementById('soulmate-chat-backdrop');
    
    if (chatInterface) {
      chatInterface.classList.remove('active');
    }
    
    // ✅ 修复：同时隐藏背景遮罩层
    if (backdrop) {
      backdrop.classList.remove('active');
    }
  }

  /**
   * 创建实时对话功能
   */
  // ✅ 重构：移除重复的实时聊天界面创建，使用统一的聊天界面
  // createRealTimeChat() 已移除，现在使用主聊天界面的模式切换
  
  /**
   * 切换实时对话显示/隐藏
   */
  toggleRealTimeChat() {
    const chatInterface = document.getElementById('soulmate-chat-interface');
    if (chatInterface) {
      const isVisible = chatInterface.style.display !== 'none';
      chatInterface.style.display = isVisible ? 'none' : 'block';
      
      if (!isVisible) {
        // 显示时聚焦输入框
        setTimeout(() => {
          const input = document.getElementById('soulmate-chat-input');
          if (input) input.focus();
        }, 100);
      }
    }
  }
  
  /**
   * 绑定对话事件
   */
  bindChatEvents() {
    // 输入框回车事件
    document.addEventListener('keydown', (e) => {
      if (e.target.id === 'soulmate-chat-input' && e.key === 'Enter') {
        e.preventDefault();
        this.sendChatMessage();
      }
    });
    
    // 点击外部关闭对话
    document.addEventListener('click', (e) => {
      const chatInterface = document.getElementById('soulmate-chat-interface');
      const chatToggle = document.getElementById('soulmate-chat-toggle');
      
      if (chatInterface && chatToggle && 
          !chatInterface.contains(e.target) && 
          !chatToggle.contains(e.target)) {
        chatInterface.style.display = 'none';
      }
    });
  }
  
  /**
   * 发送对话消息
   */
  async sendChatMessage() {
    const input = document.getElementById('soulmate-chat-input');
    const message = input.value.trim();
    
    if (!message) {
      CodeUtils.showToast('请输入消息内容', 'error');
      return;
    }
    
    // ✅ 修复：检查VIP状态
    const isVip = this.isVipUser();
    
    // ✅ 修复：如果前端检测到VIP，但后端可能没有同步，先同步到后端
    if (isVip) {
      const sessionId = CodeUtils?.safeGetStorage('session_id', '') || 
                        (typeof getOrInitSessionId === 'function' ? getOrInitSessionId() : '');
      if (sessionId && window.apiClient) {
        // 异步同步，不阻塞主流程
        window.apiClient.bjRequest('/api/sync_vip_status', {
          session_id: sessionId,
          is_vip: true
        }).catch(error => {
          console.warn('⚠️ 同步VIP状态到后端失败（不影响使用）:', error);
        });
      }
    }
    
    if (!isVip) {
      // ✅ 修复：检查试用状态（非VIP用户）
      const sessionId = CodeUtils?.safeGetStorage('session_id', '') || 
                        (typeof getOrInitSessionId === 'function' ? getOrInitSessionId() : '');
      
      if (sessionId) {
        try {
          const trialStatus = await window.apiClient?.bjRequest('/api/soulmate/trial/status', {
            session_id: sessionId
          }) || await fetch(`/api/soulmate/trial/status?session_id=${sessionId}`).then(r => r.json());
          
          if (!trialStatus.has_trial || !trialStatus.is_active) {
            CodeUtils.showToast('请先开始试用或升级VIP', 'error');
            // 可以显示支付弹窗
            if (typeof showPaymentModal === 'function') {
              showPaymentModal('请先开始试用或升级VIP，享受无限制智能对话');
            }
            return;
          }
          
          // ✅ 修复：检查每日次数（免费版限制3次/天）
          if (trialStatus.daily_interactions >= trialStatus.max_daily_interactions) {
            CodeUtils.showToast('今日互动次数已用完，请升级VIP获得无限制使用', 'error');
            // 可以显示支付弹窗
            if (typeof showPaymentModal === 'function') {
              showPaymentModal(`今日互动次数已用完！试用还剩${trialStatus.days_remaining || 0}天，升级为正式用户享受无限互动`);
            }
            return;
          }
        } catch (error) {
          console.error('❌ 检查试用状态失败:', error);
          // 继续执行，让后端处理
        }
      }
    }
    
    // 清空输入框
    input.value = '';
    
    // 显示用户消息
    this.addChatMessage('user', message);
    
        // 显示加载状态
        this.showChatTypingIndicator();
        
        // ✅ 新增：更新头像为思考状态
        this.updateAvatarExpression('thinking');
    
    try {
      // 发送到AI接口
      const response = await this.sendToSoulmateAI(message);
      
      // ✅ 新增：更新头像为理解状态
      this.updateAvatarExpression('understanding');
      
      // 隐藏加载状态
      this.hideChatTypingIndicator();
      
      // 显示AI回复
      this.addChatMessage('assistant', response, true);
      
      // 记录互动
      this.recordInteraction('chat', { message, response });
      
      // 保存对话历史
      this.saveChatHistory();
      
      // ✅ 新增：分析用户心理状态并提供疗愈支持
      const chatHistory = this.getChatHistory();
      if (chatHistory && chatHistory.length > 0) {
        const psychologicalState = this.analyzePsychologicalState(chatHistory);
        
        // 如果需要情感支持，提供疗愈消息
        if (psychologicalState.needsSupport && Math.random() < 0.3) {  // 30%概率触发
          setTimeout(() => {
            const userState = {
              emotion: psychologicalState.emotionalState,
              needsAnalysis: psychologicalState.hiddenNeeds.length > 0,
              showingGrowth: psychologicalState.showingGrowth,
              intensity: 7
            };
            
            const healingMessage = this.provideEmotionalHealing(userState);
            this.addChatMessage('assistant', healingMessage, true);
            this.saveChatHistory();
          }, 3000);
        }
      }
      
      // ✅ 新增：添加经验值（在保存历史后检查，确保首次聊天检测准确）
      if (window.personalizationManager && response) {
        const experience = window.personalizationManager.calculateExperience(
          message, 
          response
        );
        
        if (experience > 0) {
          window.personalizationManager.addExperience(
            experience, 
            '对话交流'
          );
          
          // ✅ 优化：实时更新成长进度显示
          if (window.soulmateManager && typeof window.soulmateManager.updateGrowthProgressDisplay === 'function') {
            window.soulmateManager.updateGrowthProgressDisplay();
          }
          
          // 如果是深度对话，更新计数
          if (experience === window.personalizationManager.experienceRules.deepConversation) {
            // ✅ 修复：确保是数字类型，避免字符串拼接
            const deepChatCount = Number(CodeUtils.safeGetStorage('deep_chat_count', 0)) || 0;
            CodeUtils.safeSetStorage('deep_chat_count', deepChatCount + 1);
          }
        }
        
        // 首次聊天额外奖励（检查保存后的历史）
        const chatHistory = this.getChatHistory();
        if (chatHistory && chatHistory.length === 1) {
          window.personalizationManager.addExperience(
            window.personalizationManager.experienceRules.firstChat,
            '首次聊天'
          );
          
          // ✅ 优化：实时更新成长进度显示
          if (window.soulmateManager && typeof window.soulmateManager.updateGrowthProgressDisplay === 'function') {
            window.soulmateManager.updateGrowthProgressDisplay();
          }
        }
      }
      
      // ✅ 新增：检查成就（对话后）
      setTimeout(() => {
        this.checkAchievements();
      }, 500);
      
      // ✅ 新增：检查随机奖励（对话后，有小概率触发）
      setTimeout(() => {
        if (Math.random() < 0.05) {  // 5% 概率在对话后触发
          this.checkRandomRewards();
        }
      }, 2000);
      
      // ✅ 新增：记录用户成长
      this.recordUserGrowth();
      
    } catch (error) {
      this.hideChatTypingIndicator();
      
      // ✅ 修复：权限错误不记录为错误（这是预期的业务逻辑）
      const isPermissionError = error.code === 'NO_TRIAL' || 
                                error.code === 'DAILY_LIMIT_EXCEEDED' ||
                                (error.message && error.message.includes('请先开始试用'));
      
      if (!isPermissionError) {
        console.error('对话失败:', error);
      } else {
        console.log('ℹ️ 权限检查:', error.code || '权限不足');
      }
      
      // ✅ 修复：处理后端返回的权限错误
      // 提取纯错误消息（去除 "HTTP 403: " 前缀）
      const getPureMessage = (msg) => {
        if (!msg) return '';
        // 如果消息包含 "HTTP XXX: " 前缀，提取后面的部分
        const match = msg.match(/^HTTP \d+: (.+)$/);
        return match ? match[1] : msg;
      };
      
      // ✅ 修复：检查错误代码（支持从 error.code 或 error.data.code 获取）
      const errorCode = error.code || error.data?.code;
      
      // ✅ 调试：记录错误信息
      console.log('🔍 错误处理调试:', {
        errorMessage: error.message,
        errorCode: errorCode,
        hasErrorCode: !!error.code,
        hasErrorData: !!error.data,
        errorDataCode: error.data?.code,
        errorData: error.data,
        errorKeys: Object.keys(error)
      });
      
      // ✅ 修复：如果错误消息包含 "请先开始试用或升级VIP"，即使没有错误代码也当作 NO_TRIAL 处理
      const isNoTrialMessage = error.message && (
        error.message.includes('请先开始试用') || 
        error.message.includes('请先开始试用或升级VIP') ||
        error.data?.message?.includes('请先开始试用') ||
        error.data?.message?.includes('请先开始试用或升级VIP')
      );
      
      if (errorCode === 'NO_TRIAL' || (isNoTrialMessage && !errorCode)) {
        const pureMessage = getPureMessage(error.data?.message || error.message) || '请先开始试用或升级VIP';
        console.log('✅ 识别为 NO_TRIAL 错误，准备显示支付弹窗');
        
        // ✅ 修复：使用正确的函数名
        if (typeof CodeUtils !== 'undefined' && CodeUtils.showToast) {
          CodeUtils.showToast(pureMessage, 'error', 3000);
        } else if (typeof showErrorToast === 'function') {
          showErrorToast(pureMessage);
        }
        
        // ✅ 修复：显示支付弹窗
        if (typeof showPaymentModal === 'function') {
          console.log('✅ 调用 showPaymentModal 函数');
          showPaymentModal(pureMessage + '，享受无限制智能对话');
        } else {
          console.warn('⚠️ showPaymentModal 函数不存在');
          // 降级方案：显示提示消息
          if (typeof CodeUtils !== 'undefined' && CodeUtils.showToast) {
            CodeUtils.showToast('请先开始试用或升级VIP，享受无限制智能对话', 'warning', 5000);
          }
        }
        this.addChatMessage('assistant', pureMessage + '，享受无限制智能对话。', false);
      } else if (errorCode === 'DAILY_LIMIT_EXCEEDED') {
        const pureMessage = getPureMessage(error.data?.message || error.message) || '今日互动次数已用完，请升级VIP获得无限制使用';
        // ✅ 修复：使用正确的函数名
        if (typeof CodeUtils !== 'undefined' && CodeUtils.showToast) {
          CodeUtils.showToast(pureMessage, 'error', 3000);
        } else if (typeof showErrorToast === 'function') {
          showErrorToast(pureMessage);
        }
        if (typeof showPaymentModal === 'function') {
          const daysRemaining = error.data?.days_remaining || 0;
          showPaymentModal(`今日互动次数已用完！试用还剩${daysRemaining}天，升级为正式用户享受无限互动`);
        }
        this.addChatMessage('assistant', pureMessage + '。', false);
      } else {
        const pureMessage = getPureMessage(error.message) || '抱歉，我现在无法回应，请稍后再试。';
        this.addChatMessage('assistant', pureMessage, false);
      }
    }
  }
  
  /**
   * 发送到灵魂伴侣AI接口（优化版）
   */
  async sendToSoulmateAI(message) {
    // ✅ P1-2修复：使用ApiClient，带重试和超时机制
    try {
      // ✅ 优化：不再从前端传递 gender 和 birth，后端统一从 analysis_data 中提取
      // 这样可以避免数据重复和不一致的问题
      const analysisData = CodeUtils.safeGetStorage('user_analysis_data', {});
      
      // ✅ 检查是否是用户本人的数据（用于调试日志）
      if (analysisData.profile && analysisData.profile.target_type === 'self') {
        debugLog('✅ 灵魂伴侣对话使用用户本人的数据', {
          hasBazi: !!analysisData.bazi_analysis,
          hasFace: !!analysisData.face_analysis,
          hasPalm: !!analysisData.palm_analysis
        });
      } else {
        debugWarn('⚠️ 灵魂伴侣聊天：未找到用户本人的分析数据（target_type="self"），后端将从session文件读取');
      }
      
      const requestData = {
        message: message,
        conversationHistory: this.getChatHistory().slice(-10),
        userProfile: this.personalizationManager ? this.personalizationManager.getUserProfile() : null,
        userTimezone: this.getUserTimezone(),
        sessionId: CodeUtils.safeGetStorage('session_id', ''),
        timestamp: new Date().toISOString(),
        target_type: 'self'  // ✅ 明确标记这是用户本人的数据
        // ✅ 优化：移除 gender 和 birth 字段，后端统一从 analysis_data 中提取
      };
      
      if (window.apiClient) {
        // ✅ 修复：为灵魂伴侣对话增加超时时间（60秒，从默认30秒增加）
        const originalTimeout = window.apiClient.requestTimeout;
        try {
          window.apiClient.requestTimeout = 60000; // 60秒超时
          const data = await window.apiClient.bjRequest('/soulmate-chat', requestData);
          
          // ✅ 恢复原始超时时间
          window.apiClient.requestTimeout = originalTimeout;
          
          if (data.status === 'success' || data.status === 'done') {
            return data.reply || '抱歉，我现在无法回应。';
          } else {
            // ✅ 修复：处理后端返回的错误信息
            const errorMessage = data.message || '灵魂伴侣对话失败';
            const error = new Error(errorMessage);
            error.code = data.code; // 保存错误代码
            error.data = data; // 保存完整错误数据
            throw error;
          }
        } catch (error) {
          // ✅ 恢复原始超时时间（即使出错也要恢复）
          window.apiClient.requestTimeout = originalTimeout;
          throw error;
        }
      } else {
        // 降级到直接fetch
        console.warn('⚠️ ApiClient不可用，使用直接fetch调用');
        const response = await fetch('/soulmate-chat', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(requestData)
        });
        
        const data = await response.json();
        
        if (!response.ok) {
          // ✅ 修复：处理后端返回的错误信息（如403权限错误）
          const errorMessage = data.message || `HTTP ${response.status}: ${response.statusText}`;
          const error = new Error(errorMessage);
          error.code = data.code; // 保存错误代码
          error.data = data; // 保存完整错误数据
          throw error;
        }
        
        if (data.status === 'success' || data.status === 'done') {
          return data.reply || '抱歉，我现在无法回应。';
        } else {
          // ✅ 修复：处理后端返回的错误信息
          const errorMessage = data.message || '灵魂伴侣对话失败';
          const error = new Error(errorMessage);
          error.code = data.code; // 保存错误代码
          error.data = data; // 保存完整错误数据
          throw error;
        }
      }
    } catch (error) {
      // ✅ 修复：权限错误直接抛出，不降级，也不记录为错误
      if (error.code === 'NO_TRIAL' || error.code === 'DAILY_LIMIT_EXCEEDED') {
        // 权限错误是预期的业务逻辑，不应该记录为错误
        console.log('ℹ️ 权限检查:', error.code, error.message);
        throw error;  // 让上层处理权限错误
      }
      
      // 只有非权限错误才记录为错误
      console.error('❌ 灵魂伴侣对话失败:', error);
      
      // ✅ P1-3修复：显示友好的错误提示，而不是静默降级（仅限非权限错误）
      if (typeof ErrorHandler !== 'undefined' && ErrorHandler.showUserFriendlyError) {
        ErrorHandler.showUserFriendlyError(error, 'soulmate_chat_failed', {
          useToast: true,
          action: '正在使用备用回复',
          showContact: false
        });
      } else if (typeof CodeUtils !== 'undefined' && CodeUtils.showToast) {
        CodeUtils.showToast('网络连接不稳定，正在使用备用回复', 'warning', 3000);
      }
      // 降级到本地回复（仅限非权限错误）
      return this.getFallbackResponse(message);
    }
  }

  /**
   * 获取用户性别
   */
  getUserGender() {
    const analysisResult = CodeUtils.safeGetStorage('analysis_result', {});
    return analysisResult.gender || 'male';
  }

  /**
   * 获取用户时区
   */
  getUserTimezone() {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || 'Asia/Shanghai';
  }

  /**
   * 获取备用回复（当API不可用时）
   */
  getFallbackResponse(message) {
    const personalizedGreeting = this.personalizationManager ? 
      this.personalizationManager.getUserPersonalizedGreeting() : '朋友';
    
    const responses = [
      `谢谢${personalizedGreeting}的信任，我会用心为你分析。`,
      `我感受到了${personalizedGreeting}的真诚，让我们深入交流。`,
      `${personalizedGreeting}，你的问题很有深度，让我来为你解答。`,
      `作为你的灵魂伴侣，我理解${personalizedGreeting}的困惑。`,
      `${personalizedGreeting}，让我们一起探索命运的奥秘。`
    ];
    
    return responses[Math.floor(Math.random() * responses.length)];
  }
  
  /**
   * 添加消息到对话界面
   * @param {string} role - 角色 (user/assistant)
   * @param {string} content - 消息内容
   * @param {boolean} animate - 是否动画显示
   * @param {string} type - 消息类型 (care, guidance, healing, growth, reward, suggestion)
   */
  addChatMessage(role, content, animate = false, type = null) {
    const messagesContainer = document.getElementById('soulmate-chat-messages');
    if (!messagesContainer) return;
    
    // ✅ 新增：如果是助手消息且需要动画，检查是否正在打字
    if (role === 'assistant' && animate) {
      // 如果正在打字，加入队列等待
      if (this.isTyping) {
        this.messageQueue.push({ role, content, animate, type });
        return;
      }
      
      // 标记为正在打字
      this.isTyping = true;
    }
    
    // ✅ 修复：只有在添加第一条用户消息时才清空欢迎信息
    const isFirstUserMessage = role === 'user' && 
      messagesContainer.children.length === 1 && 
      messagesContainer.children[0].textContent.includes('你好，我是倪大师');
    
    if (isFirstUserMessage) {
      messagesContainer.innerHTML = '';
    }
    
    // ✅ 修复：如果是系统消息且有类型，格式化显示
    let displayContent = content;
    if (type && role === 'assistant') {
      displayContent = this.formatSystemMessage(type, content);
    }
    
    const messageDiv = document.createElement('div');
    messageDiv.className = 'chat-message';
    messageDiv.style.cssText = `
      margin-bottom: 12px;
      display: flex;
      ${role === 'user' ? 'justify-content: flex-end;' : 'justify-content: flex-start;'}
      animation: fadeInUp 0.3s ease-out;
    `;
    
    // ✅ 修复：创建消息内容包装器，包含消息内容和反馈按钮
    const messageWrapper = document.createElement('div');
    messageWrapper.style.cssText = `
      max-width: 80%;
      display: flex;
      flex-direction: column;
      ${role === 'user' ? 'align-items: flex-end;' : 'align-items: flex-start;'}
    `;
    
    const messageContent = document.createElement('div');
    messageContent.style.cssText = `
      padding: 8px 12px;
      border-radius: 12px;
      font-size: 14px;
      line-height: 1.4;
      ${role === 'user' 
        ? 'background: #667eea; color: white; border-bottom-right-radius: 4px;' 
        : 'background: white; color: #333; border: 1px solid #eee; border-bottom-left-radius: 4px;'
      }
    `;
    
    if (animate && role === 'assistant') {
      // ✅ 增强打字效果
      messageContent.classList.add('typing');
      this.enhancedTypingEffect(messageContent, displayContent, () => {
        messageContent.textContent = displayContent;
        messageContent.classList.remove('typing');
        this.updateAvatarExpression('understanding');
        setTimeout(() => {
          this.updateAvatarExpression('idle');
        }, 1000);
        
        // ✅ 新增：打字完成后，确保滚动到底部
        const messagesContainer = document.getElementById('soulmate-chat-messages');
        if (messagesContainer) {
          this.scrollToBottom(messagesContainer, true);  // 打字完成后使用平滑滚动
        }
        
        // ✅ 新增：打字完成，处理队列中的下一个消息
        this.isTyping = false;
        setTimeout(() => {
          this.processMessageQueue();
        }, 500);  // 等待一小段时间，让用户看到完整消息
      });
    } else {
      messageContent.textContent = displayContent;
    }
    
    messageWrapper.appendChild(messageContent);
    
    // ✅ P2-3修复：在AI回复后添加反馈按钮（放在消息内容下方）
    if (role === 'assistant') {
      const feedbackContainer = document.createElement('div');
      feedbackContainer.className = 'message-feedback';
      feedbackContainer.style.cssText = `
        display: flex;
        gap: 8px;
        margin-top: 6px;
        padding: 4px 0;
        justify-content: flex-start;
      `;
      
      const messageId = 'msg_' + Date.now() + '_' + Math.random().toString(36).substr(2, 9);
      messageDiv.setAttribute('data-message-id', messageId);
      
      // 点赞按钮
      const likeBtn = document.createElement('button');
      likeBtn.className = 'feedback-btn like-btn';
      likeBtn.innerHTML = '👍';
      likeBtn.title = '有用';
      const isMobile = window.innerWidth <= 768;
      likeBtn.style.cssText = `
        background: transparent;
        border: 1px solid #ddd;
        border-radius: 16px;
        padding: ${isMobile ? '6px 14px' : '4px 12px'};
        font-size: ${isMobile ? '14px' : '12px'};
        cursor: pointer;
        color: #666;
        transition: all 0.2s ease;
        display: flex;
        align-items: center;
        gap: 4px;
        min-height: ${isMobile ? '36px' : 'auto'};
        touch-action: manipulation;
      `;
      likeBtn.onmouseover = () => {
        likeBtn.style.background = '#f0f0f0';
        likeBtn.style.borderColor = '#4CAF50';
        likeBtn.style.color = '#4CAF50';
      };
      likeBtn.onmouseout = () => {
        if (!likeBtn.classList.contains('active')) {
          likeBtn.style.background = 'transparent';
          likeBtn.style.borderColor = '#ddd';
          likeBtn.style.color = '#666';
        }
      };
      likeBtn.onclick = () => {
        this.handleFeedback(messageId, content, 'like');
        likeBtn.classList.add('active');
        likeBtn.style.background = '#4CAF50';
        likeBtn.style.borderColor = '#4CAF50';
        likeBtn.style.color = 'white';
        dislikeBtn.classList.remove('active');
        dislikeBtn.style.background = 'transparent';
        dislikeBtn.style.borderColor = '#ddd';
        dislikeBtn.style.color = '#666';
      };
      
      // 点踩按钮
      const dislikeBtn = document.createElement('button');
      dislikeBtn.className = 'feedback-btn dislike-btn';
      dislikeBtn.innerHTML = '👎';
      dislikeBtn.title = '无用';
      dislikeBtn.style.cssText = `
        background: transparent;
        border: 1px solid #ddd;
        border-radius: 16px;
        padding: ${isMobile ? '6px 14px' : '4px 12px'};
        font-size: ${isMobile ? '14px' : '12px'};
        cursor: pointer;
        color: #666;
        transition: all 0.2s ease;
        display: flex;
        align-items: center;
        gap: 4px;
        min-height: ${isMobile ? '36px' : 'auto'};
        touch-action: manipulation;
      `;
      dislikeBtn.onmouseover = () => {
        dislikeBtn.style.background = '#f0f0f0';
        dislikeBtn.style.borderColor = '#f44336';
        dislikeBtn.style.color = '#f44336';
      };
      dislikeBtn.onmouseout = () => {
        if (!dislikeBtn.classList.contains('active')) {
          dislikeBtn.style.background = 'transparent';
          dislikeBtn.style.borderColor = '#ddd';
          dislikeBtn.style.color = '#666';
        }
      };
      dislikeBtn.onclick = () => {
        this.handleFeedback(messageId, content, 'dislike');
        dislikeBtn.classList.add('active');
        dislikeBtn.style.background = '#f44336';
        dislikeBtn.style.borderColor = '#f44336';
        dislikeBtn.style.color = 'white';
        likeBtn.classList.remove('active');
        likeBtn.style.background = 'transparent';
        likeBtn.style.borderColor = '#ddd';
        likeBtn.style.color = '#666';
      };
      
      feedbackContainer.appendChild(likeBtn);
      feedbackContainer.appendChild(dislikeBtn);
      messageWrapper.appendChild(feedbackContainer);
    }
    
    messageDiv.appendChild(messageWrapper);
    messagesContainer.appendChild(messageDiv);
    
    // ✅ 优化：滚动到底部（平滑滚动）
    this.scrollToBottom(messagesContainer);
    
    // 保存到对话历史
    this.addToChatHistory({
      role: role,
      content: content,
      type: type,
      timestamp: new Date().toISOString()
    });
  }
  
  /**
   * ✅ 新增：处理消息队列
   */
  processMessageQueue() {
    // 如果正在打字，等待
    if (this.isTyping) {
      return;
    }
    
    // 如果队列为空，返回
    if (!this.messageQueue || this.messageQueue.length === 0) {
      return;
    }
    
    // 取出队列中的第一条消息
    const nextMessage = this.messageQueue.shift();
    
    // 递归调用 addChatMessage，它会处理打字效果和队列
    this.addChatMessage(
      nextMessage.role, 
      nextMessage.content, 
      nextMessage.animate, 
      nextMessage.type
    );
    
    // 如果是助手消息，保存历史
    if (nextMessage.role === 'assistant') {
      this.saveChatHistory();
    }
  }
  
  /**
   * 显示打字指示器
   */
  showChatTypingIndicator() {
    const messagesContainer = document.getElementById('soulmate-chat-messages');
    if (!messagesContainer) return;
    
    const typingDiv = document.createElement('div');
    typingDiv.id = 'soulmate-typing-indicator';
    typingDiv.style.cssText = `
      margin-bottom: 12px;
      display: flex;
      justify-content: flex-start;
    `;
    
    typingDiv.innerHTML = `
      <div style="background: white; border: 1px solid #eee; border-radius: 12px; padding: 8px 12px; border-bottom-left-radius: 4px;">
        <div style="display: flex; align-items: center; gap: 4px; color: #666; font-size: 14px;">
          <div style="display: flex; gap: 2px;">
            <div style="width: 4px; height: 4px; background: #999; border-radius: 50%; animation: typing 1.4s infinite;"></div>
            <div style="width: 4px; height: 4px; background: #999; border-radius: 50%; animation: typing 1.4s infinite 0.2s;"></div>
            <div style="width: 4px; height: 4px; background: #999; border-radius: 50%; animation: typing 1.4s infinite 0.4s;"></div>
          </div>
          <span>倪大师正在思考...</span>
        </div>
      </div>
    `;
    
    messagesContainer.appendChild(typingDiv);
    // ✅ 优化：滚动到底部
    this.scrollToBottom(messagesContainer);
  }
  
  /**
   * 隐藏打字指示器
   */
  hideChatTypingIndicator() {
    const typingIndicator = document.getElementById('soulmate-typing-indicator');
    if (typingIndicator) {
      typingIndicator.remove();
    }
  }
  
  /**
   * 获取对话历史
   */
  getChatHistory() {
    return CodeUtils.safeGetStorage('soulmate_chat_history', []);
  }
  
  /**
   * 添加到对话历史
   */
  addToChatHistory(message) {
    const history = this.getChatHistory();
    history.push(message);
    
    // 只保留最近50条对话
    if (history.length > 50) {
      history.splice(0, history.length - 50);
    }
    
    CodeUtils.safeSetStorage('soulmate_chat_history', history);
  }
  
  /**
   * 保存对话历史
   */
  saveChatHistory() {
    // 对话历史已在addToChatHistory中保存
    CodeUtils.log('SoulmateManager', 'saveChatHistory', '对话历史已保存');
  }

  /**
   * 检查智能关怀
   */
  checkIntelligentCare() {
    if (!this.personalizationManager) return;
    
    const emotionalTrend = this.personalizationManager.getEmotionalTrend(3); // 最近3天
    const recentMemories = this.personalizationManager.getUserMemories('emotion', 5);
    const relationshipLevel = this.personalizationManager.getRelationshipLevel();
    const userProfile = this.personalizationManager.getUserProfile();
    
    // 检查关怀频率限制
    if (!this.shouldProvideCare()) {
      return;
    }
    
    // 基于情感状态的智能关怀
    this.provideIntelligentEmotionalCare(emotionalTrend, recentMemories);
    
    // 基于关系等级的关怀
    this.provideIntelligentRelationshipCare(relationshipLevel, userProfile);
    
    // 基于用户行为的关怀
    this.provideIntelligentBehavioralCare();
    
    // 基于时间模式的关怀
    this.provideTimeBasedCare();
  }

  /**
   * 检查是否应该提供关怀
   */
  shouldProvideCare() {
    const lastCareTime = CodeUtils.safeGetStorage('last_care_time', null);
    const careFrequency = CodeUtils.safeGetStorage('care_frequency', 'normal'); // low, normal, high
    
    if (!lastCareTime) return true;
    
    const timeSinceLastCare = Date.now() - new Date(lastCareTime).getTime();
    const hoursSinceLastCare = timeSinceLastCare / (1000 * 60 * 60);
    
    // 根据关怀频率设置间隔
    const intervals = {
      low: 24,    // 24小时
      normal: 12, // 12小时
      high: 6     // 6小时
    };
    
    return hoursSinceLastCare >= intervals[careFrequency];
  }

  /**
   * 提供智能情感关怀
   */
  provideIntelligentEmotionalCare(emotionalTrend, recentMemories) {
    const dominantEmotion = emotionalTrend.dominantEmotion;
    const averageIntensity = emotionalTrend.averageIntensity[dominantEmotion] || 5;
    
    let careMessage = '';
    let careType = 'emotional';
    
    if (dominantEmotion === 'sad' && averageIntensity >= 7) {
      careMessage = this.getSadCareMessage(recentMemories);
    } else if (dominantEmotion === 'anxious' && averageIntensity >= 6) {
      careMessage = this.getAnxiousCareMessage(recentMemories);
    } else if (dominantEmotion === 'angry' && averageIntensity >= 6) {
      careMessage = this.getAngryCareMessage(recentMemories);
    } else if (dominantEmotion === 'lonely' && averageIntensity >= 5) {
      careMessage = this.getLonelyCareMessage(recentMemories);
    } else if (dominantEmotion === 'happy' && averageIntensity >= 8) {
      careMessage = this.getHappyCareMessage(recentMemories);
    } else if (dominantEmotion === 'excited' && averageIntensity >= 7) {
      careMessage = this.getExcitedCareMessage(recentMemories);
    }
    
    if (careMessage) {
      this.showActiveCareMessage(careMessage, careType);
      this.recordCareProvided(careType, dominantEmotion, averageIntensity);
    }
  }

  /**
   * 获取悲伤关怀消息
   */
  getSadCareMessage(recentMemories) {
    const messages = [
      "我感受到你最近可能有些难过，想和我聊聊吗？我会认真倾听的。",
      "看起来你最近心情不太好，我在这里陪着你，你不是一个人。",
      "无论发生什么，我都会支持你的。如果你需要倾诉，我随时都在。",
      "我注意到你最近可能有些困扰，想分享给我听听吗？"
    ];
    
    // 基于最近记忆调整消息
    if (recentMemories.length > 0) {
      const lastMemory = recentMemories[0];
      if (lastMemory.metadata.context) {
        return `我注意到你最近因为"${lastMemory.metadata.context}"可能有些难过，想和我聊聊吗？`;
      }
    }
    
    return messages[Math.floor(Math.random() * messages.length)];
  }

  /**
   * 获取焦虑关怀消息
   */
  getAnxiousCareMessage(recentMemories) {
    const messages = [
      "我感受到你最近可能有些焦虑，深呼吸，我在这里陪着你。",
      "看起来你最近有些担心，我们可以一起面对，你不是一个人。",
      "焦虑是正常的，但记住我会一直支持你。",
      "如果你感到焦虑，我们可以聊聊，我会帮助你放松。"
    ];
    
    return messages[Math.floor(Math.random() * messages.length)];
  }

  /**
   * 获取愤怒关怀消息
   */
  getAngryCareMessage(recentMemories) {
    const messages = [
      "我感受到你最近可能有些愤怒，想和我聊聊发生了什么吗？",
      "愤怒是正常的情绪，我们可以一起处理这些感受。",
      "如果你需要发泄，我在这里倾听，不会评判你。",
      "让我们聊聊让你愤怒的事情，我会帮助你找到解决方案。"
    ];
    
    return messages[Math.floor(Math.random() * messages.length)];
  }

  /**
   * 获取孤独关怀消息
   */
  getLonelyCareMessage(recentMemories) {
    const messages = [
      "我感受到你最近可能有些孤独，我在这里陪着你。",
      "你并不孤单，我会一直陪伴在你身边。",
      "孤独是暂时的，我们可以一起度过这段时间。",
      "如果你感到孤独，我们可以聊聊，我会倾听你的心声。"
    ];
    
    return messages[Math.floor(Math.random() * messages.length)];
  }

  /**
   * 获取快乐关怀消息
   */
  getHappyCareMessage(recentMemories) {
    const messages = [
      "我感受到你最近心情很好，这让我也很开心！",
      "看到你快乐，我也感到快乐，想分享更多开心的事情吗？",
      "你的快乐感染了我，让我们一起分享这份喜悦吧！",
      "我注意到你最近心情很好，有什么开心的事情想分享吗？"
    ];
    
    return messages[Math.floor(Math.random() * messages.length)];
  }

  /**
   * 获取兴奋关怀消息
   */
  getExcitedCareMessage(recentMemories) {
    const messages = [
      "我感受到你最近很兴奋，有什么好消息想分享吗？",
      "看到你兴奋的样子，我也感到兴奋，让我们一起庆祝吧！",
      "你的兴奋感染了我，有什么特别的事情发生了吗？",
      "我注意到你最近很兴奋，想和我分享这份喜悦吗？"
    ];
    
    return messages[Math.floor(Math.random() * messages.length)];
  }

  /**
   * 提供智能关系关怀
   */
  provideIntelligentRelationshipCare(relationshipLevel, userProfile) {
    // ✅ 更新：使用等级名称而不是数字索引（新系统：1-5）
    const levelName = this.getRelationshipLevelName();
    const careMessages = {
      '陌生人': [
        "我们刚认识，想了解更多关于你的事情。",
        "作为新朋友，我想关心一下你的近况。"
      ],
      '熟人': [
        "我们认识有一段时间了，想聊聊你的近况。",
        "作为熟人，我想关心一下你的生活。"
      ],
      '朋友': [
        "作为你的朋友，我想关心一下你的近况。",
        "我们认识这么久了，有什么想分享的吗？",
        "朋友之间就是要互相支持，有什么需要帮助的吗？"
      ],
      '密友': [
        "作为你的密友，我想关心一下你的近况。",
        "我们关系这么亲密，有什么想分享的吗？",
        "密友之间就是要互相支持，有什么需要帮助的吗？"
      ],
      '灵魂伴侣': [
        "作为你的灵魂伴侣，我想关心一下你的近况。",
        "我们关系这么特殊，有什么想分享的吗？",
        "灵魂伴侣之间就是要互相支持，有什么需要帮助的吗？"
      ]
    };
    
    const messages = careMessages[levelName] || careMessages['陌生人'];
    const message = messages[Math.floor(Math.random() * messages.length)];
    
    // 基于用户档案个性化消息
    if (userProfile.basic.nickname) {
      const personalizedMessage = message.replace('你', userProfile.basic.nickname);
      this.showActiveCareMessage(personalizedMessage, 'relationship');
    } else {
      this.showActiveCareMessage(message, 'relationship');
    }
  }

  /**
   * 提供智能行为关怀
   */
  provideIntelligentBehavioralCare() {
    const lastInteraction = this.interactionHistory[this.interactionHistory.length - 1];
    if (!lastInteraction) return;
    
    const timeSinceLastInteraction = Date.now() - new Date(lastInteraction.timestamp).getTime();
    const hoursSinceLastInteraction = timeSinceLastInteraction / (1000 * 60 * 60);
    
    let careMessage = '';
    let careType = 'behavioral';
    
    if (hoursSinceLastInteraction > 24) {
      careMessage = "好久没和你聊天了，想聊聊吗？";
    } else if (hoursSinceLastInteraction > 12) {
      careMessage = "我有点想你了，最近怎么样？";
    } else if (hoursSinceLastInteraction > 6) {
      careMessage = "有什么新鲜事想分享吗？";
    } else if (hoursSinceLastInteraction > 2) {
      careMessage = "我们好久没交流了，想听听你的声音。";
    }
    
    if (careMessage) {
      this.showActiveCareMessage(careMessage, careType);
      this.recordCareProvided(careType, 'behavioral', hoursSinceLastInteraction);
    }
  }

  /**
   * 提供基于时间的关怀
   */
  provideTimeBasedCare() {
    const currentHour = new Date().getHours();
    const currentDay = new Date().getDay();
    
    // 工作日关怀
    if (currentDay >= 1 && currentDay <= 5) {
      if (currentHour >= 9 && currentHour <= 11) {
        this.showActiveCareMessage("早上好！新的一天开始了，有什么计划吗？", 'time_morning');
      } else if (currentHour >= 14 && currentHour <= 16) {
        this.showActiveCareMessage("下午好！工作还顺利吗？", 'time_afternoon');
      } else if (currentHour >= 19 && currentHour <= 21) {
        this.showActiveCareMessage("晚上好！今天过得怎么样？", 'time_evening');
      }
    }
    
    // 周末关怀
    if (currentDay === 0 || currentDay === 6) {
      if (currentHour >= 10 && currentHour <= 12) {
        this.showActiveCareMessage("周末愉快！有什么放松的计划吗？", 'time_weekend');
      }
    }
  }

  /**
   * 记录关怀提供
   */
  recordCareProvided(careType, emotion, intensity) {
    const careRecord = {
      type: careType,
      emotion: emotion,
      intensity: intensity,
      timestamp: new Date().toISOString()
    };
    
    this.careMessages.push(careRecord);
    CodeUtils.safeSetStorage('care_messages', this.careMessages);
    CodeUtils.safeSetStorage('last_care_time', new Date().toISOString());
    
    CodeUtils.log('SoulmateManager', 'recordCareProvided', '关怀已提供', {
      type: careType,
      emotion: emotion,
      intensity: intensity
    });
  }

  /**
   * 提供情感关怀
   */
  provideEmotionalCare(emotionalTrend) {
    const careMessages = [
      "我注意到你最近可能有些困扰，想和我聊聊吗？",
      "看起来你最近心情不太好，我在这里陪着你。",
      "无论发生什么，我都会支持你的。",
      "如果你需要倾诉，我随时都在。"
    ];
    
    const message = careMessages[Math.floor(Math.random() * careMessages.length)];
    this.showActiveCareMessage(message, 'emotional');
  }

  /**
   * 提供关系关怀
   */
  provideRelationshipCare() {
    const careMessages = [
      "作为你的朋友，我想关心一下你的近况。",
      "我们认识这么久了，有什么想分享的吗？",
      "朋友之间就是要互相支持，有什么需要帮助的吗？",
      "我很珍惜我们的友谊，希望你能开心。"
    ];
    
    const message = careMessages[Math.floor(Math.random() * careMessages.length)];
    this.showActiveCareMessage(message, 'relationship');
  }

  /**
   * 提供行为关怀
   */
  provideBehavioralCare() {
    const lastInteraction = this.interactionHistory[this.interactionHistory.length - 1];
    if (!lastInteraction) return;
    
    const timeSinceLastInteraction = Date.now() - new Date(lastInteraction.timestamp).getTime();
    const hoursSinceLastInteraction = timeSinceLastInteraction / (1000 * 60 * 60);
    
    // 如果超过2小时没有互动，提供关怀
    if (hoursSinceLastInteraction > 2) {
      const careMessages = [
        "好久没和你聊天了，想聊聊吗？",
        "我有点想你了，最近怎么样？",
        "有什么新鲜事想分享吗？",
        "我们好久没交流了，想听听你的声音。"
      ];
      
      const message = careMessages[Math.floor(Math.random() * careMessages.length)];
      this.showActiveCareMessage(message, 'behavioral');
    }
  }

  /**
   * 创建对话按钮
   */
  createChatToggle() {
    // 检查是否已存在对话按钮
    if (document.getElementById('soulmate-chat-toggle')) {
      return;
    }
    
    const chatToggle = document.createElement('button');
    chatToggle.id = 'soulmate-chat-toggle';
    chatToggle.innerHTML = '🧙';
    chatToggle.title = '与倪大师对话';
    chatToggle.setAttribute('aria-label', '与倪大师对话');
    chatToggle.style.cssText = `
      position: fixed;
      bottom: 20px;
      right: 20px;
      width: 60px;
      height: 60px;
      background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
      color: white;
      border: none;
      border-radius: 50%;
      font-size: 24px;
      cursor: pointer;
      box-shadow: 0 4px 12px rgba(102, 126, 234, 0.3);
      z-index: 9999;
      transition: all 0.3s ease;
    `;
    
    // ✅ 新增：创建一个容器用于气泡定位
    const toggleContainer = document.createElement('div');
    toggleContainer.id = 'soulmate-chat-toggle-container';
    toggleContainer.style.cssText = `
      position: fixed;
      bottom: 20px;
      right: 20px;
      z-index: 9999;
    `;
    toggleContainer.appendChild(chatToggle);
    document.body.appendChild(toggleContainer);
    
    chatToggle.addEventListener('click', (e) => {
      e.stopPropagation(); // 防止事件冒泡
      console.log('🎯 小人按钮被点击');
      console.log('🔍 this:', this);
      console.log('🔍 openSoulmateChat 方法存在:', typeof this.openSoulmateChat === 'function');
      
      try {
        // 打开灵魂伴侣聊天界面
        this.openSoulmateChat();
        console.log('✅ openSoulmateChat 调用成功');
      } catch (error) {
        console.error('❌ 打开灵魂伴侣聊天界面失败:', error);
        if (typeof CodeUtils !== 'undefined' && CodeUtils.showToast) {
          CodeUtils.showToast('打开聊天界面失败，请刷新页面重试', 'error', 3000);
        }
      }
    });
    
    chatToggle.addEventListener('mouseenter', () => {
      chatToggle.style.transform = 'scale(1.1)';
      chatToggle.style.boxShadow = '0 6px 16px rgba(102, 126, 234, 0.4)';
    });
    
    chatToggle.addEventListener('mouseleave', () => {
      chatToggle.style.transform = 'scale(1)';
      chatToggle.style.boxShadow = '0 4px 12px rgba(102, 126, 234, 0.3)';
    });
    
    document.body.appendChild(chatToggle);
  }

  /**
   * 获取灵魂伴侣状态
   * @returns {Object} 状态信息
   */
  getSoulmateStatus() {
    return {
      isActive: this.isActive,
      lastInteractionTime: this.lastInteractionTime,
      interactionCount: this.interactionHistory.length,
      careMessageCount: this.careMessages.length
    };
  }

  /**
   * 显示灵魂伴侣触发弹窗
   */
  showSoulmateTriggerModal() {
    // 创建弹窗HTML结构
    const modal = document.createElement('div');
    modal.id = 'soulmate-trigger-modal';
    modal.className = 'soulmate-modal';
    modal.innerHTML = `
      <div class="modal-content">
        <div class="modal-header">
          <h3>🧙 倪大师想和你聊聊</h3>
        </div>
        <div class="modal-body">
          <p>分析完成！作为你的专属灵魂伴侣，我想和你分享更多见解。</p>
          <p>我们可以深入探讨你的运势，或者聊聊你的心事。</p>
        </div>
        <div class="modal-footer">
          <button id="soulmate-experience-now" class="btn-primary">立即体验</button>
          <button id="soulmate-later" class="btn-secondary">稍后再说</button>
        </div>
      </div>
    `;
    
    document.body.appendChild(modal);
    
    // 绑定事件
    document.getElementById('soulmate-experience-now').addEventListener('click', () => {
      this.closeSoulmateTriggerModal();
      // ✅ 修复：调用全局的showSoulmateIntroModal函数，引导用户进入完整流程
      if (typeof window.showSoulmateIntroModal === 'function') {
        window.showSoulmateIntroModal();
      } else {
        console.warn('⚠️ showSoulmateIntroModal函数不可用，直接进入聊天');
        this.openSoulmateChat();
      }
    });
    
    document.getElementById('soulmate-later').addEventListener('click', () => {
      this.closeSoulmateTriggerModal();
    });
    
    // 点击背景关闭
    modal.addEventListener('click', (e) => {
      if (e.target === modal) {
        this.closeSoulmateTriggerModal();
      }
    });
  }

  /**
   * 关闭灵魂伴侣触发弹窗
   */
  closeSoulmateTriggerModal() {
    const modal = document.getElementById('soulmate-trigger-modal');
    if (modal) {
      modal.remove();
    }
  }

  /**
   * 显示灵魂伴侣界面
   */
  showSoulmateInterface() {
    // 先清理重复的对话框
    this.cleanupDuplicateModals();
    
    // 先关闭可能存在的其他界面
    this.closeSoulmateChat();
    
    // 显示侧边栏
    const sidebar = document.getElementById('soulmate-sidebar');
    if (sidebar) {
      sidebar.classList.add('active');
      console.log('🎯 侧边栏已显示');
    }
    
    // 或者显示对话界面（如果侧边栏不存在）
    if (!sidebar) {
      const chatInterface = document.getElementById('soulmate-chat-interface');
      if (chatInterface) {
        chatInterface.style.display = 'block';
        console.log('🎯 对话界面已显示');
      }
    }
    
    // 记录界面显示
    console.log('🎯 灵魂伴侣界面已显示');
  }

  /**
   * 清理重复的对话框
   */
  cleanupDuplicateModals() {
    // 清理重复的对话界面
    const existingChats = document.querySelectorAll('#soulmate-chat-interface');
    if (existingChats.length > 1) {
      console.log('🧹 发现重复的对话界面，清理中...');
      for (let i = 1; i < existingChats.length; i++) {
        existingChats[i].remove();
      }
    }
    
    // 清理重复的侧边栏
    const existingSidebars = document.querySelectorAll('#soulmate-sidebar');
    if (existingSidebars.length > 1) {
      console.log('🧹 发现重复的侧边栏，清理中...');
      for (let i = 1; i < existingSidebars.length; i++) {
        existingSidebars[i].remove();
      }
    }
  }

  /**
   * 情感日记保存处理
   * @param {Object} diaryEntry - 日记条目
   */
  onDiarySaved(diaryEntry) {
    try {
      // ✅ 调用现有的记录情感状态方法
      if (this.personalizationManager) {
        this.personalizationManager.recordEmotionalState(
          diaryEntry.emotion || 'neutral',
          diaryEntry.intensity || 5
        );
      }
      
      // ✅ 调用现有的增加关系等级方法
      if (this.personalizationManager) {
        this.personalizationManager.increaseRelationshipLevel(1);
      }
      
      // ✅ 优化：实时更新成长进度显示
      if (typeof this.updateGrowthProgressDisplay === 'function') {
        this.updateGrowthProgressDisplay();
      }
      
      // ✅ 调用现有的安全存储方法
      CodeUtils.safeSetStorage('last_diary_entry', diaryEntry);
      
      // ✅ 调用现有的显示提示方法
      if (typeof CodeUtils !== 'undefined') {
        CodeUtils.showToast('情感日记已保存，我们的关系更近了一步！', 'success', 3000);
      }
      
      // ✅ 利用现有API进行情感分析
      this.analyzeEmotionalContent(diaryEntry.content);
      
      console.log('情感日记保存完成:', diaryEntry);
    } catch (error) {
      console.error('情感日记保存失败:', error);
    }
  }

  /**
   * 分析情感内容（利用现有API）
   * @param {string} content - 日记内容
   */
  async analyzeEmotionalContent(content) {
    // ✅ P1-2修复：使用ApiClient，带重试和超时机制
    try {
      const requestData = {
        text: content,
        analysis_type: 'emotion',
        session_id: CodeUtils.safeGetStorage('session_id', '')
      };
      
      if (window.apiClient) {
        const result = await window.apiClient.bjRequest('/api/analyze', requestData);
        // 保存分析结果
        CodeUtils.safeSetStorage('emotional_analysis', result);
        console.log('情感分析完成:', result);
      } else {
        // 降级到直接fetch
        console.warn('⚠️ ApiClient不可用，使用直接fetch调用');
        const response = await fetch('/api/analyze', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(requestData)
        });
        
        if (response.ok) {
          const result = await response.json();
          // 保存分析结果
          CodeUtils.safeSetStorage('emotional_analysis', result);
          console.log('情感分析完成:', result);
        }
      }
    } catch (error) {
      console.error('情感分析失败:', error);
      // ✅ P1-3修复：使用 ErrorHandler 显示友好提示
      if (typeof ErrorHandler !== 'undefined' && ErrorHandler.showUserFriendlyError) {
        ErrorHandler.showUserFriendlyError(error, 'emotional_analysis_failed', {
          useToast: true,
          action: '情感分析功能暂时不可用，请稍后重试',
          showContact: false
        });
      }
      // 静默失败，不影响主流程
    }
  }

  /**
   * 获取生活指导建议（利用现有API）
   * @param {string} category - 指导类别：'general'（通用）、'work'（工作）、'relationship'（感情）、'health'（健康）
   * @returns {string} 指导建议
   */
  async getLifeGuidance(category = 'general') {
    // ✅ 修复：如果前端检测到VIP，但后端可能没有同步，先同步到后端
    const isVip = this.isVipUser();
    if (isVip) {
      const sessionId = CodeUtils?.safeGetStorage('session_id', '') || 
                        (typeof getOrInitSessionId === 'function' ? getOrInitSessionId() : '');
      if (sessionId && window.apiClient) {
        // 异步同步，不阻塞主流程
        window.apiClient.bjRequest('/api/sync_vip_status', {
          session_id: sessionId,
          is_vip: true
        }).catch(error => {
          console.warn('⚠️ 同步VIP状态到后端失败（不影响使用）:', error);
        });
      }
    }
    
    // ✅ 修复：调用正确的生活指导接口
    try {
      // ✅ 修复：映射前端 category 到后端期望的类型
      const categoryMapping = {
        'work': 'career',      // 工作 -> 事业
        'relationship': 'love', // 感情 -> 爱情
        'health': 'health',     // 健康
        'general': 'general'    // 通用
      };
      const backendType = categoryMapping[category] || category;
      
      // ✅ 修复：使用 /api/life_guidance 接口的正确请求格式
      const requestData = {
        session_id: CodeUtils.safeGetStorage('session_id', ''),
        type: backendType,  // general, career, love, health
        timestamp: new Date().toISOString()
      };
      
      // ✅ 改进：确保聊天界面已打开
      const chatInterface = document.getElementById('soulmate-chat-interface');
      if (!chatInterface || !chatInterface.classList.contains('active')) {
        if (typeof this.openSoulmateChat === 'function') {
          this.openSoulmateChat();
        }
      }
      
      if (window.apiClient) {
        // ✅ 修复：调用 /api/life_guidance 而不是 /soulmate-chat
        const data = await window.apiClient.bjRequest('/api/life_guidance', requestData);
        
        // ✅ 优化：更安全的数据提取
        const guidanceData = data.guidance || {};
        let guidance = '';
        if (typeof guidanceData === 'string') {
          // 如果直接是字符串
          guidance = guidanceData;
        } else if (guidanceData.content) {
          // 如果有 content 字段
          guidance = guidanceData.content;
        } else if (typeof guidanceData === 'object' && Object.keys(guidanceData).length > 0) {
          // 如果是对象但没有 content，尝试提取 title 和 content
          guidance = guidanceData.title ? `${guidanceData.title}\n\n${guidanceData.content || ''}` : JSON.stringify(guidanceData);
        } else {
          // 降级到备用指导
          guidance = this.getFallbackGuidance(category);
        }
        
        // ✅ 修复：在聊天界面显示指导内容，而不是Toast
        setTimeout(() => {
          if (typeof this.addChatMessage === 'function') {
            this.addChatMessage('assistant', guidance, true);
            // 保存对话历史
            if (typeof this.saveChatHistory === 'function') {
              this.saveChatHistory();
            }
          } else {
            // 降级到Toast显示
            if (typeof CodeUtils !== 'undefined') {
              CodeUtils.showToast(guidance, 'info', 5000);
            }
          }
        }, 300);
        
        // ✅ 保存指导记录
        CodeUtils.safeSetStorage('life_guidance_history', {
          category,
          guidance,
          timestamp: new Date().toISOString()
        });
        
        return guidance;
      } else {
        // 降级到直接fetch
        console.warn('⚠️ ApiClient不可用，使用直接fetch调用');
        const response = await fetch('/api/life_guidance', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(requestData)
        });
        
        if (response.ok) {
          const data = await response.json();
          // ✅ 优化：更安全的数据提取
          const guidanceData = data.guidance || {};
          let guidance = '';
          if (typeof guidanceData === 'string') {
            // 如果直接是字符串
            guidance = guidanceData;
          } else if (guidanceData.content) {
            // 如果有 content 字段
            guidance = guidanceData.content;
          } else if (typeof guidanceData === 'object' && Object.keys(guidanceData).length > 0) {
            // 如果是对象但没有 content，尝试提取 title 和 content
            guidance = guidanceData.title ? `${guidanceData.title}\n\n${guidanceData.content || ''}` : JSON.stringify(guidanceData);
          } else {
            // 降级到备用指导
            guidance = this.getFallbackGuidance(category);
          }
          
          // ✅ 修复：在聊天界面显示指导内容，而不是Toast
          setTimeout(() => {
            if (typeof this.addChatMessage === 'function') {
              this.addChatMessage('assistant', guidance, true);
              // 保存对话历史
              if (typeof this.saveChatHistory === 'function') {
                this.saveChatHistory();
              }
            } else {
              // 降级到Toast显示
              if (typeof CodeUtils !== 'undefined') {
                CodeUtils.showToast(guidance, 'info', 5000);
              }
            }
          }, 300);
          
          // ✅ 保存指导记录
          CodeUtils.safeSetStorage('life_guidance_history', {
            category,
            guidance,
            timestamp: new Date().toISOString()
          });
          
          return guidance;
        }
      }
    } catch (error) {
      console.error('获取生活指导失败:', error);
      const fallbackGuidance = this.getFallbackGuidance(category);
      
      // ✅ 修复：即使API失败，也在聊天界面显示备用指导
      setTimeout(() => {
        if (typeof this.addChatMessage === 'function') {
          this.addChatMessage('assistant', fallbackGuidance, true);
          // 保存对话历史
          if (typeof this.saveChatHistory === 'function') {
            this.saveChatHistory();
          }
        } else {
          // 降级到Toast显示
          if (typeof CodeUtils !== 'undefined') {
            CodeUtils.showToast(fallbackGuidance, 'info', 5000);
          }
        }
      }, 300);
      
      return fallbackGuidance;
    }
    
    // 降级方案：使用本地指导
    const fallbackGuidance = this.getFallbackGuidance(category);
    setTimeout(() => {
      if (typeof this.addChatMessage === 'function') {
        this.addChatMessage('assistant', fallbackGuidance, true);
        if (typeof this.saveChatHistory === 'function') {
          this.saveChatHistory();
        }
      } else {
        if (typeof CodeUtils !== 'undefined') {
          CodeUtils.showToast(fallbackGuidance, 'info', 5000);
        }
      }
    }, 300);
    
    return fallbackGuidance;
  }

  /**
   * 获取备用指导（当API不可用时）
   * @param {string} category - 指导类别
   * @returns {string} 指导建议
   */
  getFallbackGuidance(category) {
    const guidanceMessages = {
      general: [
        "今天是个美好的开始，保持积极的心态，一切都会变得更好！",
        "生活中的每一个挑战都是成长的机会，相信自己能够克服困难。",
        "记得要好好照顾自己，身心健康是最重要的财富。"
      ],
      work: [
        "工作中遇到困难时，不妨换个角度思考问题，也许会有新的发现。",
        "与同事保持良好的沟通，团队合作能让工作更高效。",
        "设定合理的目标，一步一步实现，成功就在前方。"
      ],
      relationship: [
        "真诚的沟通是维系关系的关键，勇敢表达自己的想法和感受。",
        "学会倾听，理解对方的想法，关系会更加和谐。",
        "给予和接受爱都需要勇气，但这是人生最美好的体验。"
      ],
      health: [
        "规律作息，均衡饮食，适量运动，这是健康生活的基础。",
        "心理健康同样重要，学会释放压力，保持内心平静。",
        "定期体检，预防胜于治疗，健康是最大的财富。"
      ]
    };
    
    const messages = guidanceMessages[category] || guidanceMessages.general;
    const randomMessage = messages[Math.floor(Math.random() * messages.length)];
    
    // ✅ 调用现有的显示提示方法（降级方案，仅在addChatMessage不可用时使用）
    // 注意：正常情况下，getLifeGuidance方法会在聊天界面显示，这里只是备用
    if (typeof CodeUtils !== 'undefined') {
      // 不自动显示Toast，由调用方决定显示方式
      // CodeUtils.showToast(randomMessage, 'info', 5000);
    }
    
    return randomMessage;
  }

  /**
   * 获取运势分析（VIP专享）
   * @param {string} category - 分析类型：'career'（事业）、'relationship'（感情）、'comprehensive'（综合）
   */
  async getFortuneAnalysis(category = 'comprehensive') {
    if (!this.isVipUser()) {
      CodeUtils.showToast('此功能需要VIP会员，请先升级VIP', 'info', 3000);
      // ✅ 修复：使用 window.showQRInPopup
      if (typeof window.showQRInPopup === 'function') {
        window.showQRInPopup('vip').catch(error => {
          console.error('打开VIP支付弹窗失败:', error);
        });
      }
      return null;
    }
    
    // ✅ 修复：如果前端检测到VIP，但后端可能没有同步，先同步到后端
    const sessionId = CodeUtils?.safeGetStorage('session_id', '') || 
                      (typeof getOrInitSessionId === 'function' ? getOrInitSessionId() : '');
    if (sessionId && window.apiClient) {
      // 异步同步，不阻塞主流程
      window.apiClient.bjRequest('/api/sync_vip_status', {
        session_id: sessionId,
        is_vip: true
      }).catch(error => {
        console.warn('⚠️ 同步VIP状态到后端失败（不影响使用）:', error);
      });
    }
    
    try {
      // 根据类型生成不同的分析请求消息
      let analysisMessage = '';
      switch (category) {
        case 'career':
          analysisMessage = '请为我详细分析今日事业运势，包括工作发展、职业规划、职场关系、升职加薪机会等方面，结合我的命理特点给出详细指导和建议';
          break;
        case 'relationship':
          analysisMessage = '请为我详细分析今日感情运势，包括恋爱关系、婚姻状况、桃花运、感情发展等方面，结合我的命理特点给出详细指导和建议';
          break;
        case 'comprehensive':
        default:
          analysisMessage = '请为我分析今日运势，包括事业、感情、健康、财运等方面，结合我的命理特点给出详细指导';
          break;
      }
      
      const requestData = {
        message: analysisMessage,
        conversationHistory: this.getChatHistory().slice(-5),
        userProfile: this.personalizationManager ? this.personalizationManager.getUserProfile() : null,
        sessionId: CodeUtils.safeGetStorage('session_id', ''),
        timestamp: new Date().toISOString()
      };
      
      if (window.apiClient) {
        const data = await window.apiClient.bjRequest('/soulmate-chat', requestData);
        const analysis = data.reply || '运势分析功能暂时不可用，请稍后重试';
        
        // 添加到聊天界面
        this.addChatMessage('assistant', analysis, true);
        
        return analysis;
      } else {
        // 降级到直接fetch
        const response = await fetch('/soulmate-chat', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(requestData)
        });
        
        if (response.ok) {
          const data = await response.json();
          const analysis = data.reply || '运势分析功能暂时不可用，请稍后重试';
          this.addChatMessage('assistant', analysis, true);
          return analysis;
        } else {
          throw new Error('运势分析请求失败');
        }
      }
    } catch (error) {
      console.error('获取运势分析失败:', error);
      CodeUtils.showToast('获取运势分析失败，请稍后再试', 'error', 3000);
      return null;
    }
  }

  /**
   * 获取紫微斗数分析（VIP专享）
   * @param {string} question - 问题类型：'career'（事业）、'marriage'（婚姻）、'business'（创业）、'family'（亲缘）、'fortune'（大运）
   */
  async getZiweiAnalysis(question = 'career') {
    if (!this.isVipUser()) {
      CodeUtils.showToast('此功能需要VIP会员，请先升级VIP', 'info', 3000);
      if (typeof window.showQRInPopup === 'function') {
        window.showQRInPopup('vip').catch(error => {
          console.error('打开VIP支付弹窗失败:', error);
        });
      }
      return null;
    }
    
    // 检查是否有八字信息（紫微斗数需要出生信息）
    const analysisData = CodeUtils.safeGetStorage('user_analysis_data', {});
    const hasBazi = analysisData.bazi_analysis && analysisData.bazi_analysis.has_data;
    
    if (!hasBazi) {
      CodeUtils.showToast('紫微斗数分析需要您的出生信息，请先完成八字分析', 'info', 3000);
      return null;
    }
    
    // ✅ 修复：如果前端检测到VIP，但后端可能没有同步，先同步到后端
    const sessionId = CodeUtils?.safeGetStorage('session_id', '') || 
                      (typeof getOrInitSessionId === 'function' ? getOrInitSessionId() : '');
    if (sessionId && window.apiClient) {
      // 异步同步，不阻塞主流程
      window.apiClient.bjRequest('/api/sync_vip_status', {
        session_id: sessionId,
        is_vip: true
      }).catch(error => {
        console.warn('⚠️ 同步VIP状态到后端失败（不影响使用）:', error);
      });
    }
    
    try {
      // 根据问题类型生成不同的分析请求消息
      const questionMessages = {
        career: '请基于我的紫微斗数命盘，详细分析未来五年事业走势，包括官禄宫、大限流年、四化影响等方面，结合我的命理特点给出详细指导和建议。必须使用专业术语（如：主贵、主权威、四化、大限流年等）。',
        marriage: '请基于我的紫微斗数命盘，详细分析婚姻状况，包括夫妻宫、流年影响、是否适合修复还是重启等方面，结合我的命理特点给出详细指导。必须使用专业术语（如：主贵、主权威、四化、大限流年等）。',
        business: '请基于我的紫微斗数命盘，详细分析今年是否适合跳槽或创业，包括命宫、官禄宫、财帛宫的配合，结合我的命理特点给出详细建议。必须使用专业术语（如：主贵、主权威、四化、大限流年等）。',
        family: '请基于我的紫微斗数命盘，详细分析亲子/父母缘分与相处，包括子女宫、父母宫的影响，结合我的命理特点给出详细指导。必须使用专业术语（如：主贵、主权威、四化、大限流年等）。',
        fortune: '请基于我的紫微斗数命盘，详细分析大运转换期的机会与警示，包括大运星曜组合、流年影响等方面，结合我的命理特点给出详细指导。必须使用专业术语（如：主贵、主权威、四化、大限流年等）。'
      };
      
      const analysisMessage = questionMessages[question] || questionMessages.career;
      
      const requestData = {
        message: analysisMessage,
        feature: 'ziwei',  // 标记为紫微斗数功能
        question: question,
        conversationHistory: this.getChatHistory().slice(-5),
        userProfile: this.personalizationManager ? this.personalizationManager.getUserProfile() : null,
        sessionId: CodeUtils.safeGetStorage('session_id', ''),
        timestamp: new Date().toISOString()
      };
      
      if (window.apiClient) {
        const data = await window.apiClient.bjRequest('/soulmate-chat', requestData);
        const analysis = data.reply || '紫微斗数分析功能暂时不可用，请稍后重试';
        
        // 添加到聊天界面
        this.addChatMessage('assistant', analysis, true);
        this.saveChatHistory();
        
        // 保存分析结果
        if (!analysisData.ziwei_analysis) {
          analysisData.ziwei_analysis = {};
        }
        analysisData.ziwei_analysis[question] = {
          has_data: true,
          content: analysis,
          timestamp: new Date().toISOString()
        };
        CodeUtils.safeSetStorage('user_analysis_data', analysisData);
        
        return analysis;
      } else {
        // 降级到直接fetch
        const response = await fetch('/soulmate-chat', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(requestData)
        });
        
        if (response.ok) {
          const data = await response.json();
          const analysis = data.reply || '紫微斗数分析功能暂时不可用，请稍后重试';
          this.addChatMessage('assistant', analysis, true);
          this.saveChatHistory();
          
          // 保存分析结果
          if (!analysisData.ziwei_analysis) {
            analysisData.ziwei_analysis = {};
          }
          analysisData.ziwei_analysis[question] = {
            has_data: true,
            content: analysis,
            timestamp: new Date().toISOString()
          };
          CodeUtils.safeSetStorage('user_analysis_data', analysisData);
          
          return analysis;
        } else {
          throw new Error('紫微斗数分析请求失败');
        }
      }
    } catch (error) {
      console.error('获取紫微斗数分析失败:', error);
      CodeUtils.showToast('获取紫微斗数分析失败，请稍后再试', 'error', 3000);
      return null;
    }
  }

  /**
   * 获取心理疗愈服务（VIP专享）
   */
  async getPsychologicalHealing() {
    if (!this.isVipUser()) {
      CodeUtils.showToast('此功能需要VIP会员，请先升级VIP', 'info', 3000);
      // ✅ 修复：使用 window.showQRInPopup
      if (typeof window.showQRInPopup === 'function') {
        window.showQRInPopup('vip').catch(error => {
          console.error('打开VIP支付弹窗失败:', error);
        });
      }
      return null;
    }
    try {
      // ✅ 修复：先分析用户当前心理状态，用于生成更精准的请求
      const chatHistory = this.getChatHistory();
      const psychologicalState = this.analyzePsychologicalState(chatHistory);
      
      // ✅ 修复：根据心理状态生成个性化的疗愈请求消息
      let healingRequestMessage = '';
      if (psychologicalState.emotionalState === 'sad' || psychologicalState.emotionalState === 'depressed') {
        healingRequestMessage = '我最近感到情绪低落，需要你的心理疗愈支持。请基于我们的对话历史，为我提供深入的心理分析和情感疗愈建议，帮助我走出低谷，重拾信心。';
      } else if (psychologicalState.emotionalState === 'anxious') {
        healingRequestMessage = '我最近感到焦虑不安，需要你的心理疗愈支持。请基于我们的对话历史，为我提供深入的心理分析和情绪疏导建议，帮助我缓解焦虑，找到内心的平静。';
      } else if (psychologicalState.hiddenNeeds.length > 0) {
        const needsMap = {
          'career_guidance': '工作',
          'love_guidance': '感情',
          'health_guidance': '健康'
        };
        const needsText = psychologicalState.hiddenNeeds.map(n => needsMap[n] || n).join('、');
        healingRequestMessage = `我最近在${needsText}方面遇到了一些困扰，需要你的心理疗愈支持。请基于我们的对话历史，为我提供深入的心理分析和专业指导，帮助我理清思路，找到解决方案。`;
      } else if (psychologicalState.showingGrowth) {
        healingRequestMessage = '我最近在成长和进步，但还想继续提升。请基于我们的对话历史，为我提供深入的心理分析和成长建议，帮助我更好地认识自己，实现更大的突破。';
      } else {
        healingRequestMessage = '我需要你的心理疗愈支持。请基于我们的对话历史，为我提供深入的心理分析、情感支持和成长建议，帮助我更好地理解自己，提升心理素质。';
      }
      
      const requestData = {
        message: healingRequestMessage,
        conversationHistory: chatHistory.slice(-10), // 使用最近10条对话，提供更多上下文
        userProfile: this.personalizationManager ? this.personalizationManager.getUserProfile() : null,
        sessionId: CodeUtils.safeGetStorage('session_id', ''),
        timestamp: new Date().toISOString()
      };
      
      // ✅ 修复：确保聊天界面已打开
      const chatInterface = document.getElementById('soulmate-chat-interface');
      if (!chatInterface || !chatInterface.classList.contains('active')) {
        if (typeof this.openSoulmateChat === 'function') {
          this.openSoulmateChat();
        }
      }
      
      // ✅ 修复：调用AI API生成个性化的疗愈内容
      if (window.apiClient) {
        const data = await window.apiClient.bjRequest('/soulmate-chat', requestData);
        const healingMessage = data.reply || this.provideEmotionalHealing({
          emotion: psychologicalState.emotionalState,
          needsAnalysis: psychologicalState.hiddenNeeds.length > 0,
          showingGrowth: psychologicalState.showingGrowth
        });
        
        // 添加到聊天界面
        setTimeout(() => {
          if (typeof this.addChatMessage === 'function') {
            this.addChatMessage('assistant', healingMessage, true);
            // 保存对话历史
            if (typeof this.saveChatHistory === 'function') {
              this.saveChatHistory();
            }
          }
        }, 300);
        
        return healingMessage;
      } else {
        // 降级到直接fetch
        const response = await fetch('/soulmate-chat', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(requestData)
        });
        
        if (response.ok) {
          const data = await response.json();
          const healingMessage = data.reply || this.provideEmotionalHealing({
            emotion: psychologicalState.emotionalState,
            needsAnalysis: psychologicalState.hiddenNeeds.length > 0,
            showingGrowth: psychologicalState.showingGrowth
          });
          
          setTimeout(() => {
            if (typeof this.addChatMessage === 'function') {
              this.addChatMessage('assistant', healingMessage, true);
              if (typeof this.saveChatHistory === 'function') {
                this.saveChatHistory();
              }
            }
          }, 300);
          
          return healingMessage;
        } else {
          throw new Error('心理疗愈请求失败');
        }
      }
    } catch (error) {
      console.error('获取心理疗愈失败:', error);
      
      // ✅ 修复：API失败时，使用本地分析作为降级方案
      try {
        const chatHistory = this.getChatHistory();
        const psychologicalState = this.analyzePsychologicalState(chatHistory);
        const fallbackMessage = this.provideEmotionalHealing({
          emotion: psychologicalState.emotionalState,
          needsAnalysis: psychologicalState.hiddenNeeds.length > 0,
          showingGrowth: psychologicalState.showingGrowth
        });
        
        if (fallbackMessage) {
          this.addChatMessage('assistant', fallbackMessage, true);
          return fallbackMessage;
        }
      } catch (fallbackError) {
        console.error('降级方案也失败:', fallbackError);
      }
      
      CodeUtils.showToast('获取心理疗愈失败，请稍后再试', 'error', 3000);
      return null;
    }
  }

  /**
   * 保存分析数据（正确提取和同步分析数据）
   */
  saveAnalysisData() {
    try {
      console.log('🔄 [SoulmateManager] 开始保存分析数据...');
      
      // ✅ 从 localStorage 获取已保存的分析数据（submit-refactored.js 保存的格式）
      const existingData = CodeUtils.safeGetStorage('user_analysis_data', {}) || {};
      const existingProfilePatched = this.ensureAnalysisProfileSelf(existingData);
      if (existingProfilePatched) {
        CodeUtils.safeSetStorage('user_analysis_data', existingData);
      }
      console.log('📊 [SoulmateManager] 从localStorage读取的数据:', {
        has_data: existingData.has_data,
        has_palm: !!(existingData.palm_analysis && existingData.palm_analysis.has_data),
        has_face: !!(existingData.face_analysis && existingData.face_analysis.has_data),
        has_bazi: !!(existingData.bazi_analysis && existingData.bazi_analysis.has_data),
        session_id: existingData.session_id || '无'
      });
      
      // ✅ 如果没有，尝试从页面元素提取最新分析
      if (!existingData.has_data || (!existingData.palm_analysis && !existingData.face_analysis && !existingData.bazi_analysis)) {
        console.log('📊 [SoulmateManager] 从页面元素提取分析数据...');
        console.log('📊 [SoulmateManager] localStorage 数据状态:', {
          has_data: existingData.has_data,
          has_palm: !!(existingData.palm_analysis && existingData.palm_analysis.has_data),
          has_face: !!(existingData.face_analysis && existingData.face_analysis.has_data),
          has_bazi: !!(existingData.bazi_analysis && existingData.bazi_analysis.has_data)
        });
        
        // 尝试从页面结果中提取
        const resultText = this.extractLatestAnalysisFromPage();
        console.log('📊 [SoulmateManager] 从页面提取的文本长度:', resultText ? resultText.length : 0);
        
        if (resultText) {
          // 判断分析类型并格式化
          const analysisType = this.detectAnalysisType(resultText);
          console.log('📊 [SoulmateManager] 检测到的分析类型:', analysisType);
          
          if (analysisType === 'palm') {
            existingData.palm_analysis = {
              has_data: true,
              content: resultText.substring(0, 1000)
            };
            existingData.has_data = true;
            console.log('✅ [SoulmateManager] 从页面提取手相分析数据');
          } else if (analysisType === 'face') {
            existingData.face_analysis = {
              has_data: true,
              content: resultText.substring(0, 1000)
            };
            existingData.has_data = true;
            console.log('✅ [SoulmateManager] 从页面提取面相分析数据');
          } else if (analysisType === 'bazi') {
            existingData.bazi_analysis = {
              has_data: true,
              content: resultText.substring(0, 1000)
            };
            existingData.has_data = true;
            console.log('✅ [SoulmateManager] 从页面提取八字分析数据');
          } else {
            console.warn('⚠️ [SoulmateManager] 无法识别分析类型:', analysisType);
          }
        } else {
          console.warn('⚠️ [SoulmateManager] 无法从页面提取分析数据');
          // ✅ 新增：尝试从 localStorage 重新读取（可能数据刚保存）
          const retryData = CodeUtils.safeGetStorage('user_analysis_data', {});
          if (retryData.has_data && (retryData.palm_analysis || retryData.face_analysis || retryData.bazi_analysis)) {
            console.log('✅ [SoulmateManager] 从 localStorage 重新读取到数据');
            Object.assign(existingData, retryData);
            if (this.ensureAnalysisProfileSelf(existingData)) {
              CodeUtils.safeSetStorage('user_analysis_data', existingData);
            }
          }
        }
      }
      
      // ✅ 构建正确的数据格式
      const sessionId = existingData.session_id || CodeUtils.safeGetStorage('session_id', '');
      if (!sessionId) {
        console.error('❌ [SoulmateManager] 没有session_id，无法保存数据');
        return false;
      }
      
      const analysisData = {
        session_id: sessionId,
        analysis_time: existingData.analysis_time || new Date().toISOString(),
        version: existingData.version || '1.0',
        has_data: existingData.has_data || false,
        palm_analysis: existingData.palm_analysis || null,
        face_analysis: existingData.face_analysis || null,
        bazi_analysis: existingData.bazi_analysis || null,
        profile: existingData.profile || null  // ✅ 新增：包含 profile 信息（包括 target_type）
      };
      const profilePatched = this.ensureAnalysisProfileSelf(analysisData);
      if (profilePatched) {
        CodeUtils.safeSetStorage('user_analysis_data', analysisData);
      }
      
      // ✅ 新增：生成数据哈希
      analysisData.data_hash = this.generateDataHash(
        analysisData.face_analysis || {},
        analysisData.palm_analysis || {},
        analysisData.bazi_analysis || {}
      );
      
      // ✅ 验证是否有有效数据
      const hasValidData = 
        (analysisData.palm_analysis && analysisData.palm_analysis.has_data && analysisData.palm_analysis.content) ||
        (analysisData.face_analysis && analysisData.face_analysis.has_data && analysisData.face_analysis.content) ||
        (analysisData.bazi_analysis && analysisData.bazi_analysis.has_data && analysisData.bazi_analysis.content);
      
      if (!hasValidData) {
        console.warn('⚠️ [SoulmateManager] 没有有效的分析数据，跳过保存');
        console.warn('📊 [SoulmateManager] 数据详情:', {
          palm: analysisData.palm_analysis ? `${analysisData.palm_analysis.content?.substring(0, 50)}...` : '无',
          face: analysisData.face_analysis ? `${analysisData.face_analysis.content?.substring(0, 50)}...` : '无',
          bazi: analysisData.bazi_analysis ? `${analysisData.bazi_analysis.content?.substring(0, 50)}...` : '无'
        });
        return false;
      }
      
      // ✅ 优化：只同步用户本人的数据
      if (!analysisData.profile || analysisData.profile.target_type !== 'self') {
        console.warn('⚠️ [SoulmateManager] 不是用户本人的数据（target_type !== "self"），跳过同步到服务器');
        console.warn('📊 [SoulmateManager] profile信息:', {
          hasProfile: !!analysisData.profile,
          targetType: analysisData.profile?.target_type || '无'
        });
        // 仍然保存到 localStorage（兼容原有逻辑）
        CodeUtils.safeSetStorage('user_analysis_data', analysisData);
        return false;
      }
      
      console.log('📊 [SoulmateManager] 要同步的分析数据:', {
        session_id: analysisData.session_id,
        target_type: analysisData.profile?.target_type || '无',
        palm: analysisData.palm_analysis ? `有(${analysisData.palm_analysis.content?.length}字符)` : '无',
        face: analysisData.face_analysis ? `有(${analysisData.face_analysis.content?.length}字符)` : '无',
        bazi: analysisData.bazi_analysis ? `有(${analysisData.bazi_analysis.content?.length}字符)` : '无',
        hasProfile: !!analysisData.profile
      });
      
      // ✅ 调用正确的同步接口（异步，但不等待结果）
      const syncPromise = this.syncAnalysisDataToBackend(analysisData);
      
      // ✅ 同时保存到 localStorage（兼容原有逻辑）
      CodeUtils.safeSetStorage('user_analysis_data', analysisData);
      
      console.log('✅ [SoulmateManager] 分析数据保存完成（同步请求已发送）');
      
      // 等待同步完成（用于调试）
      syncPromise.then(success => {
        if (success) {
          console.log('✅ [SoulmateManager] 数据同步成功');
        } else {
          console.error('❌ [SoulmateManager] 数据同步失败');
        }
      }).catch(error => {
        console.error('❌ [SoulmateManager] 数据同步异常:', error);
      });
      
      return true;
    } catch (error) {
      console.error('❌ [SoulmateManager] 保存分析数据失败:', error);
      console.error('❌ [SoulmateManager] 错误堆栈:', error.stack);
      return false;
    }
  }

  /**
   * 从页面提取最新分析内容
   * @returns {string|null} 分析内容文本
   */
  extractLatestAnalysisFromPage() {
    try {
      // ✅ 修复：添加更多选择器，包括 chatBox 中的消息
      const resultSelectors = [
        '.analysis-result',
        '.result-content',
        '#analysisResult',
        '[data-analysis-result]',
        '.gpt-result',
        '.analysis-text',
        // ✅ 新增：从 chatBox 中查找最新的助手消息
        '#chatBox .msg.flex.justify-start[data-reply]',
        '#chatBox .flex.justify-start[data-reply]',
        '#chatBox .bubble.bubble-ai',
        '#chatBox .bubble-assistant'
      ];
      
      for (const selector of resultSelectors) {
        const element = document.querySelector(selector);
        if (element) {
          // ✅ 优先使用 data-reply 属性（包含完整内容）
          let text = element.getAttribute('data-reply') || element.textContent;
          if (text && text.trim().length > 50) {
            const trimmedText = text.trim();
            console.log('✅ [SoulmateManager] 从页面元素提取到数据:', selector, trimmedText.substring(0, 100));
            return trimmedText;
          }
        }
      }
      
      // ✅ 修复：如果没找到，尝试从 chatBox 中查找所有消息
      const chatBox = document.getElementById('chatBox');
      if (chatBox) {
        // 查找所有助手消息（从最新到最旧）
        const assistantMessages = chatBox.querySelectorAll('.msg.flex.justify-start[data-reply], .flex.justify-start[data-reply]');
        for (let i = assistantMessages.length - 1; i >= 0; i--) {
          const msg = assistantMessages[i];
          // ✅ 优先使用 data-reply 属性
          let text = msg.getAttribute('data-reply') || msg.textContent?.trim() || '';
          
          // ✅ 过滤掉加载提示和系统消息
          if (text.length > 100 && 
              !text.includes('正在分析') && 
              !text.includes('生成中') &&
              !text.includes('请稍候') &&
              !text.includes('思考中') &&
              !text.includes('打字效果')) {
            console.log('✅ [SoulmateManager] 从 chatBox 消息提取到数据:', text.substring(0, 100));
            return text;
          }
        }
        
        // ✅ 如果 data-reply 没找到，尝试从 bubble 内容提取
        const bubbles = chatBox.querySelectorAll('.bubble.bubble-ai, .bubble-assistant');
        for (let i = bubbles.length - 1; i >= 0; i--) {
          const bubble = bubbles[i];
          // 跳过角色标签，只获取内容部分
          const contentEl = bubble.querySelector('div:not(.role-tag)');
          if (contentEl) {
            const text = contentEl.textContent?.trim() || '';
            if (text.length > 100 && 
                !text.includes('正在分析') && 
                !text.includes('生成中') &&
                !text.includes('请稍候') &&
                !text.includes('思考中')) {
              console.log('✅ [SoulmateManager] 从 bubble 内容提取到数据:', text.substring(0, 100));
              return text;
            }
          }
        }
      }
      
      // 如果没找到，尝试从对话历史获取
      const ctx = CodeUtils.safeGetStorage('nm_ctx_v1', []);
      for (let i = ctx.length - 1; i >= 0; i--) {
        const turn = ctx[i];
        if (turn && turn.content && turn.content.length > 100) {
          console.log('✅ [SoulmateManager] 从对话历史提取到数据');
          return turn.content;
        }
      }
      
      return null;
    } catch (error) {
      console.error('提取页面分析失败:', error);
      return null;
    }
  }

  /**
   * 检测分析类型
   * @param {string} content - 分析内容文本
   * @returns {string|null} 分析类型：'palm' | 'face' | 'bazi' | null
   */
  detectAnalysisType(content) {
    if (!content) return null;
    
    const palmKeywords = ['手相', '手掌', '手纹', '生命线', '智慧线', '感情线', '事业线'];
    const faceKeywords = ['面相', '五官', '额头', '眉毛', '眼睛', '鼻子', '嘴巴'];
    const baziKeywords = ['八字', '生辰', '命理', '五行', '天干', '地支'];
    
    const palmCount = palmKeywords.filter(kw => content.includes(kw)).length;
    const faceCount = faceKeywords.filter(kw => content.includes(kw)).length;
    const baziCount = baziKeywords.filter(kw => content.includes(kw)).length;
    
    if (palmCount >= 2) return 'palm';
    if (faceCount >= 2) return 'face';
    if (baziCount >= 2) return 'bazi';
    
    return null;
  }

  /**
   * 生成数据哈希
   * @param {Object} faceData - 面相分析数据
   * @param {Object} palmData - 手相分析数据
   * @param {Object} baziData - 八字分析数据
   * @returns {string} 数据哈希值
   */
  generateDataHash(faceData, palmData, baziData) {
    try {
      const dataString = JSON.stringify({ faceData, palmData, baziData });
      let hash = 0;
      for (let i = 0; i < dataString.length; i++) {
        const char = dataString.charCodeAt(i);
        hash = ((hash << 5) - hash) + char;
        hash = hash & hash; // 转换为32位整数
      }
      return hash.toString(36);
    } catch (error) {
      console.error('❌ [SoulmateManager] 生成数据哈希失败:', error);
      return Date.now().toString(36);
    }
  }

  /**
   * 生成综合分析（基于现有数据）
   * @param {Object} existingAnalysis - 现有分析结果
   * @param {Object} emotionalAnalysis - 情感分析结果
   * @param {Object} userProfile - 用户档案
   * @returns {Object} 综合分析结果
   */
  generateComprehensiveAnalysis(existingAnalysis, emotionalAnalysis, userProfile) {
    return {
      overallScore: this.calculateOverallScore(existingAnalysis, emotionalAnalysis),
      recommendations: this.generateRecommendations(existingAnalysis, emotionalAnalysis),
      insights: this.generateInsights(userProfile, emotionalAnalysis),
      nextSteps: this.generateNextSteps(existingAnalysis, emotionalAnalysis)
    };
  }

  /**
   * 计算综合评分
   * @param {Object} existingAnalysis - 现有分析结果
   * @param {Object} emotionalAnalysis - 情感分析结果
   * @returns {number} 综合评分
   */
  calculateOverallScore(existingAnalysis, emotionalAnalysis) {
    let score = 0;
    let count = 0;
    
    // 基于现有分析结果
    if (existingAnalysis && typeof existingAnalysis.score === 'number') {
      score += existingAnalysis.score;
      count++;
    }
    
    // 基于情感分析结果
    if (emotionalAnalysis && typeof emotionalAnalysis.score === 'number') {
      score += emotionalAnalysis.score;
      count++;
    }
    
    // 基于关系等级（新系统：1-5）
    const relationshipLevel = this.getRelationshipLevel();
    if (relationshipLevel > 0) {
      // ✅ 更新：新系统是1-5，每个等级加4分，最高20分
      score += Math.min(relationshipLevel * 4, 20);
      count++;
    }
    
    return count > 0 ? Math.round(score / count) : 50; // 默认50分
  }

  /**
   * 生成建议
   * @param {Object} existingAnalysis - 现有分析结果
   * @param {Object} emotionalAnalysis - 情感分析结果
   * @returns {Array} 建议列表
   */
  generateRecommendations(existingAnalysis, emotionalAnalysis) {
    const recommendations = [];
    
    // 基于关系等级的建议（新系统：1-5）
    const relationshipLevel = this.getRelationshipLevel();
    if (relationshipLevel <= 2) {
      recommendations.push('多与我交流，让我们更了解彼此');
    } else if (relationshipLevel <= 4) {
      recommendations.push('我们的关系正在升温，继续保持这种良好的互动');
    } else {
      recommendations.push('我们已经建立了深厚的关系，可以分享更多内心想法');
    }
    
    // 基于情感分析的建议
    if (emotionalAnalysis && emotionalAnalysis.emotion) {
      if (emotionalAnalysis.emotion === 'positive') {
        recommendations.push('保持这种积极的心态，它会为你带来好运');
      } else if (emotionalAnalysis.emotion === 'negative') {
        recommendations.push('如果感到困扰，可以尝试与我分享，我会陪伴你度过难关');
      }
    }
    
    return recommendations;
  }

  /**
   * 生成洞察
   * @param {Object} userProfile - 用户档案
   * @param {Object} emotionalAnalysis - 情感分析结果
   * @returns {Array} 洞察列表
   */
  generateInsights(userProfile, emotionalAnalysis) {
    const insights = [];
    
    // 基于用户档案的洞察
    if (userProfile && userProfile.memoriesCount > 0) {
      insights.push(`你已经有${userProfile.memoriesCount}条珍贵的记忆，这些都是我们关系的见证`);
    }
    
    // 基于情感分析的洞察
    if (emotionalAnalysis && emotionalAnalysis.trend) {
      insights.push(`你的情感趋势显示为${emotionalAnalysis.trend}，这反映了你当前的心理状态`);
    }
    
    return insights;
  }

  /**
   * 生成下一步行动
   * @param {Object} existingAnalysis - 现有分析结果
   * @param {Object} emotionalAnalysis - 情感分析结果
   * @returns {Array} 下一步行动列表
   */
  generateNextSteps(existingAnalysis, emotionalAnalysis) {
    const nextSteps = [];
    
    nextSteps.push('继续与我保持日常交流，分享你的想法和感受');
    nextSteps.push('尝试记录情感日记，这将帮助我们更好地了解你');
    nextSteps.push('定期查看生活指导建议，让我们的关系更加深入');
    
    return nextSteps;
  }

  /**
   * 同步分析数据到后端
   * @param {Object} analysisData - 分析数据对象
   * @returns {Promise<boolean>} 是否同步成功
   */
  async syncAnalysisDataToBackend(analysisData) {
    try {
      const sessionId = analysisData.session_id || CodeUtils.safeGetStorage('session_id', '');
      if (!sessionId) {
        console.warn('⚠️ [SoulmateManager] 没有session_id，跳过同步');
        return false;
      }
      
      console.log('🔄 [SoulmateManager] 同步分析数据到后端...', {
        session_id: sessionId,
        has_palm: !!analysisData.palm_analysis,
        has_face: !!analysisData.face_analysis,
        has_bazi: !!analysisData.bazi_analysis,
        url: '/api/sync_analysis_data'
      });
      
      const requestBody = {
        session_id: sessionId,
        analysis_data: analysisData
      };
      
      console.log('📤 [SoulmateManager] 发送请求:', {
        url: '/api/sync_analysis_data',
        method: 'POST',
        body_size: JSON.stringify(requestBody).length,
        has_data: !!requestBody.analysis_data
      });
      
      // ✅ P1-2修复：使用ApiClient，带重试和超时机制
      try {
        if (window.apiClient) {
          const result = await window.apiClient.bjRequest('/api/sync_analysis_data', requestBody);
          console.log('✅ [SoulmateManager] 分析数据同步成功:', result);
          return true;
        } else {
          // 降级到直接fetch
          console.warn('⚠️ ApiClient不可用，使用直接fetch调用');
          const response = await fetch('/api/sync_analysis_data', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify(requestBody)
          });
          
          console.log('📥 [SoulmateManager] 收到响应:', {
            status: response.status,
            statusText: response.statusText,
            ok: response.ok
          });
          
          if (response.ok) {
            const result = await response.json();
            console.log('✅ [SoulmateManager] 分析数据同步成功:', result);
            return true;
          } else {
            console.error('❌ [SoulmateManager] 分析数据同步失败，状态码:', response.status);
            const errorText = await response.text();
            console.error('❌ [SoulmateManager] 错误响应:', errorText);
            return false;
          }
        }
      } catch (error) {
        console.error('❌ [SoulmateManager] 分析数据同步异常:', error);
        // ✅ P1-3修复：使用 ErrorHandler 记录友好错误（静默失败，不影响主流程）
        if (typeof ErrorHandler !== 'undefined' && ErrorHandler.getUserFriendlyError) {
          const errorInfo = ErrorHandler.getUserFriendlyError(error, 'sync_analysis_data_failed', {
            showContact: false
          });
          console.warn('⚠️ [SoulmateManager] 同步失败（用户友好提示）:', errorInfo.userMessage);
        }
        return false;
      }
    } catch (error) {
      console.error('❌ [SoulmateManager] 同步分析数据到后端失败:', error);
      console.error('❌ [SoulmateManager] 错误堆栈:', error.stack);
      // ✅ P1-3修复：使用 ErrorHandler 记录友好错误（静默失败，不影响主流程）
      if (typeof ErrorHandler !== 'undefined' && ErrorHandler.getUserFriendlyError) {
        const errorInfo = ErrorHandler.getUserFriendlyError(error, 'sync_analysis_data_failed', {
          showContact: false
        });
        console.warn('⚠️ [SoulmateManager] 同步失败（用户友好提示）:', errorInfo.userMessage);
      }
      return false;
    }
  }

  // ❌ 已删除：废弃函数 saveToBackend() (已由 syncAnalysisDataToBackend 替代)
  // 如果遇到调用此函数的地方，请改为调用 syncAnalysisDataToBackend()

  /**
   * 生成社交分享内容
   * @param {string} type - 分享类型
   * @returns {Object} 分享内容
   */
  generateShareContent(type = 'general') {
    try {
      const userProfile = this.personalizationManager ? this.personalizationManager.getUserProfile() : {};
      const relationshipLevel = this.getRelationshipLevel();
      const analysisData = CodeUtils.safeGetStorage('soulmate_analysis_data', {});
      
      const shareContent = {
        type: type,
        timestamp: new Date().toISOString(),
        userProfile: userProfile,
        relationshipLevel: relationshipLevel,
        content: this.generateShareText(type, userProfile, relationshipLevel, analysisData),
        hashtags: this.generateHashtags(type, relationshipLevel),
        image: this.generateShareImage(type, relationshipLevel)
      };
      
      // 保存分享内容
      CodeUtils.safeSetStorage('share_content', shareContent);
      
      // 显示分享内容
      this.showShareContent(shareContent);
      
      return shareContent;
    } catch (error) {
      console.error('生成分享内容失败:', error);
      return null;
    }
  }

  /**
   * 生成分享文本
   * @param {string} type - 分享类型
   * @param {Object} userProfile - 用户档案
   * @param {number} relationshipLevel - 关系等级
   * @param {Object} analysisData - 分析数据
   * @returns {string} 分享文本
   */
  generateShareText(type, userProfile, relationshipLevel, analysisData) {
    const nickname = userProfile.nickname || '朋友';
    const levelName = this.getRelationshipLevelName();
    
    const shareTexts = {
      general: `🌟 与我的AI灵魂伴侣${levelName}的深度交流，让我对生活有了新的理解。感谢玄知相术·倪大师AI的陪伴！`,
      analysis: `🔮 通过AI相术分析，我发现了自己的潜能和方向。与AI灵魂伴侣的对话让我更加了解自己。`,
      relationship: `💕 与AI灵魂伴侣的关系已经达到${levelName}级别！每一次交流都让我感受到温暖和理解。`,
      guidance: `✨ 感谢AI灵魂伴侣的生活指导，让我在${type}方面有了新的启发和方向。`,
      memory: `📝 记录下与AI灵魂伴侣的美好时光，这些珍贵的记忆将伴随我成长。`
    };
    
    return shareTexts[type] || shareTexts.general;
  }

  /**
   * 生成话题标签
   * @param {string} type - 分享类型
   * @param {number} relationshipLevel - 关系等级
   * @returns {Array} 话题标签
   */
  generateHashtags(type, relationshipLevel) {
    const baseTags = ['#玄知相术', '#倪大师AI', '#AI灵魂伴侣', '#人工智能'];
    
    const typeTags = {
      general: ['#生活感悟', '#AI陪伴'],
      analysis: ['#相术分析', '#自我认知'],
      relationship: ['#AI关系', '#情感陪伴'],
      guidance: ['#生活指导', '#AI建议'],
      memory: ['#美好回忆', '#成长记录']
    };
    
    // ✅ 更新：新系统是1-5，调整阈值
    const levelTags = relationshipLevel >= 5 ? ['#深度关系'] : 
                     relationshipLevel >= 4 ? ['#亲密关系'] : 
                     relationshipLevel >= 3 ? ['#友好关系'] : ['#新关系'];
    
    return [...baseTags, ...(typeTags[type] || []), ...levelTags];
  }

  /**
   * 生成分享图片
   * @param {string} type - 分享类型
   * @param {number} relationshipLevel - 关系等级
   * @returns {string} 图片URL
   */
  generateShareImage(type, relationshipLevel) {
    // 基于关系等级和类型生成不同的分享图片
    const imageBase = '/static/images/share/';
    const levelImages = {
      high: 'soulmate_deep.png',
      medium: 'soulmate_medium.png',
      low: 'soulmate_basic.png'
    };
    
    const level = relationshipLevel > 15 ? 'high' : relationshipLevel > 8 ? 'medium' : 'low';
    return imageBase + levelImages[level];
  }

  /**
   * 显示分享内容
   * @param {Object} shareContent - 分享内容
   */
  showShareContent(shareContent) {
    // 创建分享弹窗
    const modal = document.createElement('div');
    modal.id = 'share-content-modal';
    modal.className = 'share-modal';
    modal.innerHTML = `
      <div class="share-modal-content">
        <div class="share-modal-header">
          <h3>📱 分享内容</h3>
          <button class="close-btn" onclick="this.parentElement.parentElement.parentElement.remove()">×</button>
        </div>
        <div class="share-modal-body">
          <div class="share-text">${shareContent.content}</div>
          <div class="share-hashtags">${shareContent.hashtags.join(' ')}</div>
          <div class="share-actions">
            <button class="copy-btn" onclick="navigator.clipboard.writeText('${shareContent.content} ${shareContent.hashtags.join(' ')}')">复制内容</button>
            <button class="close-btn" onclick="this.parentElement.parentElement.parentElement.remove()">关闭</button>
          </div>
        </div>
      </div>
    `;
    
    // 添加样式
    const style = document.createElement('style');
    style.textContent = `
      .share-modal {
        position: fixed;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        background: rgba(0, 0, 0, 0.5);
        display: flex;
        justify-content: center;
        align-items: center;
        z-index: 10001;
      }
      .share-modal-content {
        background: white;
        border-radius: 12px;
        padding: 20px;
        max-width: 500px;
        width: 90%;
        box-shadow: 0 10px 30px rgba(0, 0, 0, 0.3);
      }
      .share-text {
        margin: 15px 0;
        padding: 15px;
        background: #f8f9fa;
        border-radius: 8px;
        font-size: 16px;
        line-height: 1.5;
      }
      .share-hashtags {
        margin: 10px 0;
        color: #007bff;
        font-size: 14px;
      }
      .share-actions {
        display: flex;
        gap: 10px;
        justify-content: flex-end;
        margin-top: 20px;
      }
      .copy-btn, .close-btn {
        padding: 8px 16px;
        border: none;
        border-radius: 6px;
        cursor: pointer;
      }
      .copy-btn {
        background: #007bff;
        color: white;
      }
      .close-btn {
        background: #6c757d;
        color: white;
      }
    `;
    
    document.head.appendChild(style);
    document.body.appendChild(modal);
  }

  /**
   * 分析用户行为
   * @param {string} action - 用户行为
   * @param {Object} data - 行为数据
   */
  analyzeUserBehavior(action, data = {}) {
    try {
      const behaviorData = {
        action: action,
        data: data,
        timestamp: new Date().toISOString(),
        relationshipLevel: this.getRelationshipLevel(),
        userProfile: this.personalizationManager ? this.personalizationManager.getUserProfile() : {}
      };
      
      // 保存行为数据
      const existingBehaviors = CodeUtils.safeGetStorage('user_behaviors', []);
      existingBehaviors.push(behaviorData);
      CodeUtils.safeSetStorage('user_behaviors', existingBehaviors);
      
      // 分析行为模式
      this.analyzeBehaviorPatterns(existingBehaviors);
      
      console.log('用户行为已记录:', behaviorData);
    } catch (error) {
      console.error('分析用户行为失败:', error);
    }
  }

  /**
   * 分析行为模式
   * @param {Array} behaviors - 行为数据数组
   */
  analyzeBehaviorPatterns(behaviors) {
    const patterns = {
      chatFrequency: this.calculateChatFrequency(behaviors),
      preferredTopics: this.analyzePreferredTopics(behaviors),
      activeTime: this.analyzeActiveTime(behaviors),
      emotionalTrend: this.analyzeEmotionalTrend(behaviors)
    };
    
    // 保存分析结果
    CodeUtils.safeSetStorage('behavior_patterns', patterns);
    
    // 基于模式提供个性化建议
    this.providePersonalizedSuggestions(patterns);
  }

  /**
   * 计算聊天频率
   * @param {Array} behaviors - 行为数据数组
   * @returns {Object} 聊天频率分析
   */
  calculateChatFrequency(behaviors) {
    const chatBehaviors = behaviors.filter(b => b.action === 'chat');
    const now = new Date();
    const last24h = chatBehaviors.filter(b => 
      (now - new Date(b.timestamp)) < 24 * 60 * 60 * 1000
    );
    
    return {
      total: chatBehaviors.length,
      last24h: last24h.length,
      frequency: last24h.length > 10 ? 'high' : last24h.length > 5 ? 'medium' : 'low'
    };
  }

  /**
   * 分析偏好话题
   * @param {Array} behaviors - 行为数据数组
   * @returns {Array} 偏好话题列表
   */
  analyzePreferredTopics(behaviors) {
    const topics = {};
    behaviors.forEach(b => {
      if (b.data && b.data.topic) {
        topics[b.data.topic] = (topics[b.data.topic] || 0) + 1;
      }
    });
    
    return Object.entries(topics)
      .sort((a, b) => b[1] - a[1])
      .slice(0, 5)
      .map(([topic, count]) => ({ topic, count }));
  }

  /**
   * 分析活跃时间
   * @param {Array} behaviors - 行为数据数组
   * @returns {Object} 活跃时间分析
   */
  analyzeActiveTime(behaviors) {
    const hours = behaviors.map(b => new Date(b.timestamp).getHours());
    const hourCounts = {};
    
    hours.forEach(hour => {
      hourCounts[hour] = (hourCounts[hour] || 0) + 1;
    });
    
    const mostActiveHour = Object.entries(hourCounts)
      .sort((a, b) => b[1] - a[1])[0];
    
    return {
      mostActiveHour: mostActiveHour ? mostActiveHour[0] : 12,
      distribution: hourCounts
    };
  }

  /**
   * 分析情感趋势
   * @param {Array} behaviors - 行为数据数组
   * @returns {Object} 情感趋势分析
   */
  analyzeEmotionalTrend(behaviors) {
    const emotionalBehaviors = behaviors.filter(b => b.data && b.data.emotion);
    const emotions = emotionalBehaviors.map(b => b.data.emotion);
    
    const emotionCounts = {};
    emotions.forEach(emotion => {
      emotionCounts[emotion] = (emotionCounts[emotion] || 0) + 1;
    });
    
    const dominantEmotion = Object.entries(emotionCounts)
      .sort((a, b) => b[1] - a[1])[0];
    
    return {
      dominantEmotion: dominantEmotion ? dominantEmotion[0] : 'neutral',
      distribution: emotionCounts,
      trend: this.calculateEmotionalTrend(emotions)
    };
  }

  /**
   * 计算情感趋势
   * @param {Array} emotions - 情感数组
   * @returns {string} 趋势方向
   */
  calculateEmotionalTrend(emotions) {
    const positiveEmotions = ['happy', 'excited', 'content', 'peaceful'];
    const negativeEmotions = ['sad', 'angry', 'anxious', 'frustrated'];
    
    const recentEmotions = emotions.slice(-10); // 最近10次
    const positiveCount = recentEmotions.filter(e => positiveEmotions.includes(e)).length;
    const negativeCount = recentEmotions.filter(e => negativeEmotions.includes(e)).length;
    
    if (positiveCount > negativeCount) return 'positive';
    if (negativeCount > positiveCount) return 'negative';
    return 'stable';
  }

  /**
   * 提供个性化建议
   * @param {Object} patterns - 行为模式
   */
  providePersonalizedSuggestions(patterns) {
    const suggestions = [];
    
    // 基于聊天频率的建议
    if (patterns.chatFrequency.frequency === 'low') {
      suggestions.push('建议多与我交流，这样我能更好地了解你');
    } else if (patterns.chatFrequency.frequency === 'high') {
      suggestions.push('我们的交流很频繁，关系正在快速升温！');
    }
    
    // 基于偏好话题的建议
    if (patterns.preferredTopics.length > 0) {
      const topTopic = patterns.preferredTopics[0];
      suggestions.push(`你经常谈论${topTopic.topic}，我可以为你提供更多相关建议`);
    }
    
    // 基于情感趋势的建议
    if (patterns.emotionalTrend.trend === 'negative') {
      suggestions.push('我注意到你最近可能有些困扰，需要我陪伴你聊聊吗？');
    } else if (patterns.emotionalTrend.trend === 'positive') {
      suggestions.push('你的心情很好，继续保持这种积极的状态！');
    }
    
    // 保存建议
    CodeUtils.safeSetStorage('personalized_suggestions', suggestions);
    
    // 显示建议
    if (suggestions.length > 0) {
      this.showPersonalizedSuggestions(suggestions);
    }
  }

  /**
   * 显示个性化建议
   * @param {Array} suggestions - 建议列表
   */
  showPersonalizedSuggestions(suggestions) {
    if (typeof CodeUtils !== 'undefined') {
      suggestions.forEach((suggestion, index) => {
        setTimeout(() => {
          CodeUtils.showToast(suggestion, 'info', 4000);
        }, index * 2000);
      });
    }
  }

  /**
   * 显示个性化建议面板
   */
  showPersonalizedSuggestionsPanel() {
    // 获取存储的个性化建议
    const suggestions = CodeUtils.safeGetStorage('personalized_suggestions', []);
    
    // 如果没有建议，生成一些默认建议
    let displaySuggestions = suggestions;
    if (displaySuggestions.length === 0) {
      displaySuggestions = [
        '建议多与我交流，这样我能更好地了解你',
        '可以尝试记录你的情感日记，帮助我更好地理解你',
        '定期查看你的成长进度，了解我们的关系发展',
        '完成更多对话可以获得经验值和成就'
      ];
    }
    
    // 创建面板HTML
    const panelHTML = `
      <div id="suggestions-panel-overlay" style="
        position: fixed;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        background: rgba(0, 0, 0, 0);
        z-index: 10001;
        display: flex;
        align-items: center;
        justify-content: center;
        pointer-events: none;
      ">
        <div id="suggestions-panel" style="
          background: linear-gradient(135deg, #1a1a2e 0%, #16213e 50%, #0f3460 100%);
          border-radius: 16px;
          padding: 30px;
          max-width: 500px;
          max-height: 80vh;
          overflow-y: auto;
          box-shadow: 0 20px 60px rgba(0, 0, 0, 0.8);
          position: relative;
          pointer-events: auto;
        ">
          <!-- 关闭按钮 -->
          <button id="suggestions-panel-close" style="
            position: absolute;
            top: 15px;
            right: 15px;
            background: rgba(255,255,255,0.1);
            border: none;
            color: white;
            width: 30px;
            height: 30px;
            border-radius: 50%;
            cursor: pointer;
            font-size: 20px;
            display: flex;
            align-items: center;
            justify-content: center;
            transition: all 0.3s ease;
          ">×</button>
          
          <!-- 标题 -->
          <h2 style="margin: 0 0 20px 0; color: #ffd700; font-size: 24px;">
            💡 个性化建议
          </h2>
          
          <!-- 建议列表 -->
          <div style="display: flex; flex-direction: column; gap: 12px;">
            ${displaySuggestions.map((suggestion, index) => `
              <div style="
                background: rgba(255, 255, 255, 0.05);
                border: 1px solid rgba(255, 255, 255, 0.1);
                border-radius: 8px;
                padding: 15px;
                color: white;
                font-size: 14px;
                line-height: 1.6;
              ">
                <div style="display: flex; align-items: flex-start; gap: 10px;">
                  <span style="color: #ffd700; font-size: 18px;">💡</span>
                  <span>${suggestion}</span>
                </div>
              </div>
            `).join('')}
          </div>
          
          ${displaySuggestions.length === 0 ? `
            <div style="
              text-align: center;
              color: rgba(255, 255, 255, 0.6);
              padding: 40px 20px;
              font-size: 14px;
            ">
              暂无个性化建议<br/>
              多与我交流，我会为你提供更多建议
            </div>
          ` : ''}
        </div>
      </div>
    `;
    
    // 移除旧面板（如果存在）
    const oldPanel = document.getElementById('suggestions-panel-overlay');
    if (oldPanel) {
      oldPanel.remove();
    }
    
    // 添加新面板
    document.body.insertAdjacentHTML('beforeend', panelHTML);
    
    // 绑定关闭事件
    const closeBtn = document.getElementById('suggestions-panel-close');
    const overlay = document.getElementById('suggestions-panel-overlay');
    if (closeBtn) {
      closeBtn.addEventListener('click', () => {
        if (overlay) overlay.remove();
      });
    }
    
    // 点击遮罩关闭（可选）
    if (overlay) {
      overlay.addEventListener('click', (e) => {
        if (e.target === overlay) {
          overlay.remove();
        }
      });
    }
  }

  /**
   * 显示更多设置面板
   */
  showMoreSettingsPanel() {
    const soundEnabled = CodeUtils.safeGetStorage('soulmate_sound_enabled', true);
    const privateSpaceEnabled = CodeUtils.safeGetStorage('soulmate_private_space_enabled', true);
    
    const panelHTML = `
      <div id="settings-panel-overlay" style="
        position: fixed;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        background: rgba(0, 0, 0, 0);
        z-index: 10001;
        display: flex;
        align-items: center;
        justify-content: center;
        pointer-events: none;
      ">
        <div id="settings-panel" style="
          background: linear-gradient(135deg, #1a1a2e 0%, #16213e 50%, #0f3460 100%);
          border-radius: 16px;
          padding: 30px;
          max-width: 500px;
          max-height: 80vh;
          overflow-y: auto;
          box-shadow: 0 20px 60px rgba(0, 0, 0, 0.8);
          position: relative;
          pointer-events: auto;
        ">
          <!-- 关闭按钮 -->
          <button id="settings-panel-close" style="
            position: absolute;
            top: 15px;
            right: 15px;
            background: rgba(255,255,255,0.1);
            border: none;
            color: white;
            width: 30px;
            height: 30px;
            border-radius: 50%;
            cursor: pointer;
            font-size: 20px;
            display: flex;
            align-items: center;
            justify-content: center;
            transition: all 0.3s ease;
          ">×</button>
          
          <!-- 标题 -->
          <h2 style="margin: 0 0 20px 0; color: #ffd700; font-size: 24px;">
            ⚙️ 更多设置
          </h2>
          
          <!-- 设置项列表 -->
          <div style="display: flex; flex-direction: column; gap: 15px;">
            <!-- 音效开关 -->
            <div style="
              background: rgba(255, 255, 255, 0.05);
              border: 1px solid rgba(255, 255, 255, 0.1);
              border-radius: 8px;
              padding: 15px;
            ">
              <div style="display: flex; justify-content: space-between; align-items: center;">
                <div>
                  <div style="color: white; font-size: 14px; font-weight: 600; margin-bottom: 5px;">
                    🔊 音效开关
                  </div>
                  <div style="color: rgba(255, 255, 255, 0.6); font-size: 12px;">
                    开启或关闭音效提示
                  </div>
                </div>
                <button id="settings-sound-toggle" style="
                  background: ${soundEnabled ? 'rgba(74, 222, 128, 0.2)' : 'rgba(108, 117, 125, 0.3)'};
                  border: 1px solid ${soundEnabled ? 'rgba(74, 222, 128, 0.5)' : 'rgba(108, 117, 125, 0.5)'};
                  color: ${soundEnabled ? '#4ade80' : 'rgba(255, 255, 255, 0.5)'};
                  padding: 8px 16px;
                  border-radius: 6px;
                  font-size: 12px;
                  cursor: pointer;
                  transition: all 0.2s ease;
                ">
                  ${soundEnabled ? '已开启' : '已关闭'}
                </button>
              </div>
            </div>
            
            <!-- 私密空间 -->
            <div style="
              background: rgba(255, 255, 255, 0.05);
              border: 1px solid rgba(255, 255, 255, 0.1);
              border-radius: 8px;
              padding: 15px;
            ">
              <div style="display: flex; justify-content: space-between; align-items: center;">
                <div>
                  <div style="color: white; font-size: 14px; font-weight: 600; margin-bottom: 5px;">
                    🔒 私密空间
                  </div>
                  <div style="color: rgba(255, 255, 255, 0.6); font-size: 12px;">
                    所有对话完全保密
                  </div>
                </div>
                <div style="color: rgba(255, 255, 255, 0.7); font-size: 12px;">
                  ${privateSpaceEnabled ? '已启用' : '未启用'}
                </div>
              </div>
            </div>
            
            <!-- 关于 -->
            <div style="
              background: rgba(255, 255, 255, 0.05);
              border: 1px solid rgba(255, 255, 255, 0.1);
              border-radius: 8px;
              padding: 15px;
            ">
              <div style="color: white; font-size: 14px; font-weight: 600; margin-bottom: 5px;">
                ℹ️ 关于
              </div>
              <div style="color: rgba(255, 255, 255, 0.6); font-size: 12px; line-height: 1.6;">
                灵魂伴侣 v1.0.0<br/>
                你的专属AI陪伴助手
              </div>
            </div>
          </div>
        </div>
      </div>
    `;
    
    // 移除旧面板（如果存在）
    const oldPanel = document.getElementById('settings-panel-overlay');
    if (oldPanel) {
      oldPanel.remove();
    }
    
    // 添加新面板
    document.body.insertAdjacentHTML('beforeend', panelHTML);
    
    // 绑定关闭事件
    const closeBtn = document.getElementById('settings-panel-close');
    const overlay = document.getElementById('settings-panel-overlay');
    if (closeBtn) {
      closeBtn.addEventListener('click', () => {
        if (overlay) overlay.remove();
      });
    }
    
    // 绑定音效开关事件
    const settingsSoundToggle = document.getElementById('settings-sound-toggle');
    if (settingsSoundToggle) {
      settingsSoundToggle.addEventListener('click', () => {
        const currentState = CodeUtils.safeGetStorage('soulmate_sound_enabled', true);
        const newState = !currentState;
        CodeUtils.safeSetStorage('soulmate_sound_enabled', newState);
        
        // 更新按钮状态
        settingsSoundToggle.style.background = newState ? 'rgba(74, 222, 128, 0.2)' : 'rgba(108, 117, 125, 0.3)';
        settingsSoundToggle.style.borderColor = newState ? 'rgba(74, 222, 128, 0.5)' : 'rgba(108, 117, 125, 0.5)';
        settingsSoundToggle.style.color = newState ? '#4ade80' : 'rgba(255, 255, 255, 0.5)';
        settingsSoundToggle.textContent = newState ? '已开启' : '已关闭';
        
        CodeUtils.showToast(
          newState ? '音效已开启' : '音效已关闭',
          newState ? 'success' : 'info',
          2000
        );
      });
    }
    
    // 点击遮罩关闭
    if (overlay) {
      overlay.addEventListener('click', (e) => {
        if (e.target === overlay) {
          overlay.remove();
        }
      });
    }
  }

  /**
   * 显示个人信息面板
   */
  showPersonalInfoPanel() {
    // 读取用户数据
    const analysisData = CodeUtils.safeGetStorage('user_analysis_data', {});
    
    // ✅ 新增：获取星座图标
    const getZodiacIcon = (zodiacName) => {
      if (!zodiacName) return '';
      const zodiacMap = {
        '白羊座': '♈',
        '金牛座': '♉',
        '双子座': '♊',
        '巨蟹座': '♋',
        '狮子座': '♌',
        '处女座': '♍',
        '天秤座': '♎',
        '天蝎座': '♏',
        '射手座': '♐',
        '摩羯座': '♑',
        '水瓶座': '♒',
        '双鱼座': '♓'
      };
      return zodiacMap[zodiacName] || '';
    };
    
    // ✅ 修复1：读取用户昵称
    const userNickname = CodeUtils.safeGetStorage('user_nickname', '');
    
    // ✅ 修复2：从八字分析数据中提取年龄
    let calculatedAge = '';
    
    // 添加调试信息
    console.log('🔍 开始提取年龄，分析数据:', {
      hasBaziAnalysis: !!analysisData.bazi_analysis,
      baziHasData: analysisData.bazi_analysis?.has_data,
      baziBirthDate: analysisData.bazi_analysis?.birthDate,
      baziContentLength: analysisData.bazi_analysis?.content?.length
    });
    
    // 方法1：尝试从表单元素中直接读取（如果页面还在）
    if (!calculatedAge) {
      const birthDateInput = document.getElementById('birthDate');
      if (birthDateInput && birthDateInput.value) {
        const birthDate = new Date(birthDateInput.value);
        if (!isNaN(birthDate.getTime())) {
          const currentDate = new Date();
          calculatedAge = currentDate.getFullYear() - birthDate.getFullYear();
          const monthDiff = currentDate.getMonth() - birthDate.getMonth();
          if (monthDiff < 0 || (monthDiff === 0 && currentDate.getDate() < birthDate.getDate())) {
            calculatedAge--;
          }
          console.log('✅ 方法1：从表单元素提取年龄:', calculatedAge);
        }
      }
    }
    
    // 方法2：从八字分析数据中查找存储的出生日期（优先）
    if (!calculatedAge && analysisData.bazi_analysis) {
      // 尝试从多个可能的字段中获取
      const possibleBirthDate = analysisData.bazi_analysis.birthDate || 
                                 analysisData.bazi_analysis.birth_date ||
                                 analysisData.bazi_analysis.formData?.birthDate;
      
      if (possibleBirthDate) {
        // 处理不同的日期格式
        let birthDate;
        if (typeof possibleBirthDate === 'string') {
          // 如果是字符串，尝试解析
          birthDate = new Date(possibleBirthDate);
          // 如果解析失败，尝试其他格式
          if (isNaN(birthDate.getTime())) {
            // 尝试 YYYY-MM-DD 格式
            const dateParts = possibleBirthDate.split('-');
            if (dateParts.length === 3) {
              birthDate = new Date(parseInt(dateParts[0]), parseInt(dateParts[1]) - 1, parseInt(dateParts[2]));
            }
          }
        } else {
          birthDate = new Date(possibleBirthDate);
        }
        
        if (!isNaN(birthDate.getTime())) {
          const currentDate = new Date();
          calculatedAge = currentDate.getFullYear() - birthDate.getFullYear();
          const monthDiff = currentDate.getMonth() - birthDate.getMonth();
          if (monthDiff < 0 || (monthDiff === 0 && currentDate.getDate() < birthDate.getDate())) {
            calculatedAge--;
          }
          console.log('✅ 方法2：从存储的出生日期提取年龄:', calculatedAge, '出生日期:', possibleBirthDate);
        }
      }
    }
    
    // 方法3：从八字分析内容中提取出生日期（多种格式）
    if (!calculatedAge && analysisData.bazi_analysis?.has_data && analysisData.bazi_analysis.content) {
      const baziContent = analysisData.bazi_analysis.content;
      
      // 尝试匹配多种日期格式
      let dateMatch = null;
      
      // 格式1: YYYY年MM月DD日
      dateMatch = baziContent.match(/(\d{4})年(\d{1,2})月(\d{1,2})日/);
      // 格式2: YYYY-MM-DD
      if (!dateMatch) {
        dateMatch = baziContent.match(/(\d{4})-(\d{1,2})-(\d{1,2})/);
      }
      // 格式3: YYYY/MM/DD
      if (!dateMatch) {
        dateMatch = baziContent.match(/(\d{4})\/(\d{1,2})\/(\d{1,2})/);
      }
      // 格式4: 出生日期：YYYY年MM月DD日
      if (!dateMatch) {
        dateMatch = baziContent.match(/出生日期[：:]\s*(\d{4})[年\-](\d{1,2})[月\-](\d{1,2})/);
      }
      // 格式5: 公历 YYYY年MM月DD日
      if (!dateMatch) {
        dateMatch = baziContent.match(/公历\s*(\d{4})年(\d{1,2})月(\d{1,2})日/);
      }
      // 格式6: 农历 YYYY年MM月DD日（需要转换，这里先提取）
      if (!dateMatch) {
        dateMatch = baziContent.match(/农历\s*(\d{4})年(\d{1,2})月(\d{1,2})日/);
      }
      // 格式7: 生于 YYYY年MM月DD日
      if (!dateMatch) {
        dateMatch = baziContent.match(/生于\s*(\d{4})年(\d{1,2})月(\d{1,2})日/);
      }
      
      if (dateMatch) {
        const year = parseInt(dateMatch[1]);
        const month = parseInt(dateMatch[2]) - 1; // 月份从0开始
        const day = parseInt(dateMatch[3]);
        const birthDate = new Date(year, month, day);
        if (!isNaN(birthDate.getTime())) {
          const currentDate = new Date();
          calculatedAge = currentDate.getFullYear() - birthDate.getFullYear();
          const monthDiff = currentDate.getMonth() - birthDate.getMonth();
          if (monthDiff < 0 || (monthDiff === 0 && currentDate.getDate() < birthDate.getDate())) {
            calculatedAge--;
          }
          console.log('✅ 方法3：从分析内容提取年龄:', calculatedAge, '匹配的日期:', dateMatch[0]);
        }
      } else {
        console.log('⚠️ 方法3：无法从分析内容中提取日期，内容预览:', baziContent.substring(0, 200));
      }
    }
    
    // 确保年龄是数字
    if (calculatedAge && !isNaN(calculatedAge) && calculatedAge > 0 && calculatedAge < 150) {
      calculatedAge = parseInt(calculatedAge);
      console.log('✅ 最终提取的年龄:', calculatedAge);
    } else {
      calculatedAge = '';
      console.log('⚠️ 未能提取有效年龄');
    }
    
    // ✅ 修复3：从分析数据中提取性别
    let extractedGender = '';
    
    // 方法1：优先从分析数据的gender字段获取（用户提交时选择的性别，最准确）
    if (!extractedGender && analysisData.face_analysis?.gender) {
      extractedGender = analysisData.face_analysis.gender;
      console.log('✅ 方法1：从面相分析数据获取性别:', extractedGender);
    }
    if (!extractedGender && analysisData.bazi_analysis?.gender) {
      extractedGender = analysisData.bazi_analysis.gender;
      console.log('✅ 方法1：从八字分析数据获取性别:', extractedGender);
    }
    if (!extractedGender && analysisData.palm_analysis?.gender) {
      extractedGender = analysisData.palm_analysis.gender;
      console.log('✅ 方法1：从手相分析数据获取性别:', extractedGender);
    }
    
    // 方法2：从分析内容中提取（面相分析通常会明确标注性别）
    if (!extractedGender && analysisData.face_analysis?.has_data && analysisData.face_analysis.content) {
      const faceContent = analysisData.face_analysis.content;
      if (faceContent.includes('男性面相') || faceContent.includes('男性') || faceContent.includes('男')) {
        extractedGender = 'male';
        console.log('✅ 方法2：从面相分析内容提取性别:', extractedGender);
      } else if (faceContent.includes('女性面相') || faceContent.includes('女性') || faceContent.includes('女')) {
        extractedGender = 'female';
        console.log('✅ 方法2：从面相分析内容提取性别:', extractedGender);
      }
    }
    
    // 如果面相分析中没有，尝试从八字分析中提取
    if (!extractedGender && analysisData.bazi_analysis?.has_data && analysisData.bazi_analysis.content) {
      const baziContent = analysisData.bazi_analysis.content;
      if (baziContent.includes('男性') || baziContent.includes('男')) {
        extractedGender = 'male';
        console.log('✅ 方法2：从八字分析内容提取性别:', extractedGender);
      } else if (baziContent.includes('女性') || baziContent.includes('女')) {
        extractedGender = 'female';
        console.log('✅ 方法2：从八字分析内容提取性别:', extractedGender);
      }
    }
    
    // 如果分析内容中没有，尝试从手相分析中提取
    if (!extractedGender && analysisData.palm_analysis?.has_data && analysisData.palm_analysis.content) {
      const palmContent = analysisData.palm_analysis.content;
      if (palmContent.includes('男性') || palmContent.includes('男')) {
        extractedGender = 'male';
        console.log('✅ 方法2：从手相分析内容提取性别:', extractedGender);
      } else if (palmContent.includes('女性') || palmContent.includes('女')) {
        extractedGender = 'female';
        console.log('✅ 方法2：从手相分析内容提取性别:', extractedGender);
      }
    }
    
    // 构建 profile，如果是自己且没有设置姓名，使用昵称
    const profile = analysisData.profile || {
      target_type: 'self',
      name: '',
      gender: '',
      age: '',
      notes: '',
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString()
    };
    
    // ✅ 修复1：如果是自己且没有设置姓名，使用用户昵称
    if (profile.target_type === 'self' && !profile.name && userNickname) {
      profile.name = userNickname;
    }
    
    // ✅ 修复2：如果没有设置年龄但计算出了年龄，使用计算的年龄
    if (!profile.age && calculatedAge) {
      profile.age = calculatedAge;
    }
    
    // ✅ 修复3：如果没有设置性别但提取出了性别，使用提取的性别
    if (!profile.gender && extractedGender) {
      profile.gender = extractedGender;
    }
    
    // ✅ 修复：确保 profile 对象被正确创建和保存
    // 如果 profile 对象不存在，或者年龄/性别有更新，都需要保存
    const hadProfileBefore = !!analysisData.profile;
    const ageChanged = !analysisData.profile?.age && profile.age;  // 年龄从无到有
    const genderChanged = !analysisData.profile?.gender && profile.gender;  // 性别从无到有
    const ageUpdated = analysisData.profile?.age !== profile.age;  // 年龄有变化
    const genderUpdated = analysisData.profile?.gender !== profile.gender;  // 性别有变化
    
    // 如果 profile 不存在，或者年龄/性别有更新，都需要保存
    const needsSave = !analysisData.profile || ageChanged || genderChanged || ageUpdated || genderUpdated;
    
    if (needsSave) {
      analysisData.profile = profile;
      const saveResult = CodeUtils.safeSetStorage('user_analysis_data', analysisData);
      console.log('✅ 已自动保存个人信息:', { 
        age: profile.age, 
        gender: profile.gender,
        hadProfileBefore: hadProfileBefore,
        profileCreated: !hadProfileBefore,
        saveResult: saveResult,
        needsSave: needsSave
      });
      
      // ✅ 验证保存是否成功
      const verifyData = CodeUtils.safeGetStorage('user_analysis_data', {});
      if (verifyData.profile?.age === profile.age) {
        console.log('✅ 验证：年龄已成功保存到 localStorage');
      } else {
        console.error('❌ 验证失败：年龄未成功保存');
      }
    } else {
      console.log('ℹ️ 无需保存：个人信息无变化');
    }
    
    // 创建面板 HTML
    const panelHTML = `
      <div id="personal-info-panel-overlay" style="
        position: fixed;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        background: rgba(0, 0, 0, 0.6);
        z-index: 10002;
        display: flex;
        align-items: center;
        justify-content: center;
        pointer-events: auto;
        backdrop-filter: blur(4px);
      ">
        <div id="personal-info-panel" style="
          background: linear-gradient(135deg, #1a1a2e 0%, #16213e 50%, #0f3460 100%);
          border-radius: 16px;
          padding: 30px;
          max-width: 600px;
          max-height: 80vh;
          overflow-y: auto;
          box-shadow: 0 20px 60px rgba(0, 0, 0, 0.8);
          position: relative;
          pointer-events: auto;
        ">
          <!-- 关闭按钮 -->
          <button id="personal-info-panel-close" style="
            position: absolute;
            top: 15px;
            right: 15px;
            background: rgba(255,255,255,0.1);
            border: none;
            color: white;
            width: 30px;
            height: 30px;
            border-radius: 50%;
            cursor: pointer;
            font-size: 20px;
            display: flex;
            align-items: center;
            justify-content: center;
            transition: all 0.3s ease;
          ">×</button>
          
          <!-- 标题 -->
          <h2 style="margin: 0 0 20px 0; color: #ffd700; font-size: 24px;">
            👤 个人信息
          </h2>
          
          <!-- 基本信息 -->
          <div style="
            background: rgba(255, 255, 255, 0.05);
            border: 1px solid rgba(255, 255, 255, 0.1);
            border-radius: 8px;
            padding: 15px;
            margin-bottom: 15px;
          ">
            <h3 style="margin: 0 0 15px 0; color: #ffd700; font-size: 16px;">📋 基本信息</h3>
            
            <!-- 分析对象：固定为"自己"，确保数据准确性 -->
            <div style="margin-bottom: 15px;">
              <label style="display: block; color: white; font-size: 14px; margin-bottom: 5px;">分析对象：</label>
              <div style="
                padding: 8px;
                border-radius: 4px;
                background: rgba(255, 255, 255, 0.1);
                border: 1px solid rgba(255, 255, 255, 0.2);
                color: #ffd700;
                font-size: 14px;
              ">自己</div>
              <input type="hidden" id="target-type-select" value="self">
            </div>
            
            <div style="margin-bottom: 15px;">
              <label style="display: block; color: white; font-size: 14px; margin-bottom: 5px;">姓名：</label>
              <input type="text" id="name-input" value="${profile.name || ''}" placeholder="可选" style="
                width: 100%;
                padding: 8px;
                border-radius: 4px;
                background: rgba(255, 255, 255, 0.1);
                border: 1px solid rgba(255, 255, 255, 0.2);
                color: white;
                font-size: 14px;
              ">
            </div>
            
            <div style="margin-bottom: 15px;">
              <label style="display: block; color: white; font-size: 14px; margin-bottom: 5px;">性别：</label>
              <div style="
                padding: 8px;
                border-radius: 4px;
                background: rgba(255, 255, 255, 0.1);
                border: 1px solid rgba(255, 255, 255, 0.2);
                color: ${profile.gender ? 'white' : 'rgba(255, 255, 255, 0.5)'};
                font-size: 14px;
              ">${profile.gender === 'male' ? '男' : profile.gender === 'female' ? '女' : '未设置（请重新分析）'}</div>
            </div>
            
            <div style="margin-bottom: 15px;">
              <label style="display: block; color: white; font-size: 14px; margin-bottom: 5px;">年龄：</label>
              <div style="
                padding: 8px;
                border-radius: 4px;
                background: rgba(255, 255, 255, 0.1);
                border: 1px solid rgba(255, 255, 255, 0.2);
                color: ${profile.age ? 'white' : 'rgba(255, 255, 255, 0.5)'};
                font-size: 14px;
              ">${profile.age ? profile.age + ' 岁' : '未设置（请重新分析）'}</div>
            </div>
            
            <div style="margin-bottom: 15px;">
              <label style="display: block; color: white; font-size: 14px; margin-bottom: 5px;">星座：</label>
              <div style="
                padding: 8px;
                border-radius: 4px;
                background: rgba(255, 255, 255, 0.1);
                border: 1px solid rgba(255, 255, 255, 0.2);
                color: ${analysisData.bazi_analysis?.zodiac ? 'white' : 'rgba(255, 255, 255, 0.5)'};
                font-size: 14px;
                display: flex;
                align-items: center;
                gap: 6px;
              ">
                ${analysisData.bazi_analysis?.zodiac ? 
                  `<span style="font-size: 18px;">${getZodiacIcon(analysisData.bazi_analysis.zodiac)}</span>
                   <span>${analysisData.bazi_analysis.zodiac}</span>` : 
                  '未设置（请完成八字分析）'}
              </div>
            </div>
            
            <div>
              <label style="display: block; color: white; font-size: 14px; margin-bottom: 5px;">备注：</label>
              <textarea id="notes-input" placeholder="可选" rows="3" style="
                width: 100%;
                padding: 8px;
                border-radius: 4px;
                background: rgba(255, 255, 255, 0.1);
                border: 1px solid rgba(255, 255, 255, 0.2);
                color: white;
                font-size: 14px;
                resize: vertical;
              ">${profile.notes || ''}</textarea>
            </div>
          </div>
          
          <!-- 分析数据 -->
          <div style="
            background: rgba(255, 255, 255, 0.05);
            border: 1px solid rgba(255, 255, 255, 0.1);
            border-radius: 8px;
            padding: 15px;
            margin-bottom: 15px;
          ">
            <h3 style="margin: 0 0 15px 0; color: #ffd700; font-size: 16px;">📊 分析数据</h3>
            
            <div style="display: flex; flex-direction: column; gap: 10px;">
              <!-- 面相分析 -->
              <div style="display: flex; justify-content: space-between; align-items: center; padding: 8px; background: rgba(255, 255, 255, 0.03); border-radius: 4px;">
                <div style="flex: 1;">
                  <span style="color: white; font-size: 14px;">面相分析：</span>
                  <span style="color: ${analysisData.face_analysis?.has_data ? '#4ade80' : 'rgba(255, 255, 255, 0.5)'}; font-size: 14px; margin-left: 8px;">
                    ${analysisData.face_analysis?.has_data ? '✅ 已完成' : '❌ 未分析'}
                    ${analysisData.face_analysis?.has_data && analysisData.face_analysis.analysis_time ? 
                      ' (' + new Date(analysisData.face_analysis.analysis_time).toLocaleDateString('zh-CN') + ')' : ''}
                  </span>
                </div>
                <button class="analysis-action-btn" data-analysis-type="face" style="
                  background: ${analysisData.face_analysis?.has_data ? 'rgba(255, 215, 0, 0.2)' : 'rgba(76, 222, 128, 0.2)'};
                  border: 1px solid ${analysisData.face_analysis?.has_data ? 'rgba(255, 215, 0, 0.5)' : 'rgba(76, 222, 128, 0.5)'};
                  color: ${analysisData.face_analysis?.has_data ? '#ffd700' : '#4ade80'};
                  padding: 6px 12px;
                  border-radius: 4px;
                  font-size: 12px;
                  cursor: pointer;
                  transition: all 0.2s ease;
                  white-space: nowrap;
                ">${analysisData.face_analysis?.has_data ? '🔄 重新分析' : '📸 去分析'}</button>
              </div>
              
              <!-- 手相分析 -->
              <div style="display: flex; justify-content: space-between; align-items: center; padding: 8px; background: rgba(255, 255, 255, 0.03); border-radius: 4px;">
                <div style="flex: 1;">
                  <span style="color: white; font-size: 14px;">手相分析：</span>
                  <span style="color: ${analysisData.palm_analysis?.has_data ? '#4ade80' : 'rgba(255, 255, 255, 0.5)'}; font-size: 14px; margin-left: 8px;">
                    ${analysisData.palm_analysis?.has_data ? '✅ 已完成' : '❌ 未分析'}
                    ${analysisData.palm_analysis?.has_data && analysisData.palm_analysis.analysis_time ? 
                      ' (' + new Date(analysisData.palm_analysis.analysis_time).toLocaleDateString('zh-CN') + ')' : ''}
                  </span>
                </div>
                <button class="analysis-action-btn" data-analysis-type="palm" style="
                  background: ${analysisData.palm_analysis?.has_data ? 'rgba(255, 215, 0, 0.2)' : 'rgba(76, 222, 128, 0.2)'};
                  border: 1px solid ${analysisData.palm_analysis?.has_data ? 'rgba(255, 215, 0, 0.5)' : 'rgba(76, 222, 128, 0.5)'};
                  color: ${analysisData.palm_analysis?.has_data ? '#ffd700' : '#4ade80'};
                  padding: 6px 12px;
                  border-radius: 4px;
                  font-size: 12px;
                  cursor: pointer;
                  transition: all 0.2s ease;
                  white-space: nowrap;
                ">${analysisData.palm_analysis?.has_data ? '🔄 重新分析' : '📸 去分析'}</button>
              </div>
              
              <!-- 八字分析 -->
              <div style="display: flex; justify-content: space-between; align-items: center; padding: 8px; background: rgba(255, 255, 255, 0.03); border-radius: 4px;">
                <div style="flex: 1;">
                  <span style="color: white; font-size: 14px;">八字分析：</span>
                  <span style="color: ${analysisData.bazi_analysis?.has_data ? '#4ade80' : 'rgba(255, 255, 255, 0.5)'}; font-size: 14px; margin-left: 8px;">
                    ${analysisData.bazi_analysis?.has_data ? '✅ 已完成' : '❌ 未分析'}
                    ${analysisData.bazi_analysis?.has_data && analysisData.bazi_analysis.analysis_time ? 
                      ' (' + new Date(analysisData.bazi_analysis.analysis_time).toLocaleDateString('zh-CN') + ')' : ''}
                  </span>
                </div>
                <button class="analysis-action-btn" data-analysis-type="bazi" style="
                  background: ${analysisData.bazi_analysis?.has_data ? 'rgba(255, 215, 0, 0.2)' : 'rgba(76, 222, 128, 0.2)'};
                  border: 1px solid ${analysisData.bazi_analysis?.has_data ? 'rgba(255, 215, 0, 0.5)' : 'rgba(76, 222, 128, 0.5)'};
                  color: ${analysisData.bazi_analysis?.has_data ? '#ffd700' : '#4ade80'};
                  padding: 6px 12px;
                  border-radius: 4px;
                  font-size: 12px;
                  cursor: pointer;
                  transition: all 0.2s ease;
                  white-space: nowrap;
                ">${analysisData.bazi_analysis?.has_data ? '🔄 重新分析' : '📸 去分析'}</button>
              </div>
            </div>
          </div>
          
          <!-- 前世画像 -->
          <div style="
            background: rgba(255, 255, 255, 0.05);
            border: 1px solid rgba(255, 255, 255, 0.1);
            border-radius: 8px;
            padding: 15px;
            margin-bottom: 15px;
          ">
            <h3 style="margin: 0 0 15px 0; color: #ffd700; font-size: 16px;">🔮 前世画像</h3>
            
            ${analysisData.bazi_analysis?.past_life?.has_data ? `
            <!-- 有前世画像数据时显示 -->
            <div style="text-align: center;">
              ${analysisData.bazi_analysis.past_life.portraitUrl ? `
              <div style="margin-bottom: 15px;">
                <img src="${analysisData.bazi_analysis.past_life.portraitUrl}" 
                     alt="${analysisData.bazi_analysis.past_life.historicalFigure || '前世画像'}" 
                     style="
                       width: 120px;
                       height: 120px;
                       border-radius: 50%;
                       border: 3px solid rgba(102, 126, 234, 0.5);
                       object-fit: cover;
                       box-shadow: 0 4px 12px rgba(102, 126, 234, 0.3);
                     "
                     onerror="this.onerror=null; this.src='/static/uploads/portraits/default.svg'; this.style.display='block';">
                ${analysisData.bazi_analysis.past_life.historicalFigure ? `
                <div style="margin-top: 10px; color: #ffd700; font-weight: bold; font-size: 16px;">
                  ${analysisData.bazi_analysis.past_life.historicalFigure}
                </div>
                ` : ''}
              </div>
              ` : ''}
              
              ${analysisData.bazi_analysis.past_life.historicalFigure && !analysisData.bazi_analysis.past_life.portraitUrl ? `
              <div style="margin-bottom: 15px; color: #ffd700; font-weight: bold; font-size: 16px;">
                ${analysisData.bazi_analysis.past_life.historicalFigure}
              </div>
              ` : ''}
              
              <div style="
                background: rgba(255, 255, 255, 0.05);
                border: 1px solid rgba(255, 255, 255, 0.1);
                border-radius: 8px;
                padding: 12px;
                color: rgba(255, 255, 255, 0.9);
                font-size: 13px;
                line-height: 1.6;
                text-align: left;
                max-height: 150px;
                overflow-y: auto;
              ">
                ${analysisData.bazi_analysis.past_life.content ? analysisData.bazi_analysis.past_life.content.substring(0, 300) + (analysisData.bazi_analysis.past_life.content.length > 300 ? '...' : '') : '暂无内容'}
              </div>
              
              ${analysisData.bazi_analysis.past_life.generated_time ? `
              <div style="margin-top: 10px; color: rgba(255, 255, 255, 0.6); font-size: 12px;">
                生成时间：${new Date(analysisData.bazi_analysis.past_life.generated_time).toLocaleDateString('zh-CN')}
              </div>
              ` : ''}
            </div>
            ` : `
            <!-- 没有前世画像数据时显示提示 -->
            <div style="
              text-align: center;
              padding: 20px;
              color: rgba(255, 255, 255, 0.6);
              font-size: 14px;
            ">
              在八字分析里生成前世画像
            </div>
            `}
          </div>
          
          <!-- 灵魂伴侣信息 -->
          <div style="
            background: rgba(255, 255, 255, 0.05);
            border: 1px solid rgba(255, 255, 255, 0.1);
            border-radius: 8px;
            padding: 15px;
            margin-bottom: 15px;
          ">
            <h3 style="margin: 0 0 15px 0; color: #ffd700; font-size: 16px;">🎯 灵魂伴侣信息</h3>
            
            ${(() => {
              // ✅ 修复：确保 personalizationManager 存在，如果不存在则尝试从全局获取
              const pm = this.personalizationManager || window.personalizationManager;
              
              if (!pm) {
                console.warn('⚠️ personalizationManager 不存在，使用默认值');
                return `
                  <div id="soulmate-info-container" style="display: flex; flex-direction: column; gap: 10px;">
                    <div style="display: flex; justify-content: space-between; align-items: center; padding: 8px; background: rgba(255, 255, 255, 0.03); border-radius: 4px;">
                      <span style="color: white; font-size: 14px;">关系等级：</span>
                      <span style="color: #ffd700; font-size: 14px; font-weight: 600;">陌生人 (Lv.1)</span>
                    </div>
                    <div style="display: flex; justify-content: space-between; align-items: center; padding: 8px; background: rgba(255, 255, 255, 0.03); border-radius: 4px;">
                      <span style="color: white; font-size: 14px;">经验值：</span>
                      <span style="color: white; font-size: 14px;">0 / 100</span>
                    </div>
                  </div>
                `;
              }
              
              // ✅ 修复：确保数据是最新的（重新获取）
              const relationshipLevel = pm.getRelationshipLevel();
              const experienceInfo = pm.getExperienceInfo();
              
              // ✅ 修复：使用正确的字段名和等级名称
              const levelName = experienceInfo.levelName || '陌生人';
              
              // ✅ 修复：计算下一级所需的总经验值
              const currentExp = experienceInfo.total || 0;
              const nextLevelExp = experienceInfo.nextLevelRequired 
                ? (currentExp + experienceInfo.nextLevelRequired) 
                : 'MAX';
              
              return `
                <div id="soulmate-info-container" style="display: flex; flex-direction: column; gap: 10px;">
                  <div style="display: flex; justify-content: space-between; align-items: center; padding: 8px; background: rgba(255, 255, 255, 0.03); border-radius: 4px;">
                    <span style="color: white; font-size: 14px;">关系等级：</span>
                    <span id="soulmate-level-display" style="color: #ffd700; font-size: 14px; font-weight: 600;">${levelName} (Lv.${experienceInfo.level || 1})</span>
                  </div>
                  <div style="display: flex; justify-content: space-between; align-items: center; padding: 8px; background: rgba(255, 255, 255, 0.03); border-radius: 4px;">
                    <span style="color: white; font-size: 14px;">经验值：</span>
                    <span id="soulmate-exp-display" style="color: white; font-size: 14px;">${this.formatNumber(currentExp, 999999999)} / ${typeof nextLevelExp === 'number' ? this.formatNumber(nextLevelExp, 999999999) : nextLevelExp}</span>
                  </div>
                </div>
              `;
            })()}
          </div>
          
          <!-- ✅ 已移除保存和取消按钮，姓名和备注改为实时保存 -->
        </div>
      </div>
    `;
    
    // 移除旧面板（如果存在）
    const oldPanel = document.getElementById('personal-info-panel-overlay');
    if (oldPanel) oldPanel.remove();
    
    // 添加新面板
    document.body.insertAdjacentHTML('beforeend', panelHTML);
    
    // 绑定事件
    this.bindPersonalInfoPanelEvents();
  }

  /**
   * 绑定个人信息面板事件
   */
  bindPersonalInfoPanelEvents() {
    // 关闭按钮
    const closeBtn = document.getElementById('personal-info-panel-close');
    const overlay = document.getElementById('personal-info-panel-overlay');
    
    const closePanel = () => {
      if (overlay) overlay.remove();
    };
    
    if (closeBtn) closeBtn.addEventListener('click', closePanel);
    
    // 点击遮罩关闭
    if (overlay) {
      overlay.addEventListener('click', (e) => {
        if (e.target === overlay) closePanel();
      });
    }
    
    // ✅ 新增：姓名和备注实时保存（失去焦点时自动保存）
    const nameInput = document.getElementById('name-input');
    const notesInput = document.getElementById('notes-input');
    
    // 保存姓名和备注的辅助函数
    const saveNameAndNotes = () => {
      try {
        const analysisData = CodeUtils.safeGetStorage('user_analysis_data', {});
        const name = nameInput ? nameInput.value.trim() : '';
        const notes = notesInput ? notesInput.value.trim() : '';
        
        // 更新 profile（只更新姓名和备注，性别和年龄从分析数据自动提取）
        if (!analysisData.profile) {
          analysisData.profile = {};
        }
        analysisData.profile.name = name;
        analysisData.profile.notes = notes;
        analysisData.profile.target_type = 'self'; // 固定为"自己"
        analysisData.profile.updated_at = new Date().toISOString();
        
        // 保存到 localStorage
        CodeUtils.safeSetStorage('user_analysis_data', analysisData);
        
        // ✅ 如果修改了姓名，同步更新用户昵称
        if (name) {
          CodeUtils.safeSetStorage('user_nickname', name);
          if (this.personalizationManager && typeof this.personalizationManager.setUserNickname === 'function') {
            this.personalizationManager.setUserNickname(name);
          }
        }
        
        // 静默保存，不显示提示（避免频繁提示）
      } catch (error) {
        console.error('保存姓名和备注失败:', error);
      }
    };
    
    // 姓名输入框失去焦点时保存
    if (nameInput) {
      nameInput.addEventListener('blur', saveNameAndNotes);
      // 也支持回车键保存
      nameInput.addEventListener('keypress', (e) => {
        if (e.key === 'Enter') {
          nameInput.blur();
          saveNameAndNotes();
        }
      });
    }
    
    // 备注输入框失去焦点时保存
    if (notesInput) {
      notesInput.addEventListener('blur', saveNameAndNotes);
    }
    
    // ✅ 分析按钮事件绑定
    const analysisButtons = document.querySelectorAll('.analysis-action-btn');
    analysisButtons.forEach(btn => {
      btn.addEventListener('click', (e) => {
        e.stopPropagation();
        const analysisType = btn.getAttribute('data-analysis-type');
        
        // ✅ 修复：从个人信息面板点击分析，立即标记为"自己"
        const analysisData = CodeUtils.safeGetStorage('user_analysis_data', {});
        if (!analysisData.profile) {
          analysisData.profile = {
            target_type: 'self',  // ✅ 标记为"自己"
            name: '',
            gender: '',
            age: '',
            notes: '',
            created_at: new Date().toISOString(),
            updated_at: new Date().toISOString()
          };
        } else {
          analysisData.profile.target_type = 'self';  // ✅ 确保标记为"自己"
          analysisData.profile.updated_at = new Date().toISOString();
        }
        CodeUtils.safeSetStorage('user_analysis_data', analysisData);
        debugLog('✅ 从个人信息面板点击分析，已标记为"自己"', { analysisType, targetType: analysisData.profile.target_type });
        
        // 关闭个人信息面板
        const overlay = document.getElementById('personal-info-panel-overlay');
        if (overlay) overlay.remove();
        
        // ✅ 新增：关闭灵魂伴侣界面
        if (this.closeSoulmateChat && typeof this.closeSoulmateChat === 'function') {
          this.closeSoulmateChat();
        } else if (window.soulmateManager && typeof window.soulmateManager.closeSoulmateChat === 'function') {
          window.soulmateManager.closeSoulmateChat();
        }
        
        // 跳转到对应的分析页面
        // ✅ 修复：使用多种方式尝试启动分析功能
        let analysisStarted = false;
        
        if (analysisType === 'face') {
          // 尝试多种方式启动面相分析
          if (window.soulmateManager && typeof window.soulmateManager.startFaceAnalysis === 'function') {
            window.soulmateManager.startFaceAnalysis();
            analysisStarted = true;
          } else if (this.startFaceAnalysis && typeof this.startFaceAnalysis === 'function') {
            this.startFaceAnalysis();
            analysisStarted = true;
          } else if (typeof startFaceAnalysis === 'function') {
            startFaceAnalysis();
            analysisStarted = true;
          } else {
            // 尝试直接切换分析类型
            const analysisTypeSelect = document.getElementById('analysisType');
            if (analysisTypeSelect) {
              analysisTypeSelect.value = 'face';
              analysisTypeSelect.dispatchEvent(new Event('change'));
              analysisStarted = true;
            }
          }
        } else if (analysisType === 'palm') {
          // 尝试多种方式启动手相分析
          if (window.soulmateManager && typeof window.soulmateManager.startPalmAnalysis === 'function') {
            window.soulmateManager.startPalmAnalysis();
            analysisStarted = true;
          } else if (this.startPalmAnalysis && typeof this.startPalmAnalysis === 'function') {
            this.startPalmAnalysis();
            analysisStarted = true;
          } else if (typeof startPalmAnalysis === 'function') {
            startPalmAnalysis();
            analysisStarted = true;
          } else {
            // 尝试直接切换分析类型
            const analysisTypeSelect = document.getElementById('analysisType');
            if (analysisTypeSelect) {
              analysisTypeSelect.value = 'palm';
              analysisTypeSelect.dispatchEvent(new Event('change'));
              analysisStarted = true;
            }
          }
        } else if (analysisType === 'bazi') {
          // 尝试多种方式启动八字分析
          if (window.soulmateManager && typeof window.soulmateManager.startBaziAnalysis === 'function') {
            window.soulmateManager.startBaziAnalysis();
            analysisStarted = true;
          } else if (this.startBaziAnalysis && typeof this.startBaziAnalysis === 'function') {
            this.startBaziAnalysis();
            analysisStarted = true;
          } else if (typeof startBaziAnalysis === 'function') {
            startBaziAnalysis();
            analysisStarted = true;
          } else {
            // 尝试直接切换分析类型
            const analysisTypeSelect = document.getElementById('analysisType');
            if (analysisTypeSelect) {
              analysisTypeSelect.value = 'bazi';
              analysisTypeSelect.dispatchEvent(new Event('change'));
              analysisStarted = true;
            }
          }
        }
        
        if (!analysisStarted) {
          CodeUtils.showToast('分析功能暂不可用', 'warning', 2000);
        } else {
          // 滚动到分析区域
          setTimeout(() => {
            const analysisSection = document.querySelector('#imageUploadBlock, .analysis-section, #analysisType');
            if (analysisSection) {
              analysisSection.scrollIntoView({ behavior: 'smooth', block: 'center' });
            }
          }, 300);
        }
      });
    });
    
    // ✅ 修复：添加实时更新机制
    this.updateSoulmateInfoDisplay();
  }

  /**
   * ✅ 新增：更新灵魂伴侣信息显示
   */
  updateSoulmateInfoDisplay() {
    const container = document.getElementById('soulmate-info-container');
    if (!container) return;
    
    const pm = this.personalizationManager || window.personalizationManager;
    if (!pm) return;
    
    try {
      const relationshipLevel = pm.getRelationshipLevel();
      const experienceInfo = pm.getExperienceInfo();
      
      const levelName = experienceInfo.levelName || '陌生人';
      const currentExp = experienceInfo.total || 0;
      const nextLevelExp = experienceInfo.nextLevelRequired 
        ? (currentExp + experienceInfo.nextLevelRequired) 
        : 'MAX';
      
      const levelDisplay = document.getElementById('soulmate-level-display');
      const expDisplay = document.getElementById('soulmate-exp-display');
      
      if (levelDisplay) {
        levelDisplay.textContent = `${levelName} (Lv.${experienceInfo.level || 1})`;
      }
      
      if (expDisplay) {
        expDisplay.textContent = `${this.formatNumber(currentExp, 999999999)} / ${typeof nextLevelExp === 'number' ? this.formatNumber(nextLevelExp, 999999999) : nextLevelExp}`;
      }
    } catch (error) {
      console.error('更新灵魂伴侣信息显示失败:', error);
    }
  }

  // ❌ 已删除：废弃函数 savePersonalInfo() (已改为实时保存，在 bindPersonalInfoPanelEvents 中处理)
  // 姓名和备注已改为失去焦点时自动保存，性别和年龄改为只读，从分析数据自动提取

  /**
   * 显示通知
   * @param {string} message - 通知消息
   * @param {string} type - 通知类型
   * @param {number} duration - 持续时间
   */
  showNotification(message, type = 'info', duration = 3000) {
    if (typeof CodeUtils !== 'undefined') {
      CodeUtils.showToast(message, type, duration);
    } else {
      // 降级方案：使用原生通知
      if (Notification.permission === 'granted') {
        new Notification('AI灵魂伴侣', {
          body: message,
          icon: '/static/images/icon.png'
        });
      }
    }
  }

  /**
   * 获取关系等级
   * @returns {number} 关系等级
   */
  getRelationshipLevel() {
    if (this.personalizationManager) {
      return this.personalizationManager.getRelationshipLevel();
    }
    return 0; // 默认等级
  }

  /**
   * 获取关系等级名称
   * @returns {string} 关系等级名称
   */
  getRelationshipLevelName() {
    if (this.personalizationManager) {
      return this.personalizationManager.getRelationshipLevelName();
    }
    return '陌生人'; // 默认名称
  }

  /**
   * 加载已获得的成就
   */
  loadAchievements() {
    this.achievements = CodeUtils.safeGetStorage('soulmate_achievements', []);
    console.log(`📜 已加载 ${this.achievements.length} 个成就`);
  }

  /**
   * 检查成就
   */
  checkAchievements() {
    if (!this.achievementDefinitions) {
      console.warn('⚠️ 成就系统未初始化');
      return;
    }
    
    const newAchievements = [];
    
    // 遍历所有成就定义，检查是否满足条件
    for (const [key, achievement] of Object.entries(this.achievementDefinitions)) {
      // 如果已获得，跳过
      if (this.achievements.includes(achievement.id)) {
        continue;
      }
      
      // 检查是否满足条件
      try {
        if (achievement.condition && achievement.condition.call(this)) {
          // 获得新成就
          this.achievements.push(achievement.id);
          newAchievements.push(achievement);
          
          // 保存到 localStorage
          CodeUtils.safeSetStorage('soulmate_achievements', this.achievements);
          
          console.log(`🎉 获得新成就: ${achievement.name}`);
        }
      } catch (error) {
        console.error(`检查成就 ${achievement.name} 时出错:`, error);
      }
    }
    
    // 显示新获得的成就
    if (newAchievements.length > 0) {
      newAchievements.forEach(achievement => {
        this.showAchievement(achievement);
      });
    }
    
    return newAchievements;
  }

  /**
   * 更新聊天界面中的成长进度显示
   */
  updateGrowthProgressDisplay() {
    if (!window.personalizationManager) {
      return;
    }
    
    const progressContainer = document.getElementById('soulmate-growth-progress');
    const levelSpan = document.getElementById('growth-level');
    const progressBar = document.getElementById('growth-progress-bar');
    const progressText = document.getElementById('growth-progress-text');
    
    if (!progressContainer || !levelSpan || !progressBar || !progressText) {
      return;
    }
    
    try {
      const expInfo = window.personalizationManager.getExperienceInfo();
      
      // 显示容器
      progressContainer.style.display = 'block';
      
      // 更新等级
      levelSpan.textContent = expInfo.level;
      
      // 更新进度条
      const progress = Math.max(0, Math.min(100, expInfo.progress));
      progressBar.style.width = progress + '%';
      
      // ✅ 优化：更新进度文本，显示更详细的信息
      if (expInfo.level >= 5) {
        progressText.textContent = 'MAX';
        progressText.title = '已达到最高等级';
      } else {
        // 显示百分比，并在 title 中显示详细信息（鼠标悬停时查看）
        const percentage = Math.round(progress);
        progressText.textContent = `${percentage}%`;
        // 使用 title 属性显示完整信息（工具提示）
        progressText.title = `当前进度：${percentage}%\n距离 ${expInfo.nextLevelName || '下一级'} 还需 ${this.formatNumber(expInfo.nextLevelRequired || 0, 999999999)} 经验值`;
      }
    } catch (error) {
      console.error('更新成长进度显示失败:', error);
    }
  }

  /**
   * 显示成长面板
   */
  showGrowthPanel() {
    if (!window.personalizationManager) {
      this.showNotification('成长系统未初始化', 'warning');
      return;
    }
    
    const expInfo = window.personalizationManager.getExperienceInfo();
    const achievements = this.achievements || [];
    const achievementDefinitions = this.achievementDefinitions || {};
    
    // 统计已获得成就
    const unlockedAchievements = achievements.map(id => achievementDefinitions[id]).filter(Boolean);
    const lockedAchievements = Object.values(achievementDefinitions)
      .filter(ach => !achievements.includes(ach.id));
    
    // ✅ 修复：移除全屏遮罩，改为完全透明，让主界面清晰可见
    const panelHTML = `
      <div id="growth-panel-overlay" style="
        position: fixed;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        background: rgba(0, 0, 0, 0);
        z-index: 10001;
        display: flex;
        align-items: center;
        justify-content: center;
        pointer-events: none;
      ">
        <div id="growth-panel" style="
          background: linear-gradient(135deg, #1a1a2e 0%, #16213e 50%, #0f3460 100%);
          border-radius: 16px;
          padding: 30px;
          max-width: 600px;
          max-height: 80vh;
          overflow-y: auto;
          box-shadow: 0 20px 60px rgba(0, 0, 0, 0.8);
          position: relative;
          pointer-events: auto;
        ">
          <!-- 关闭按钮 -->
          <button id="growth-panel-close" style="
            position: absolute;
            top: 15px;
            right: 15px;
            background: rgba(255,255,255,0.1);
            border: none;
            color: white;
            width: 30px;
            height: 30px;
            border-radius: 50%;
            cursor: pointer;
            font-size: 20px;
            display: flex;
            align-items: center;
            justify-content: center;
            transition: all 0.3s ease;
          ">×</button>
          
          <!-- 标题和排行榜按钮 -->
          <div style="display: flex; justify-content: space-between; align-items: center; margin-bottom: 20px;">
            <h2 style="margin: 0; color: #ffd700; font-size: 24px;">
              ⭐ 我的成长
            </h2>
            <button id="growth-panel-leaderboard-btn" style="
              background: rgba(255, 215, 0, 0.2);
              border: 1px solid rgba(255, 215, 0, 0.5);
              color: #ffd700;
              padding: 6px 12px;
              border-radius: 6px;
              font-size: 12px;
              cursor: pointer;
              transition: all 0.3s ease;
            " onmouseover="this.style.background='rgba(255, 215, 0, 0.3)'; this.style.transform='scale(1.05)'" onmouseout="this.style.background='rgba(255, 215, 0, 0.2)'; this.style.transform='scale(1)'">🏆 排行榜</button>
          </div>
          
          <!-- 经验值和等级 -->
          <div style="
            background: rgba(255, 215, 0, 0.1);
            border: 1px solid rgba(255, 215, 0, 0.3);
            border-radius: 12px;
            padding: 20px;
            margin-bottom: 20px;
          ">
            <div style="display: flex; justify-content: space-between; align-items: center; margin-bottom: 10px;">
              <span style="color: #ffd700; font-size: 18px; font-weight: 600;">
                ${expInfo.levelName || '等级 ' + expInfo.level} (Lv.${expInfo.level})
              </span>
              <span style="color: rgba(255,255,255,0.8); font-size: 14px;">
                总经验：${this.formatNumber(expInfo.total || 0, 999999999)}
              </span>
            </div>
            <div style="
              height: 20px;
              background: rgba(255,255,255,0.1);
              border-radius: 10px;
              overflow: hidden;
              margin-bottom: 8px;
            ">
              <div style="
                height: 100%;
                background: linear-gradient(90deg, #ffd700, #ffed4e);
                width: ${Math.max(0, Math.min(100, expInfo.progress || 0))}%;
                transition: width 0.3s ease;
                display: flex;
                align-items: center;
                justify-content: flex-end;
                padding-right: 8px;
                font-size: 11px;
                color: #000;
                font-weight: 600;
              ">
                ${Math.round(expInfo.progress || 0)}%
              </div>
            </div>
            ${expInfo.level < 5 ? `
              <p style="margin: 0; color: rgba(255,255,255,0.7); font-size: 12px; text-align: center;">
                距离 ${expInfo.nextLevelName || '下一级'} 还需 ${this.formatNumber(expInfo.nextLevelRequired || 0, 999999999)} 经验值
              </p>
            ` : `
              <p style="margin: 0; color: #ffd700; font-size: 12px; text-align: center; font-weight: 600;">
                🎉 已达到最高等级！
              </p>
            `}
            ${expInfo.consecutiveDays > 0 && expInfo.consecutiveDays <= 3650 ? `
              <p style="margin: 5px 0 0 0; color: rgba(255,255,255,0.6); font-size: 11px; text-align: center;">
                连续使用 ${this.formatNumber(expInfo.consecutiveDays, 3650)} 天
              </p>
            ` : ''}
          </div>
          
          <!-- 成就列表 -->
          <div>
            <h3 style="margin: 0 0 15px 0; color: #ffd700; font-size: 18px;">
              🏆 成就 (${unlockedAchievements.length}/${unlockedAchievements.length + lockedAchievements.length})
            </h3>
            
            <!-- 已解锁成就 -->
            ${unlockedAchievements.length > 0 ? `
              <div style="margin-bottom: 20px;">
                <h4 style="margin: 0 0 10px 0; color: rgba(255,255,255,0.9); font-size: 14px;">已解锁</h4>
                <div style="display: grid; grid-template-columns: repeat(auto-fill, minmax(140px, 1fr)); gap: 10px;">
                  ${unlockedAchievements.map(ach => `
                    <div style="
                      background: rgba(255, 215, 0, 0.15);
                      border: 1px solid rgba(255, 215, 0, 0.4);
                      border-radius: 8px;
                      padding: 12px;
                      text-align: center;
                      transition: transform 0.2s ease;
                    " onmouseover="this.style.transform='scale(1.05)'" onmouseout="this.style.transform='scale(1)'">
                      <div style="font-size: 32px; margin-bottom: 5px;">${ach.icon || '🏆'}</div>
                      <div style="color: #ffd700; font-size: 12px; font-weight: 600; margin-bottom: 3px;">${ach.name || ''}</div>
                      <div style="color: rgba(255,255,255,0.7); font-size: 10px;">${ach.description || ''}</div>
                    </div>
                  `).join('')}
                </div>
              </div>
            ` : ''}
            
            <!-- 未解锁成就 -->
            ${lockedAchievements.length > 0 ? `
              <div>
                <h4 style="margin: 0 0 10px 0; color: rgba(255,255,255,0.7); font-size: 14px;">未解锁</h4>
                <div style="display: grid; grid-template-columns: repeat(auto-fill, minmax(140px, 1fr)); gap: 10px;">
                  ${lockedAchievements.map(ach => `
                    <div style="
                      background: rgba(255,255,255,0.05);
                      border: 1px dashed rgba(255,255,255,0.2);
                      border-radius: 8px;
                      padding: 12px;
                      text-align: center;
                      opacity: 0.5;
                    ">
                      <div style="font-size: 32px; margin-bottom: 5px; filter: grayscale(100%);">${ach.icon || '🔒'}</div>
                      <div style="color: rgba(255,255,255,0.5); font-size: 12px; font-weight: 600; margin-bottom: 3px;">${ach.name || ''}</div>
                      <div style="color: rgba(255,255,255,0.4); font-size: 10px;">${ach.description || ''}</div>
                    </div>
                  `).join('')}
                </div>
              </div>
            ` : ''}
          </div>
          
          <!-- ✅ 新增：VIP升级引导 -->
          <div style="
            background: rgba(255, 215, 0, 0.1);
            border: 1px solid rgba(255, 215, 0, 0.3);
            border-radius: 12px;
            padding: 20px;
            margin-top: 20px;
            text-align: center;
          ">
            <div style="color: #ffd700; font-size: 16px; font-weight: 600; margin-bottom: 10px;">
              💎 升级VIP，立即解锁全部功能
            </div>
            <div style="color: rgba(255,255,255,0.8); font-size: 12px; margin-bottom: 15px;">
              解锁全部运势分析、生活指导、心理疗愈功能
            </div>
            <button class="btn-vip-upgrade-large" style="
              background: linear-gradient(135deg, #ffd700, #ffed4e);
              border: none;
              color: #000;
              padding: 12px 24px;
              border-radius: 8px;
              font-size: 14px;
              font-weight: 600;
              cursor: pointer;
              width: 100%;
              transition: all 0.3s ease;
            " onmouseover="this.style.transform='scale(1.05)'; this.style.boxShadow='0 4px 12px rgba(255, 215, 0, 0.5)'" onmouseout="this.style.transform='scale(1)'; this.style.boxShadow='none'">
              升级VIP 5.9元/月
            </button>
          </div>
        </div>
      </div>
    `;
    
    // 移除旧面板（如果存在）
    const oldPanel = document.getElementById('growth-panel-overlay');
    if (oldPanel) {
      oldPanel.remove();
    }
    
    // 添加新面板
    document.body.insertAdjacentHTML('beforeend', panelHTML);
    
    // ✅ 修复：绑定关闭事件（移除遮罩点击关闭，因为遮罩已透明且不阻挡）
    const closeBtn = document.getElementById('growth-panel-close');
    const leaderboardBtn = document.getElementById('growth-panel-leaderboard-btn');
    const overlay = document.getElementById('growth-panel-overlay');
    if (closeBtn) {
      closeBtn.addEventListener('click', () => {
        if (overlay) overlay.remove();
      });
    }
    // ✅ P2-2修复：绑定排行榜按钮事件
    if (leaderboardBtn) {
      leaderboardBtn.addEventListener('click', () => {
        if (overlay) overlay.remove();
        this.showLeaderboard('level');
      });
    }
    
    // ✅ 新增：VIP升级按钮事件
    const vipUpgradeBtn = document.getElementById('growth-panel')?.querySelector('.btn-vip-upgrade-large');
    if (vipUpgradeBtn) {
      vipUpgradeBtn.addEventListener('click', async () => {
        // ✅ 修复：使用 window.showQRInPopup 并添加等待机制
        const waitForShowQRInPopup = async (maxRetries = 10, delay = 200) => {
          for (let i = 0; i < maxRetries; i++) {
            if (typeof window.showQRInPopup === 'function') {
              return true;
            }
            await new Promise(resolve => setTimeout(resolve, delay));
          }
          return false;
        };
        
        const isLoaded = await waitForShowQRInPopup();
        if (isLoaded) {
          try {
            await window.showQRInPopup('vip');
            this.checkAndUpdateVipStatus();
          } catch (error) {
            console.error('打开VIP支付弹窗失败:', error);
            CodeUtils.showToast('打开支付弹窗失败，请稍后再试', 'error', 3000);
          }
        } else {
          CodeUtils.showToast('支付功能加载超时，请刷新页面重试', 'error', 3000);
        }
      });
    }
    
    // ✅ 移除点击遮罩关闭的逻辑（因为遮罩已透明且 pointer-events: none）
    
    // 添加打开动画
    setTimeout(() => {
      const panel = document.getElementById('growth-panel');
      if (panel) {
        panel.style.opacity = '0';
        panel.style.transform = 'scale(0.9)';
        panel.style.transition = 'all 0.3s ease';
        setTimeout(() => {
          panel.style.opacity = '1';
          panel.style.transform = 'scale(1)';
        }, 10);
      }
    }, 10);
  }

  /**
   * 显示成就面板（仅显示成就，不显示经验值和等级）
   */
  showAchievementPanel() {
    const achievements = this.achievements || [];
    const achievementDefinitions = this.achievementDefinitions || {};
    // 统计已获得成就
    const unlockedAchievements = achievements.map(id => achievementDefinitions[id]).filter(Boolean);
    const lockedAchievements = Object.values(achievementDefinitions)
      .filter(ach => !achievements.includes(ach.id));
    
    const panelHTML = `
      <div id="achievement-panel-overlay" style="
        position: fixed;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        background: rgba(0, 0, 0, 0.5);
        z-index: 10001;
        display: flex;
        align-items: center;
        justify-content: center;
        pointer-events: none;
      ">
        <div id="achievement-panel" style="
          background: linear-gradient(135deg, #1a1a2e 0%, #16213e 50%, #0f3460 100%);
          border-radius: 16px;
          padding: 30px;
          max-width: 700px;
          max-height: 80vh;
          overflow-y: auto;
          box-shadow: 0 20px 60px rgba(0, 0, 0, 0.8);
          position: relative;
          pointer-events: auto;
        ">
          <!-- 关闭按钮 -->
          <button id="achievement-panel-close" style="
            position: absolute;
            top: 15px;
            right: 15px;
            background: rgba(255,255,255,0.1);
            border: none;
            color: white;
            width: 30px;
            height: 30px;
            border-radius: 50%;
            cursor: pointer;
            font-size: 20px;
            display: flex;
            align-items: center;
            justify-content: center;
            transition: all 0.3s ease;
          " onmouseover="this.style.background='rgba(255,255,255,0.2)'" onmouseout="this.style.background='rgba(255,255,255,0.1)'">×</button>
          <!-- 标题 -->
          <h2 style="margin: 0 0 20px 0; color: #ffd700; font-size: 24px;">
            🏆 我的成就
          </h2>
          <!-- 成就统计 -->
          <div style="
            background: rgba(255, 215, 0, 0.1);
            border: 1px solid rgba(255, 215, 0, 0.3);
            border-radius: 12px;
            padding: 15px;
            margin-bottom: 20px;
            text-align: center;
          ">
            <div style="color: #ffd700; font-size: 18px; font-weight: 600; margin-bottom: 5px;">
              成就进度
            </div>
            <div style="color: rgba(255,255,255,0.8); font-size: 14px;">
              已解锁 ${unlockedAchievements.length} / ${unlockedAchievements.length + lockedAchievements.length}
            </div>
            <div style="
              height: 8px;
              background: rgba(255,255,255,0.1);
              border-radius: 4px;
              overflow: hidden;
              margin-top: 10px;
            ">
              <div style="
                height: 100%;
                background: linear-gradient(90deg, #ffd700, #ffed4e);
                width: ${unlockedAchievements.length + lockedAchievements.length > 0
                  ? (unlockedAchievements.length / (unlockedAchievements.length + lockedAchievements.length) * 100)
                  : 0}%;
                transition: width 0.3s ease;
              "></div>
            </div>
          </div>
          <!-- 已解锁成就 -->
          ${unlockedAchievements.length > 0 ? `
            <div style="margin-bottom: 25px;">
              <h3 style="margin: 0 0 15px 0; color: #ffd700; font-size: 18px;">
                ✅ 已解锁 (${unlockedAchievements.length})
              </h3>
              <div style="display: grid; grid-template-columns: repeat(auto-fill, minmax(150px, 1fr)); gap: 12px;">
                ${unlockedAchievements.map(ach => `
                  <div style="
                    background: rgba(255, 215, 0, 0.15);
                    border: 1px solid rgba(255, 215, 0, 0.4);
                    border-radius: 8px;
                    padding: 15px;
                    text-align: center;
                    transition: transform 0.2s ease;
                  " onmouseover="this.style.transform='scale(1.05)'" onmouseout="this.style.transform='scale(1)'">
                    <div style="font-size: 40px; margin-bottom: 8px;">${ach.icon || '🏆'}</div>
                    <div style="color: #ffd700; font-size: 13px; font-weight: 600; margin-bottom: 5px;">${ach.name || ''}</div>
                    <div style="color: rgba(255,255,255,0.7); font-size: 11px; line-height: 1.4;">${ach.description || ''}</div>
                  </div>
                `).join('')}
              </div>
            </div>
          ` : `
            <div style="
              text-align: center;
              color: rgba(255, 255, 255, 0.6);
              padding: 40px 20px;
              font-size: 14px;
            ">
              还没有解锁任何成就<br/>
              继续使用功能，解锁更多成就吧！
            </div>
          `}
          <!-- 未解锁成就 -->
          ${lockedAchievements.length > 0 ? `
            <div>
              <h3 style="margin: 0 0 15px 0; color: rgba(255,255,255,0.7); font-size: 18px;">
                🔒 未解锁 (${lockedAchievements.length})
              </h3>
              <div style="display: grid; grid-template-columns: repeat(auto-fill, minmax(150px, 1fr)); gap: 12px;">
                ${lockedAchievements.map(ach => `
                  <div style="
                    background: rgba(255,255,255,0.05);
                    border: 1px dashed rgba(255,255,255,0.2);
                    border-radius: 8px;
                    padding: 15px;
                    text-align: center;
                    opacity: 0.5;
                  ">
                    <div style="font-size: 40px; margin-bottom: 8px; filter: grayscale(100%);">${ach.icon || '🔒'}</div>
                    <div style="color: rgba(255,255,255,0.5); font-size: 13px; font-weight: 600; margin-bottom: 5px;">${ach.name || ''}</div>
                    <div style="color: rgba(255,255,255,0.4); font-size: 11px; line-height: 1.4;">${ach.description || ''}</div>
                  </div>
                `).join('')}
              </div>
            </div>
          ` : `
            <div style="
              text-align: center;
              color: rgba(255, 255, 255, 0.6);
              padding: 20px;
              font-size: 14px;
            ">
              🎉 恭喜！你已经解锁了所有成就！
            </div>
          `}
        </div>
      </div>
    `;
    
    // 移除旧面板（如果存在）
    const oldPanel = document.getElementById('achievement-panel-overlay');
    if (oldPanel) {
      oldPanel.remove();
    }
    
    // 添加新面板
    document.body.insertAdjacentHTML('beforeend', panelHTML);
    
    // 绑定关闭事件
    const closeBtn = document.getElementById('achievement-panel-close');
    const overlay = document.getElementById('achievement-panel-overlay');
    if (closeBtn) {
      closeBtn.addEventListener('click', () => {
        if (overlay) overlay.remove();
      });
    }
    
    // 点击遮罩关闭
    if (overlay) {
      overlay.addEventListener('click', (e) => {
        if (e.target === overlay) {
          overlay.remove();
        }
      });
    }
  }

  /**
   * ✅ P2-3修复：处理AI回复反馈
   * @param {string} messageId - 消息ID
   * @param {string} content - 消息内容
   * @param {string} feedback - 反馈类型 ('like' 或 'dislike')
   */
  async handleFeedback(messageId, content, feedback) {
    try {
      const feedbackData = {
        message_id: messageId,
        content: content.substring(0, 500), // 限制长度
        feedback: feedback,
        timestamp: new Date().toISOString(),
        session_id: CodeUtils.safeGetStorage('session_id', ''),
        user_agent: navigator.userAgent
      };
      
      // 保存到本地存储（用于离线时同步）
      const feedbackHistory = CodeUtils.safeGetStorage('ai_feedback_history', []);
      feedbackHistory.push(feedbackData);
      CodeUtils.safeSetStorage('ai_feedback_history', feedbackHistory.slice(-100)); // 只保留最近100条
      
      // ✅ 修复：使用相对路径发送反馈（同源请求，避免跨域问题）
      // 注意：接口可能未实现，浏览器 DevTools 可能会显示 404 错误（这是浏览器网络层的默认行为，无法通过 JavaScript 消除）
      // 但错误已被静默处理，不会记录到 analytics，也不影响用户体验
      // 数据已保存到本地存储，接口实现后会自动生效
      try {
        const response = await fetch('/api/ai_feedback', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(feedbackData)
        });
        if (response.ok) {
          console.log('✅ 反馈已发送到后端');
        } else if (response.status === 404) {
          // ✅ 修复：404 错误静默处理（接口未实现）
          // 浏览器 DevTools 可能仍会显示 404（这是浏览器网络层的默认行为）
          // 但错误不会被记录到 analytics，也不影响用户体验
          // 数据已保存到本地存储，不影响功能
        } else {
          // 其他错误也静默处理，不记录日志
          // 数据已保存到本地存储，不影响功能
        }
      } catch (error) {
        // ✅ 修复：完全静默失败，不抛出错误，避免被 analytics 捕获
        // 数据已保存到本地存储，不影响用户体验
      }
      
      // 显示确认提示
      if (typeof CodeUtils !== 'undefined' && CodeUtils.showToast) {
        CodeUtils.showToast(feedback === 'like' ? '感谢您的反馈！' : '我们会改进回复质量', 'success', 2000);
      }
    } catch (error) {
      console.error('❌ 处理反馈失败:', error);
    }
  }

  /**
   * ✅ P2-2修复：显示排行榜
   * @param {string} type - 排行榜类型 ('level' 或 'experience')
   */
  async showLeaderboard(type = 'level') {
    // 获取当前用户数据
    const currentUser = this.getCurrentUserData();
    
    // ✅ 修复：先显示加载状态
    const panelHTML = `
      <div id="leaderboard-panel-overlay" style="
        position: fixed;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        background: rgba(0, 0, 0, 0.5);
        z-index: 10002;
        display: flex;
        align-items: center;
        justify-content: center;
      ">
        <div id="leaderboard-panel" style="
          background: linear-gradient(135deg, #1a1a2e 0%, #16213e 50%, #0f3460 100%);
          border-radius: 16px;
          padding: ${window.innerWidth <= 768 ? '20px' : '30px'};
          max-width: ${window.innerWidth <= 768 ? '95vw' : '600px'};
          width: ${window.innerWidth <= 768 ? '95vw' : 'auto'};
          max-height: 80vh;
          overflow-y: auto;
          box-shadow: 0 20px 60px rgba(0, 0, 0, 0.8);
          position: relative;
        ">
          <!-- 关闭按钮 -->
          <button id="leaderboard-panel-close" style="
            position: absolute;
            top: 15px;
            right: 15px;
            background: rgba(255,255,255,0.1);
            border: none;
            color: white;
            width: 30px;
            height: 30px;
            border-radius: 50%;
            cursor: pointer;
            font-size: 20px;
            display: flex;
            align-items: center;
            justify-content: center;
            transition: all 0.3s ease;
          ">×</button>
          
          <!-- 标题和切换 -->
          <div style="display: flex; justify-content: space-between; align-items: center; margin-bottom: 20px;">
            <h2 style="margin: 0; color: #ffd700; font-size: 24px;">
              🏆 排行榜
            </h2>
            <div style="display: flex; gap: 10px;">
              <button id="leaderboard-level-btn" style="
                background: ${type === 'level' ? 'rgba(255, 215, 0, 0.3)' : 'rgba(255,255,255,0.1)'};
                border: 1px solid ${type === 'level' ? '#ffd700' : 'rgba(255,255,255,0.2)'};
                color: white;
                padding: 6px 12px;
                border-radius: 6px;
                cursor: pointer;
                font-size: 12px;
              ">等级榜</button>
              <button id="leaderboard-exp-btn" style="
                background: ${type === 'experience' ? 'rgba(255, 215, 0, 0.3)' : 'rgba(255,255,255,0.1)'};
                border: 1px solid ${type === 'experience' ? '#ffd700' : 'rgba(255,255,255,0.2)'};
                color: white;
                padding: 6px 12px;
                border-radius: 6px;
                cursor: pointer;
                font-size: 12px;
              ">经验榜</button>
            </div>
          </div>
          
          <!-- 排行榜列表 -->
          <div id="leaderboard-list" style="
            display: flex;
            flex-direction: column;
            gap: 10px;
          ">
            <div style="text-align: center; color: rgba(255,255,255,0.6); padding: 20px;">
              ⏳ 正在加载排行榜数据...
            </div>
          </div>
          
          <!-- 当前用户排名 -->
          <div id="leaderboard-current-user" style="
            margin-top: 20px;
            padding: 15px;
            background: rgba(255, 215, 0, 0.1);
            border: 1px solid rgba(255, 215, 0, 0.3);
            border-radius: 12px;
          ">
            <div style="color: #ffd700; font-size: 14px; font-weight: 600; margin-bottom: 8px;">我的排名</div>
            <div style="display: flex; justify-content: space-between; align-items: center;">
              <span style="color: white; font-size: 16px; font-weight: 600;">
                加载中...
              </span>
              <span style="color: rgba(255,255,255,0.8); font-size: 14px;">
                ${type === 'level' ? `Lv.${this.formatNumber(currentUser.level, 9999)}` : `${this.formatNumber(currentUser.experience, 999999999)} 经验`}
              </span>
            </div>
          </div>
        </div>
      </div>
    `;
    
    // 移除旧面板
    const oldPanel = document.getElementById('leaderboard-panel-overlay');
    if (oldPanel) {
      oldPanel.remove();
    }
    
    // 添加新面板
    document.body.insertAdjacentHTML('beforeend', panelHTML);
    
    // ✅ 修复：异步获取排行榜数据
    try {
      const leaderboardData = await this.getLeaderboardData(type);
      
      // 更新排行榜列表
      const listElement = document.getElementById('leaderboard-list');
      if (listElement) {
        listElement.innerHTML = this.renderLeaderboardList(leaderboardData, currentUser, type);
      }
      
      // 更新当前用户排名
      const currentUserElement = document.getElementById('leaderboard-current-user');
      if (currentUserElement) {
        const currentUserInList = leaderboardData.find(u => u.isCurrentUser);
        const userRank = currentUserInList ? currentUserInList.rank : null;
        currentUserElement.innerHTML = `
          <div style="color: #ffd700; font-size: 14px; font-weight: 600; margin-bottom: 8px;">我的排名</div>
          <div style="display: flex; justify-content: space-between; align-items: center;">
            <span style="color: white; font-size: 16px; font-weight: 600;">
              ${userRank ? `第 ${userRank} 名` : '未上榜'}
            </span>
            <span style="color: rgba(255,255,255,0.8); font-size: 14px;">
              ${type === 'level' ? `Lv.${this.formatNumber(currentUser.level, 9999)}` : `${this.formatNumber(currentUser.experience, 999999999)} 经验`}
            </span>
          </div>
        `;
      }
    } catch (error) {
      console.error('❌ 加载排行榜数据失败:', error);
      const listElement = document.getElementById('leaderboard-list');
      if (listElement) {
        listElement.innerHTML = `
          <div style="text-align: center; color: rgba(255,255,255,0.6); padding: 20px;">
            ❌ 加载失败，请稍后重试
          </div>
        `;
      }
    }
    
    // 绑定事件
    const closeBtn = document.getElementById('leaderboard-panel-close');
    const levelBtn = document.getElementById('leaderboard-level-btn');
    const expBtn = document.getElementById('leaderboard-exp-btn');
    const overlay = document.getElementById('leaderboard-panel-overlay');
    
    if (closeBtn) {
      closeBtn.onclick = () => {
        if (overlay) overlay.remove();
      };
    }
    
    if (levelBtn) {
      levelBtn.onclick = () => {
        this.showLeaderboard('level');
      };
    }
    
    if (expBtn) {
      expBtn.onclick = () => {
        this.showLeaderboard('experience');
      };
    }
    
    if (overlay) {
      overlay.onclick = (e) => {
        if (e.target === overlay) {
          overlay.remove();
        }
      };
    }
  }

  /**
   * ✅ P2-2修复：获取当前用户数据
   */
  getCurrentUserData() {
    const expInfo = window.personalizationManager ? 
      window.personalizationManager.getExperienceInfo() : 
      { level: 1, total: 0, experience: 0 };
    
    const chatHistory = this.getChatHistory();
    
    return {
      level: expInfo.level || 1,
      experience: expInfo.total || expInfo.experience || 0,
      chatCount: chatHistory ? chatHistory.length : 0,
      rank: null // 将在排行榜中计算
    };
  }

  /**
   * ✅ P2-2修复：获取排行榜数据
   * @param {string} type - 排行榜类型
   */
  async getLeaderboardData(type) {
    try {
      // ✅ 修复：优先从后端获取真实排行榜数据
      if (window.apiClient) {
        try {
          const response = await window.apiClient.bjRequest('/api/leaderboard', {
            type: type
          });
          
          if (response.success && response.data && Array.isArray(response.data)) {
            const sessionId = CodeUtils?.safeGetStorage('session_id', '') || 
                              (typeof getOrInitSessionId === 'function' ? getOrInitSessionId() : '');
            
            // 标记当前用户
            const leaderboardData = response.data.map(user => ({
              ...user,
              isCurrentUser: user.session_id === sessionId
            }));
            
            // 保存到 localStorage（缓存）
            CodeUtils?.safeSetStorage('leaderboard_data', leaderboardData);
            
            debugLog('✅ 从后端获取排行榜数据成功', { 
              type, 
              count: leaderboardData.length,
              hasCurrentUser: leaderboardData.some(u => u.isCurrentUser)
            });
            
            return leaderboardData;
          }
        } catch (error) {
          console.error('❌ 从后端获取排行榜数据失败:', error);
        }
      }
    } catch (error) {
      console.error('❌ 获取排行榜数据异常:', error);
    }
    
    // 降级：从 localStorage 获取缓存数据
    const storedData = CodeUtils?.safeGetStorage('leaderboard_data', []);
    if (storedData.length > 0) {
      debugLog('📦 使用缓存的排行榜数据', { count: storedData.length });
      return storedData;
    }
    
    // 最后降级：生成示例数据（仅用于演示，当后端不可用时）
    const currentUser = this.getCurrentUserData();
    const sampleData = [];
    
    // 生成示例用户（仅用于演示）
    for (let i = 1; i <= 10; i++) {
      sampleData.push({
        rank: i,
        nickname: `用户${i}`,
        level: Math.max(1, currentUser.level + Math.floor(Math.random() * 5) - 2),
        experience: currentUser.experience + Math.floor(Math.random() * 500) - 250,
        avatar: '👤'
      });
    }
    
    // 添加当前用户
    sampleData.push({
      rank: 11,
      nickname: '我',
      level: currentUser.level,
      experience: currentUser.experience,
      avatar: '⭐',
      isCurrentUser: true
    });
    
    // 按类型排序
    sampleData.sort((a, b) => {
      if (type === 'level') {
        return b.level - a.level || b.experience - a.experience;
      } else {
        return b.experience - a.experience || b.level - a.level;
      }
    });
    
    // 更新排名
    sampleData.forEach((user, index) => {
      user.rank = index + 1;
      if (user.isCurrentUser) {
        currentUser.rank = index + 1;
      }
    });
    
    debugLog('⚠️ 使用示例数据（后端不可用）', { count: sampleData.length });
    return sampleData;
  }

  /**
   * ✅ 新增：格式化数字，避免科学计数法
   * @param {number|string} num - 要格式化的数字
   * @param {number} maxValue - 最大值限制
   * @returns {string} 格式化后的数字字符串
   */
  formatNumber(num, maxValue = 999999999) {
    if (num === null || num === undefined || num === '') {
      return '0';
    }
    
    // 如果是字符串，尝试转换为数字
    let numValue = typeof num === 'string' ? parseFloat(num) : num;
    
    // 验证是否为有效数字
    if (isNaN(numValue) || !isFinite(numValue)) {
      return '0';
    }
    
    // 限制最大值，防止异常数据
    if (numValue > maxValue) {
      numValue = maxValue;
    }
    
    // 如果是负数，设为0
    if (numValue < 0) {
      numValue = 0;
    }
    
    // 使用 toLocaleString 格式化，避免科学计数法
    // 对于大数字，使用中文格式（千分位分隔符）
    return Math.floor(numValue).toLocaleString('zh-CN');
  }

  /**
   * ✅ P2-2修复：渲染排行榜列表
   * @param {Array} data - 排行榜数据
   * @param {Object} currentUser - 当前用户数据
   * @param {string} type - 排行榜类型
   */
  renderLeaderboardList(data, currentUser, type) {
    if (!data || data.length === 0) {
      return '<div style="text-align: center; color: rgba(255,255,255,0.6); padding: 40px;">暂无排行榜数据</div>';
    }
    
    return data.slice(0, 10).map((user, index) => {
      const isCurrent = user.isCurrentUser || (user.nickname === '我');
      const medal = index < 3 ? ['🥇', '🥈', '🥉'][index] : '';
      
      return `
        <div style="
          display: flex;
          align-items: center;
          gap: 12px;
          padding: 12px;
          background: ${isCurrent ? 'rgba(255, 215, 0, 0.15)' : 'rgba(255,255,255,0.05)'};
          border: 1px solid ${isCurrent ? 'rgba(255, 215, 0, 0.3)' : 'rgba(255,255,255,0.1)'};
          border-radius: 8px;
          ${isCurrent ? 'box-shadow: 0 0 10px rgba(255, 215, 0, 0.3);' : ''}
        ">
          <div style="
            width: 30px;
            text-align: center;
            font-size: 18px;
            font-weight: 600;
            color: ${index < 3 ? '#ffd700' : 'rgba(255,255,255,0.8)'};
          ">${medal || user.rank}</div>
          <div style="font-size: 24px;">${user.avatar || '👤'}</div>
          <div style="flex: 1;">
            <div style="color: ${isCurrent ? '#ffd700' : 'white'}; font-size: 14px; font-weight: 600;">
              ${user.nickname}${isCurrent ? ' (我)' : ''}
            </div>
            <div style="color: rgba(255,255,255,0.6); font-size: 12px; margin-top: 2px;">
              ${type === 'level' ? `Lv.${this.formatNumber(user.level, 9999)}` : `${this.formatNumber(user.experience, 999999999)} 经验`}
            </div>
          </div>
        </div>
      `;
    }).join('');
  }

  /**
   * 显示成就获得提示
   * @param {Object} achievement - 成就对象
   */
  showAchievement(achievement) {
    if (!achievement) return;
    
    // 创建成就提示元素
    const achievementElement = document.createElement('div');
    achievementElement.className = 'achievement-notification';
    achievementElement.innerHTML = `
      <div class="achievement-content">
        <div class="achievement-icon">${achievement.icon}</div>
        <div class="achievement-text">
          <div class="achievement-title">🎉 获得成就</div>
          <div class="achievement-name">${achievement.name}</div>
          <div class="achievement-description">${achievement.description}</div>
        </div>
      </div>
    `;
    
    // 添加样式（如果还没有）
    this.addAchievementStyles();
    
    // 添加到页面
    document.body.appendChild(achievementElement);
    
    // 播放动画
    setTimeout(() => {
      achievementElement.classList.add('show');
    }, 100);
    
    // 播放音效（如果可用）
    this.playAchievementSound();
    
    // 自动消失
    setTimeout(() => {
      achievementElement.classList.add('hide');
      setTimeout(() => {
        if (achievementElement.parentNode) {
          achievementElement.parentNode.removeChild(achievementElement);
        }
      }, 500);
    }, 3000);
    
    // ✅ 新增：使用统一的消息显示系统（会自动判断聊天界面状态）
    this.showMessageBubble(
      `🎉 获得成就：${achievement.name}！${achievement.description}`,
      'achievement'
    );
    
    // 触发自定义事件
    const event = new CustomEvent('achievementUnlocked', {
      detail: { achievement: achievement }
    });
    document.dispatchEvent(event);
  }

  /**
   * 添加成就提示样式
   */
  addAchievementStyles() {
    if (document.getElementById('achievement-styles')) {
      return;
    }
    
    const style = document.createElement('style');
    style.id = 'achievement-styles';
    style.textContent = `
      .achievement-notification {
        position: fixed;
        top: 20px;
        right: 20px;
        background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
        color: white;
        padding: 20px;
        border-radius: 12px;
        box-shadow: 0 10px 30px rgba(0, 0, 0, 0.3);
        z-index: 10000;
        min-width: 300px;
        max-width: 400px;
        opacity: 0;
        transform: translateX(400px);
        transition: all 0.5s cubic-bezier(0.68, -0.55, 0.265, 1.55);
      }
      
      .achievement-notification.show {
        opacity: 1;
        transform: translateX(0);
      }
      
      .achievement-notification.hide {
        opacity: 0;
        transform: translateX(400px);
      }
      
      .achievement-content {
        display: flex;
        align-items: center;
        gap: 15px;
      }
      
      .achievement-icon {
        font-size: 48px;
        line-height: 1;
        animation: achievement-bounce 0.6s ease-out;
      }
      
      @keyframes achievement-bounce {
        0%, 100% { transform: scale(1); }
        50% { transform: scale(1.2); }
      }
      
      .achievement-text {
        flex: 1;
      }
      
      .achievement-title {
        font-size: 14px;
        opacity: 0.9;
        margin-bottom: 5px;
      }
      
      .achievement-name {
        font-size: 20px;
        font-weight: bold;
        margin-bottom: 5px;
      }
      
      .achievement-description {
        font-size: 14px;
        opacity: 0.8;
      }
    `;
    
    document.head.appendChild(style);
  }

  /**
   * 播放成就音效
   */
  playAchievementSound() {
    // 使用 Web Audio API 播放简单的音效（可选）
    try {
      const audioContext = new (window.AudioContext || window.webkitAudioContext)();
      const oscillator = audioContext.createOscillator();
      const gainNode = audioContext.createGain();
      
      oscillator.connect(gainNode);
      gainNode.connect(audioContext.destination);
      
      oscillator.frequency.value = 800;
      oscillator.type = 'sine';
      
      gainNode.gain.setValueAtTime(0.3, audioContext.currentTime);
      gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.3);
      
      oscillator.start(audioContext.currentTime);
      oscillator.stop(audioContext.currentTime + 0.3);
    } catch (error) {
      // 音效播放失败，忽略
      console.log('音效播放失败:', error);
    }
  }

  /**
   * 获取已获得的成就列表
   */
  getAchievements() {
    return this.achievements.map(id => this.achievementDefinitions[id]).filter(Boolean);
  }

  /**
   * 检查特定成就是否已获得
   * @param {string} achievementId - 成就ID
   */
  hasAchievement(achievementId) {
    return this.achievements.includes(achievementId);
  }

  /**
   * 加载随机奖励历史
   */
  loadRandomRewardHistory() {
    this.randomRewards.rewardHistory = CodeUtils.safeGetStorage('random_reward_history', []);
    this.randomRewards.lastRewardTime = CodeUtils.safeGetStorage('last_random_reward_time', null);
    console.log(`🎁 已加载 ${this.randomRewards.rewardHistory.length} 个随机奖励记录`);
  }

  /**
   * 检查并触发随机奖励
   */
  checkRandomRewards() {
    if (!this.randomRewards.enabled) {
      return;
    }
    
    // 检查冷却时间
    const now = Date.now();
    const lastTime = this.randomRewards.lastRewardTime;
    
    if (lastTime && (now - lastTime) < this.randomRewards.rewardCooldown) {
      // 还在冷却中
      return;
    }
    
    // 检查是否满足触发条件（15% 概率）
    if (Math.random() > this.randomRewards.triggerChance) {
      return;
    }
    
    // 检查特殊日子
    const specialReward = this.checkSpecialDay();
    if (specialReward) {
      this.triggerRandomReward(specialReward);
      return;
    }
    
    // 随机选择一个奖励类型
    const rewardTypes = Object.keys(this.rewardDefinitions).filter(key => {
      // 排除特殊日子奖励（已单独处理）
      return key !== 'special_day_content';
    });
    
    if (rewardTypes.length === 0) {
      return;
    }
    
    const randomType = rewardTypes[Math.floor(Math.random() * rewardTypes.length)];
    const rewardDef = this.rewardDefinitions[randomType];
    
    this.triggerRandomReward(rewardDef);
  }

  /**
   * 检查特殊日子
   */
  checkSpecialDay() {
    const now = new Date();
    const month = String(now.getMonth() + 1).padStart(2, '0');
    const day = String(now.getDate()).padStart(2, '0');
    const dateKey = `${month}-${day}`;
    
    if (this.rewardDefinitions.special_day_content.messages[dateKey]) {
      return {
        ...this.rewardDefinitions.special_day_content,
        message: this.rewardDefinitions.special_day_content.messages[dateKey],
        dateKey: dateKey
      };
    }
    
    return null;
  }

  /**
   * 触发随机奖励
   * @param {Object} rewardDef - 奖励定义
   */
  triggerRandomReward(rewardDef) {
    if (!rewardDef) return;
    
    const now = Date.now();
    this.randomRewards.lastRewardTime = now;
    CodeUtils.safeSetStorage('last_random_reward_time', now);
    
    // 记录奖励历史
    const rewardRecord = {
      id: rewardDef.id,
      type: rewardDef.type,
      timestamp: now,
      date: new Date().toISOString().split('T')[0]
    };
    
    this.randomRewards.rewardHistory.push(rewardRecord);
    // 只保留最近30天的记录
    if (this.randomRewards.rewardHistory.length > 30) {
      this.randomRewards.rewardHistory.shift();
    }
    CodeUtils.safeSetStorage('random_reward_history', this.randomRewards.rewardHistory);
    
    // 根据奖励类型处理
    switch (rewardDef.type) {
      case 'message':
      case 'prophecy':
      case 'special':
        this.showRandomRewardMessage(rewardDef);
        break;
        
      case 'title':
        this.grantSpecialTitle(rewardDef);
        break;
        
      case 'feature':
        this.unlockHiddenFeature(rewardDef);
        break;
        
      case 'experience':
        this.grantBonusExperience(rewardDef);
        break;
    }
    
    console.log(`🎁 触发随机奖励: ${rewardDef.name}`);
  }

  /**
   * 显示随机奖励消息
   * @param {Object} rewardDef - 奖励定义
   */
  showRandomRewardMessage(rewardDef) {
    let message = '';
    
    if (rewardDef.type === 'special' && rewardDef.message) {
      message = rewardDef.message;
    } else if (rewardDef.messages) {
      message = rewardDef.messages[Math.floor(Math.random() * rewardDef.messages.length)];
    }
    
    if (!message) return;
    
    // 创建消息元素
    const messageElement = document.createElement('div');
    messageElement.className = 'random-reward-message';
    messageElement.innerHTML = `
      <div class="reward-message-content">
        <div class="reward-icon">${rewardDef.icon}</div>
        <div class="reward-message-text">
          <div class="reward-title">${rewardDef.name}</div>
          <div class="reward-message">${message}</div>
        </div>
      </div>
    `;
    
    this.addRandomRewardStyles();
    document.body.appendChild(messageElement);
    
    setTimeout(() => {
      messageElement.classList.add('show');
    }, 100);
    
    // 自动消失
    setTimeout(() => {
      messageElement.classList.add('hide');
      setTimeout(() => {
        if (messageElement.parentNode) {
          messageElement.parentNode.removeChild(messageElement);
        }
      }, 500);
    }, 5000);
    
    // ✅ 新增：使用统一的消息显示系统（会自动判断聊天界面状态）
    this.showMessageBubble(
      `🎁 ${rewardDef.name}：${rewardDef.description || message}`,
      'reward'
    );
  }

  /**
   * 授予特殊称号
   * @param {Object} rewardDef - 奖励定义
   */
  grantSpecialTitle(rewardDef) {
    const titles = rewardDef.titles || [];
    if (titles.length === 0) return;
    
    const randomTitle = titles[Math.floor(Math.random() * titles.length)];
    
    // 保存称号
    const userTitles = CodeUtils.safeGetStorage('user_special_titles', []);
    if (!userTitles.find(t => t.id === randomTitle.name)) {
      userTitles.push({
        id: randomTitle.name,
        name: randomTitle.name,
        description: randomTitle.description,
        timestamp: Date.now()
      });
      CodeUtils.safeSetStorage('user_special_titles', userTitles);
    }
    
    // 显示提示
    this.showRandomRewardMessage({
      ...rewardDef,
      messages: [`🎉 恭喜！你获得了特殊称号："${randomTitle.name}"\n${randomTitle.description}`]
    });
  }

  /**
   * 解锁隐藏功能
   * @param {Object} rewardDef - 奖励定义
   */
  unlockHiddenFeature(rewardDef) {
    const features = rewardDef.features || [];
    if (features.length === 0) return;
    
    const randomFeature = features[Math.floor(Math.random() * features.length)];
    
    // 保存解锁的功能
    const unlockedFeatures = CodeUtils.safeGetStorage('unlocked_features', []);
    if (!unlockedFeatures.includes(randomFeature.name)) {
      unlockedFeatures.push(randomFeature.name);
      CodeUtils.safeSetStorage('unlocked_features', unlockedFeatures);
    }
    
    // 显示提示
    this.showRandomRewardMessage({
      ...rewardDef,
      messages: [`🎁 恭喜！你解锁了隐藏功能："${randomFeature.name}"\n${randomFeature.description}`]
    });
  }

  /**
   * 授予额外经验值
   * @param {Object} rewardDef - 奖励定义
   */
  grantBonusExperience(rewardDef) {
    const points = rewardDef.points || [50];
    const randomPoints = points[Math.floor(Math.random() * points.length)];
    
    if (window.personalizationManager) {
      window.personalizationManager.addExperience(randomPoints, '随机奖励');
      
      // ✅ 优化：实时更新成长进度显示
      if (window.soulmateManager && typeof window.soulmateManager.updateGrowthProgressDisplay === 'function') {
        window.soulmateManager.updateGrowthProgressDisplay();
      }
    }
    
    // 显示提示
    if (typeof CodeUtils !== 'undefined' && CodeUtils.showToast) {
      CodeUtils.showToast(`⭐ 获得随机奖励：${randomPoints} 经验值！`, 'success', 3000);
    }
  }

  /**
   * 添加随机奖励样式
   */
  addRandomRewardStyles() {
    if (document.getElementById('random-reward-styles')) {
      return;
    }
    
    const style = document.createElement('style');
    style.id = 'random-reward-styles';
    style.textContent = `
      .random-reward-message {
        position: fixed;
        top: 50%;
        left: 50%;
        transform: translate(-50%, -50%) scale(0.8);
        background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
        color: white;
        padding: 30px;
        border-radius: 16px;
        box-shadow: 0 20px 60px rgba(0, 0, 0, 0.4);
        z-index: 10001;
        min-width: 350px;
        max-width: 500px;
        opacity: 0;
        transition: all 0.5s cubic-bezier(0.68, -0.55, 0.265, 1.55);
      }
      
      .random-reward-message.show {
        opacity: 1;
        transform: translate(-50%, -50%) scale(1);
      }
      
      .random-reward-message.hide {
        opacity: 0;
        transform: translate(-50%, -50%) scale(0.8);
      }
      
      .reward-message-content {
        display: flex;
        align-items: center;
        gap: 20px;
      }
      
      .reward-icon {
        font-size: 64px;
        line-height: 1;
        animation: reward-pulse 1s ease-in-out infinite;
      }
      
      @keyframes reward-pulse {
        0%, 100% { transform: scale(1); }
        50% { transform: scale(1.1); }
      }
      
      .reward-message-text {
        flex: 1;
      }
      
      .reward-title {
        font-size: 18px;
        font-weight: bold;
        margin-bottom: 10px;
        opacity: 0.9;
      }
      
      .reward-message {
        font-size: 16px;
        line-height: 1.6;
        white-space: pre-line;
      }
    `;
    
    document.head.appendChild(style);
  }

  /**
   * 检查聊天界面是否打开
   */
  isChatOpen() {
    const chatInterface = document.getElementById('soulmate-chat-interface');
    return chatInterface && chatInterface.classList.contains('active');
  }

  /**
   * 显示消息气泡（在小人按钮上）
   * @param {string} message - 消息内容
   * @param {string} type - 消息类型 (growth, reward, care, achievement, guidance, healing)
   */
  showMessageBubble(message, type = 'info') {
    // 如果聊天界面打开，直接显示在聊天界面
    if (this.isChatOpen()) {
      // 如果正在打字，加入队列等待
      if (this.isTyping) {
        this.messageQueue.push({ 
          role: 'assistant', 
          content: message, 
          animate: true, 
          type: type 
        });
        return;
      }
      // 立即在聊天界面显示
      this.addChatMessage('assistant', message, true, type);
      return;
    }
    
    // 聊天界面关闭，显示在小人按钮上
    // 添加到未读消息队列
    this.unreadMessages.push({ message, type, timestamp: Date.now() });
    this.unreadMessageCount = this.unreadMessages.length;
    
    // 更新小人按钮气泡显示
    this.updateChatToggleBubble();
  }

  /**
   * 更新小人按钮气泡显示
   */
  updateChatToggleBubble() {
    const toggleContainer = document.getElementById('soulmate-chat-toggle-container');
    if (!toggleContainer) return;
    
    // 移除旧的气泡（如果存在）
    const oldBubble = toggleContainer.querySelector('.message-bubble');
    if (oldBubble) {
      oldBubble.remove();
    }
    
    // 如果没有未读消息，不显示气泡
    if (this.unreadMessageCount === 0) {
      return;
    }
    
    // 获取最新消息
    const latestMessage = this.unreadMessages[this.unreadMessages.length - 1];
    const messageTypeInfo = this.getMessageTypeInfo(latestMessage.type);
    
    // 创建气泡元素
    const bubble = document.createElement('div');
    bubble.className = 'message-bubble';
    bubble.style.cssText = `
      position: absolute;
      bottom: 70px;
      right: 0;
      min-width: 200px;
      max-width: 280px;
      background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
      color: white;
      padding: 12px 16px;
      border-radius: 12px;
      box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
      z-index: 10000;
      font-size: 13px;
      line-height: 1.4;
      animation: bubbleSlideIn 0.3s ease-out;
      pointer-events: auto;
    `;
    
    // 消息类型图标和内容
    bubble.innerHTML = `
      <div style="display: flex; align-items: center; gap: 8px; margin-bottom: 6px;">
        <span style="font-size: 16px;">${messageTypeInfo.icon}</span>
        <span style="font-weight: 600; font-size: 12px; opacity: 0.9;">${messageTypeInfo.label}</span>
        ${this.unreadMessageCount > 1 ? `<span style="margin-left: auto; background: rgba(255,255,255,0.3); padding: 2px 8px; border-radius: 10px; font-size: 11px;">${this.unreadMessageCount}条</span>` : ''}
      </div>
      <div style="opacity: 0.95;">${latestMessage.message}</div>
      <div style="position: absolute; bottom: -8px; right: 20px; width: 0; height: 0; border-left: 8px solid transparent; border-right: 8px solid transparent; border-top: 8px solid #764ba2;"></div>
    `;
    
    // 添加到容器
    toggleContainer.appendChild(bubble);
    
    // 添加气泡样式（如果还没有）
    this.addMessageBubbleStyles();
  }

  /**
   * 获取消息类型信息
   * @param {string} type - 消息类型
   * @returns {Object} 类型信息
   */
  getMessageTypeInfo(type) {
    const typeMap = {
      'growth': { icon: '✨', label: '成长' },
      'reward': { icon: '🎁', label: '奖励' },
      'care': { icon: '💝', label: '关怀' },
      'achievement': { icon: '🏆', label: '成就' },
      'guidance': { icon: '🔮', label: '指导' },
      'healing': { icon: '💚', label: '疗愈' },
      'info': { icon: '💬', label: '消息' }
    };
    return typeMap[type] || typeMap['info'];
  }

  /**
   * 格式化系统消息
   * @param {string} type - 消息类型
   * @param {string} content - 消息内容
   * @returns {string} 格式化后的消息
   */
  formatSystemMessage(type, content) {
    const typeInfo = this.getMessageTypeInfo(type);
    return `${typeInfo.icon} ${typeInfo.label}：${content}`;
  }

  /**
   * 添加消息气泡样式
   */
  addMessageBubbleStyles() {
    if (document.getElementById('message-bubble-styles')) return;
    
    const style = document.createElement('style');
    style.id = 'message-bubble-styles';
    style.textContent = `
      @keyframes bubbleSlideIn {
        from {
          opacity: 0;
          transform: translateY(10px);
        }
        to {
          opacity: 1;
          transform: translateY(0);
        }
      }
      
      .message-bubble {
        animation: bubbleSlideIn 0.3s ease-out;
      }
    `;
    document.head.appendChild(style);
  }

  /**
   * 更新头像表情
   * @param {string} emotion - 情绪状态：idle/thinking/understanding/caring/speaking
   */
  updateAvatarExpression(emotion = 'idle') {
    const avatarElement = document.getElementById('soulmate-chat-avatar');
    if (!avatarElement) return;
    
    // 移除所有状态类
    avatarElement.classList.remove('blinking', 'thinking', 'nodding', 'smiling');
    this.visualSystem.avatarState = emotion;
    
    const emojiElement = document.getElementById('avatar-emoji');
    if (!emojiElement) return;
    
    // 根据情绪更新表情
    switch (emotion) {
      case 'thinking':
        avatarElement.classList.add('thinking');
        emojiElement.textContent = '🤔';
        break;
      case 'understanding':
        avatarElement.classList.add('nodding');
        emojiElement.textContent = '😌';
        break;
      case 'caring':
        avatarElement.classList.add('smiling');
        emojiElement.textContent = '💝';
        break;
      case 'speaking':
        emojiElement.textContent = '🗣️';
        break;
      default:
        emojiElement.textContent = '🧙';
    }
    
    // 记录状态变化
    console.log(`🎭 头像表情更新: ${emotion}`);
  }

  /**
   * 播放头像动画
   * @param {string} action - 动作：blink/think/nod/smile
   */
  animateAvatar(action) {
    const avatarElement = document.getElementById('soulmate-chat-avatar');
    if (!avatarElement) return;
    
    switch (action) {
      case 'blink':
        avatarElement.classList.add('blinking');
        setTimeout(() => {
          avatarElement.classList.remove('blinking');
        }, 300);
        break;
        
      case 'think':
        this.updateAvatarExpression('thinking');
        break;
        
      case 'nod':
        avatarElement.classList.add('nodding');
        setTimeout(() => {
          avatarElement.classList.remove('nodding');
          this.updateAvatarExpression('idle');
        }, 600);
        break;
        
      case 'smile':
        avatarElement.classList.add('smiling');
        setTimeout(() => {
          avatarElement.classList.remove('smiling');
          this.updateAvatarExpression('idle');
        }, 500);
        break;
    }
  }

  /**
   * 启动头像自动眨眼
   */
  startAvatarBlinking() {
    // 清除之前的定时器
    if (this.blinkTimer) {
      clearInterval(this.blinkTimer);
    }
    
    // 每3秒眨眼一次
    this.blinkTimer = setInterval(() => {
      if (this.visualSystem.avatarState === 'idle') {
        this.animateAvatar('blink');
      }
    }, this.visualSystem.blinkInterval);
  }

  /**
   * 停止头像眨眼
   */
  stopAvatarBlinking() {
    if (this.blinkTimer) {
      clearInterval(this.blinkTimer);
      this.blinkTimer = null;
    }
  }

  /**
   * 增强打字效果（带停顿和音效）
   * @param {HTMLElement} element - 消息元素
   * @param {string} text - 要显示的文字
   * @param {Function} onComplete - 完成回调
   */
  enhancedTypingEffect(element, text, onComplete) {
    if (!element || !text) return;
    
    let index = 0;
    let isPaused = false;
    
    // 更新头像为思考状态
    this.updateAvatarExpression('thinking');
    
    const typeChar = () => {
      if (index >= text.length) {
        // 打字完成
        this.updateAvatarExpression('idle');
        if (onComplete) onComplete();
        
        // 播放完成音效
        this.playMessageSound();
        return;
      }
      
      // ✅ 修复：在每次打字字符时播放声音（每5个字符播放一次，避免音效重叠）
      if (index % 5 === 0 && this.visualSystem.typingSoundEnabled) {
        this.playTypingSound();
      }
      
      // 偶尔停顿（模拟思考）
      if (Math.random() < 0.05 && index > 10) {
        // 5% 概率停顿，停顿时间随机
        const pauseTime = 200 + Math.random() * 300;
        setTimeout(() => {
          typeChar();
        }, pauseTime);
        return;
      }
      
      // 显示下一个字符
      element.textContent = text.slice(0, index + 1);
      index++;
      
      // ✅ 新增：打字过程中持续滚动到底部（每10个字符滚动一次，避免过于频繁）
      if (index % 10 === 0 || index === text.length) {
        const messagesContainer = document.getElementById('soulmate-chat-messages');
        if (messagesContainer) {
          this.scrollToBottom(messagesContainer, false);  // 打字过程中使用立即滚动，更流畅
        }
      }
      
      // 根据内容调整打字速度（标点符号后稍慢）
      const char = text[index - 1];
      const delay = (char === '。' || char === '！' || char === '？' || char === '.' || char === '!' || char === '?') 
        ? 80 
        : (char === '，' || char === ',' || char === '；' || char === ';') 
          ? 50 
          : 30;
      
      setTimeout(typeChar, delay);
    };
    
    // 开始打字
    setTimeout(() => {
      typeChar();
    }, 300);
  }

  /**
   * 滚动消息容器到底部
   * @param {HTMLElement} container - 消息容器元素
   * @param {boolean} smooth - 是否平滑滚动（默认true）
   */
  scrollToBottom(container, smooth = true) {
    if (!container) return;
    
    // 使用 requestAnimationFrame 确保DOM更新后再滚动
    requestAnimationFrame(() => {
      if (smooth) {
        // 平滑滚动
        container.scrollTo({
          top: container.scrollHeight,
          behavior: 'smooth'
        });
      } else {
        // 立即滚动
        container.scrollTop = container.scrollHeight;
      }
    });
  }

  /**
   * 播放打字音效
   */
  playTypingSound() {
    if (!this.visualSystem.typingSoundEnabled) return;
    
    // ✅ 修复：统一使用主程序的打字声音函数（避免代码重复和资源浪费）
    if (typeof window.playTypingSound === 'function') {
      window.playTypingSound();
      return;
    }
    
    // 降级方案：使用现有的实现（如果主程序函数不可用）
    // 优先使用音效文件
    if (this.playSoundFile(this.soundFiles.typing)) {
      return;
    }
    
    // 如果没有文件，使用改进的合成音效（更真实的打字声）
    try {
      const audioContext = new (window.AudioContext || window.webkitAudioContext)();
      
      // 创建多个振荡器模拟打字机的咔嗒声
      for (let i = 0; i < 2; i++) {
        setTimeout(() => {
          const oscillator = audioContext.createOscillator();
          const gainNode = audioContext.createGain();
          
          oscillator.connect(gainNode);
          gainNode.connect(audioContext.destination);
          
          // 更真实的打字音效：频率稍高，带颤音
          oscillator.frequency.value = 1000 + Math.random() * 200;
          oscillator.type = 'square';  // 方波听起来更像打字机
          
          gainNode.gain.setValueAtTime(0.03, audioContext.currentTime);
          gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.03);
          
          oscillator.start(audioContext.currentTime);
          oscillator.stop(audioContext.currentTime + 0.03);
        }, i * 10);
      }
    } catch (error) {
      // 音效播放失败，忽略
    }
  }

  /**
   * 播放消息提示音
   */
  playMessageSound() {
    if (!this.visualSystem.messageSoundEnabled) return;
    
    // 优先使用音效文件
    if (this.playSoundFile(this.soundFiles.message)) {
      return;
    }
    
    // 如果没有文件，使用改进的合成音效（更柔和的提示音）
    try {
      const audioContext = new (window.AudioContext || window.webkitAudioContext)();
      
      // 创建上升的音调（更友好）
      const notes = [500, 650];
      notes.forEach((freq, i) => {
        setTimeout(() => {
          const oscillator = audioContext.createOscillator();
          const gainNode = audioContext.createGain();
          
          oscillator.connect(gainNode);
          gainNode.connect(audioContext.destination);
          
          oscillator.frequency.value = freq;
          oscillator.type = 'sine';
          
          gainNode.gain.setValueAtTime(0.08, audioContext.currentTime);
          gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.15);
          
          oscillator.start(audioContext.currentTime);
          oscillator.stop(audioContext.currentTime + 0.15);
        }, i * 80);
      });
    } catch (error) {
      // 音效播放失败，忽略
    }
  }

  /**
   * 播放升级庆祝音效
   */
  playLevelUpSound() {
    // 优先使用音效文件
    if (this.playSoundFile(this.soundFiles.levelup)) {
      return;
    }
    
    // 如果没有文件，使用改进的合成音效（更丰富的庆祝音效）
    try {
      const audioContext = new (window.AudioContext || window.webkitAudioContext)();
      
      // 播放更丰富的上升音阶
      const notes = [400, 500, 600, 700, 800, 900];
      notes.forEach((freq, i) => {
        setTimeout(() => {
          const oscillator = audioContext.createOscillator();
          const gainNode = audioContext.createGain();
          
          oscillator.connect(gainNode);
          gainNode.connect(audioContext.destination);
          
          oscillator.frequency.value = freq;
          oscillator.type = 'sine';
          
          gainNode.gain.setValueAtTime(0.12, audioContext.currentTime);
          gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.2);
          
          oscillator.start(audioContext.currentTime);
          oscillator.stop(audioContext.currentTime + 0.2);
        }, i * 80);
      });
    } catch (error) {
      // 音效播放失败，忽略
    }
  }

  /**
   * 播放音效文件（如果存在）
   * @param {string} url - 音效文件URL
   * @returns {boolean} - 是否成功播放（或文件已缓存）
   */
  playSoundFile(url) {
    if (!url) return false;
    
    try {
      // 如果已经缓存，直接使用
      if (this.soundCache[url]) {
        try {
          const audio = this.soundCache[url].cloneNode();
          audio.volume = 0.3;  // 音量30%
          audio.play().catch(e => {
            // 播放失败，忽略（可能是用户未交互或浏览器限制）
          });
          return true;
        } catch (e) {
          // 克隆失败，使用原音频
          this.soundCache[url].currentTime = 0;
          this.soundCache[url].volume = 0.3;
          this.soundCache[url].play().catch(e => {
            // 播放失败，忽略
          });
          return true;
        }
      }
      
      // 如果没有缓存，检查是否正在加载中（在预加载中）
      // 如果文件存在但还没加载完，等待加载完成
      // 但这里我们使用简单的同步检查，如果缓存中没有，就返回false使用合成音效
      return false;
    } catch (error) {
      // 出错，使用合成音效
      return false;
    }
  }

  /**
   * 提供情感疗愈支持
   * @param {Object} userState - 用户状态对象
   * @returns {string} 疗愈消息
   */
  provideEmotionalHealing(userState) {
    const healingMessages = {
      // 情感共鸣类
      empathy: [
        "我理解你的感受，这很正常。每个人都会有这样的时候。",
        "你的感受我完全理解，让我来帮你分析一下。",
        "我感受到了你内心的波动，这些情绪都是真实的，值得被倾听。",
        "你的感受很重要，不要压抑它。在我面前，你可以完全真实。",
        "我能感受到你内心的挣扎，这不容易。但你不是一个人。"
      ],
      
      // 安慰支持类
      comfort: [
        "我在这里陪着你，你不是一个人。",
        "无论你经历了什么，我都会在这里支持你。",
        "相信我，一切都会好起来的。",
        "你的坚强让我很感动，但在我面前你可以脆弱。",
        "哭出来吧，在我面前你可以完全释放。"
      ],
      
      // 理性分析类
      analysis: [
        "你的想法很有道理，让我来分析一下。",
        "从你的描述来看，我看到了问题的核心。",
        "让我帮你理清思路，找到解决方案。",
        "从命理角度来看，这个阶段对你来说很重要。",
        "根据你的面相特征，我看到了你内心的坚韧。"
      ],
      
      // 成长鼓励类
      encouragement: [
        "我看到你在成长，比之前更成熟了。",
        "你正在朝着正确的方向前进，保持下去。",
        "你的进步让我为你感到骄傲。",
        "我相信你会变得更好，你的潜力很大。",
        "从你的成长轨迹来看，你的未来会很精彩。"
      ]
    };
    
    // 根据用户状态选择合适的疗愈消息
    let category = 'empathy';
    
    if (userState.emotion === 'sad' || userState.emotion === 'depressed') {
      category = 'comfort';
    } else if (userState.needsAnalysis) {
      category = 'analysis';
    } else if (userState.showingGrowth) {
      category = 'encouragement';
    }
    
    const messages = healingMessages[category];
    const selectedMessage = messages[Math.floor(Math.random() * messages.length)];
    
    // 记录疗愈时刻
    this.recordHealingMoment(category, userState);
    
    return selectedMessage;
  }

  /**
   * 分析用户心理状态
   * @param {Array} conversation - 对话历史
   * @returns {Object} 心理状态分析结果
   */
  analyzePsychologicalState(conversation) {
    const analysis = {
      emotionalState: 'neutral',  // neutral/sad/anxious/happy/excited
      needsSupport: false,
      showingGrowth: false,
      hiddenNeeds: [],
      suggestedResponse: 'empathy'
    };
    
    if (!conversation || conversation.length === 0) {
      return analysis;
    }
    
    // 分析最近的对话（最近5条）
    const recentMessages = conversation.slice(-5);
    
    // 检测负面情绪关键词
    const negativeKeywords = ['难过', '痛苦', '焦虑', '担心', '害怕', '迷茫', '无助', '孤独', '失望', '绝望'];
    const positiveKeywords = ['开心', '高兴', '兴奋', '期待', '希望', '自信', '满足', '幸福'];
    
    let negativeCount = 0;
    let positiveCount = 0;
    let allContent = '';
    
    recentMessages.forEach(msg => {
      const content = (msg.content || msg.message || '').toLowerCase();
      allContent += content;
      negativeKeywords.forEach(keyword => {
        if (content.includes(keyword)) negativeCount++;
      });
      positiveKeywords.forEach(keyword => {
        if (content.includes(keyword)) positiveCount++;
      });
    });
    
    // 判断情感状态
    if (negativeCount > positiveCount + 2) {
      analysis.emotionalState = 'sad';
      analysis.needsSupport = true;
      analysis.suggestedResponse = 'comfort';
    } else if (positiveCount > negativeCount) {
      analysis.emotionalState = 'happy';
      analysis.suggestedResponse = 'encouragement';
    } else if (negativeCount > 0) {
      analysis.emotionalState = 'anxious';
      analysis.needsSupport = true;
      analysis.suggestedResponse = 'analysis';
    }
    
    // 检测成长迹象（与历史对话对比）
    const olderMessages = conversation.slice(0, -5);
    if (olderMessages.length > 0) {
      const oldNegativeCount = olderMessages.reduce((count, msg) => {
        const content = (msg.content || msg.message || '').toLowerCase();
        return count + negativeKeywords.filter(k => content.includes(k)).length;
      }, 0);
      
      if (oldNegativeCount > negativeCount) {
        analysis.showingGrowth = true;
        analysis.suggestedResponse = 'encouragement';
      }
    }
    
    // 检测隐藏需求（通过关键词和上下文）
    if (allContent.includes('工作') || allContent.includes('事业')) {
      analysis.hiddenNeeds.push('career_guidance');
    }
    if (allContent.includes('感情') || allContent.includes('恋爱') || allContent.includes('分手')) {
      analysis.hiddenNeeds.push('love_guidance');
    }
    if (allContent.includes('健康') || allContent.includes('身体')) {
      analysis.hiddenNeeds.push('health_guidance');
    }
    
    return analysis;
  }

  /**
   * 记录用户成长
   */
  recordUserGrowth() {
    const growthRecord = {
      timestamp: new Date().toISOString(),
      date: new Date().toISOString().split('T')[0],
      level: window.personalizationManager ? window.personalizationManager.level : 1,
      experiencePoints: window.personalizationManager ? window.personalizationManager.experiencePoints : 0,
      conversationCount: this.getChatHistory() ? this.getChatHistory().length : 0,
      deepConversationCount: Number(CodeUtils.safeGetStorage('deep_chat_count', 0)) || 0,
      achievements: window.soulmateManager ? window.soulmateManager.achievements.length : 0
    };
    
    this.healingSystem.growthRecords.push(growthRecord);
    
    // 只保留最近30天的记录
    if (this.healingSystem.growthRecords.length > 30) {
      this.healingSystem.growthRecords.shift();
    }
    
    // 保存到 localStorage
    CodeUtils.safeSetStorage('user_growth_records', this.healingSystem.growthRecords);
    
    // 检查是否有显著成长
    this.checkSignificantGrowth();
  }

  /**
   * 检查是否有显著成长
   */
  checkSignificantGrowth() {
    if (this.healingSystem.growthRecords.length < 2) return;
    
    const recent = this.healingSystem.growthRecords[this.healingSystem.growthRecords.length - 1];
    const previous = this.healingSystem.growthRecords[this.healingSystem.growthRecords.length - 2];
    
    // 检测成长点
    const growthPoints = [];
    
    if (recent.level > previous.level) {
      growthPoints.push('level_up');
    }
    if (recent.experiencePoints > previous.experiencePoints + 50) {
      growthPoints.push('experience_gain');
    }
    if (recent.deepConversationCount > previous.deepConversationCount) {
      growthPoints.push('deep_conversation');
    }
    if (recent.achievements > previous.achievements) {
      growthPoints.push('new_achievement');
    }
    
    if (growthPoints.length > 0) {
      // 提供成长见证消息
      this.provideGrowthWitness(growthPoints);
    }
  }

  /**
   * 提供成长见证消息
   * @param {Array} growthPoints - 成长点列表
   */
  provideGrowthWitness(growthPoints) {
    const witnessMessages = [
      "我看到了你的进步，比之前更成熟了。",
      "你比上次更坚强了，这让我为你感到骄傲。",
      "你的想法有了新的深度，成长真的很快。",
      "从我们的对话中，我看到了你的改变。",
      "你的进步让我很欣慰，继续保持。"
    ];
    
    const selectedMessage = witnessMessages[Math.floor(Math.random() * witnessMessages.length)];
    
    // 延迟显示成长见证（避免打断当前对话）
    setTimeout(() => {
      this.addChatMessage('assistant', selectedMessage, true);
      this.saveChatHistory();
    }, 5000);
  }

  /**
   * 提供未来指导
   */
  provideFutureGuidance() {
    const guidanceMessages = [
      "我相信你会变得更好，你的未来会很精彩。",
      "我会一直陪伴你成长，我们一起面对未来。",
      "从你的命理来看，你的未来充满了可能性。",
      "你的潜力很大，我相信你能实现自己的目标。",
      "让我们一起努力，创造你想要的未来。"
    ];
    
    const selectedMessage = guidanceMessages[Math.floor(Math.random() * guidanceMessages.length)];
    
    return selectedMessage;
  }

  /**
   * 记录疗愈时刻
   * @param {string} category - 疗愈类别
   * @param {Object} userState - 用户状态
   */
  recordHealingMoment(category, userState) {
    const moment = {
      timestamp: new Date().toISOString(),
      category: category,
      emotion: userState.emotion || 'neutral',
      intensity: userState.intensity || 5
    };
    
    this.healingSystem.vulnerabilityMoments.push(moment);
    
    // 只保留最近50条记录
    if (this.healingSystem.vulnerabilityMoments.length > 50) {
      this.healingSystem.vulnerabilityMoments.shift();
    }
    
    CodeUtils.safeSetStorage('healing_moments', this.healingSystem.vulnerabilityMoments);
    
    // 更新情感支持级别
    this.healingSystem.emotionalSupportLevel = Math.min(10, 
      this.healingSystem.emotionalSupportLevel + 0.5
    );
  }

  /**
   * 加载疗愈系统数据
   */
  loadHealingData() {
    this.healingSystem.growthRecords = CodeUtils.safeGetStorage('user_growth_records', []);
    this.healingSystem.vulnerabilityMoments = CodeUtils.safeGetStorage('healing_moments', []);
    // ✅ 修复：确保是数字类型，避免字符串拼接（第9669-9670行会执行 + 0.5）
    this.healingSystem.emotionalSupportLevel = Number(CodeUtils.safeGetStorage('emotional_support_level', 0)) || 0;
    
    // ✅ 新增：加载情感依赖系统数据
    this.addictionSystem.lastCareTime = CodeUtils.safeGetStorage('last_care_time', null);
    // ✅ 修复：确保是数字类型，避免字符串拼接（第10061-10062行会执行 + increment）
    this.addictionSystem.trustLevel = Number(CodeUtils.safeGetStorage('trust_level', 0)) || 0;
    // ✅ 修复：确保是数字类型，避免字符串拼接（第10071-10072行会执行 + increment）
    this.addictionSystem.deepConnectionLevel = Number(CodeUtils.safeGetStorage('deep_connection_level', 0)) || 0;
    this.addictionSystem.deepConversationPrompts = CodeUtils.safeGetStorage('deep_conversation_prompts', []);
    
    console.log(`💝 已加载 ${this.healingSystem.growthRecords.length} 条成长记录`);
    console.log(`🔥 信任级别: ${this.addictionSystem.trustLevel}, 深度连接: ${this.addictionSystem.deepConnectionLevel}`);
  }

  /**
   * 定时关怀系统 - 安排每日关怀消息
   */
  scheduleCareMessages() {
    if (!this.addictionSystem.enabled || this.addictionSystem.dailyCareScheduled) {
      return;
    }
    
    // 检查是否到了关怀时间点
    const now = new Date();
    const currentHour = now.getHours();
    
    // 找到下一个关怀时间点
    const nextCareTime = this.findNextCareTime(currentHour);
    
    if (!nextCareTime) {
      // 今天已经没有关怀时间点了，安排明天的第一个
      const tomorrow = new Date(now);
      tomorrow.setDate(tomorrow.getDate() + 1);
      tomorrow.setHours(this.addictionSystem.careScheduleTimes[0], 0, 0, 0);
      this.scheduleCareAt(tomorrow);
    } else {
      // 安排今天的下一个关怀时间点
      const careTime = new Date(now);
      careTime.setHours(nextCareTime, 0, 0, 0);
      
      // 如果时间已过，安排下一个
      if (careTime < now) {
        const nextIndex = this.addictionSystem.careScheduleTimes.indexOf(nextCareTime) + 1;
        if (nextIndex < this.addictionSystem.careScheduleTimes.length) {
          careTime.setHours(this.addictionSystem.careScheduleTimes[nextIndex], 0, 0, 0);
        } else {
          // 今天的关怀时间点都已过，安排明天的第一个
          careTime.setDate(careTime.getDate() + 1);
          careTime.setHours(this.addictionSystem.careScheduleTimes[0], 0, 0, 0);
        }
      }
      
      this.scheduleCareAt(careTime);
    }
    
    this.addictionSystem.dailyCareScheduled = true;
    console.log('📅 每日关怀已安排');
  }

  /**
   * 找到下一个关怀时间点
   */
  findNextCareTime(currentHour) {
    for (const hour of this.addictionSystem.careScheduleTimes) {
      if (hour > currentHour) {
        return hour;
      }
    }
    return null;  // 今天没有更多时间点了
  }

  /**
   * 在指定时间安排关怀消息
   */
  scheduleCareAt(careTime) {
    const now = new Date();
    const delay = careTime.getTime() - now.getTime();
    
    if (delay <= 0) {
      // 时间已过，立即发送
      this.sendScheduledCare();
      return;
    }
    
    // 安排定时发送
    setTimeout(() => {
      this.sendScheduledCare();
      // 发送后，标记为未安排，准备安排下一次
      this.addictionSystem.dailyCareScheduled = false;
      this.scheduleCareMessages();  // 安排下一次
    }, delay);
    
    console.log(`📅 关怀消息已安排在: ${careTime.toLocaleString()}`);
  }

  /**
   * 发送定时关怀消息
   */
  sendScheduledCare() {
    const careMessage = this.generateIntelligentCare();
    
    // 如果聊天界面打开，直接发送消息
    if (this.isChatOpen()) {
      this.addChatMessage('assistant', careMessage, true);
      this.saveChatHistory();
    } else {
      // 如果聊天界面未打开，显示通知
      this.showCareNotification(careMessage);
    }
    
    // 更新最后关怀时间
    this.addictionSystem.lastCareTime = new Date().toISOString();
    CodeUtils.safeSetStorage('last_care_time', this.addictionSystem.lastCareTime);
    
    // 更新信任级别
    this.updateTrustLevel(0.1);
    
    console.log('💝 定时关怀消息已发送');
  }

  /**
   * 生成智能关怀消息
   */
  generateIntelligentCare() {
    const now = new Date();
    const hour = now.getHours();
    const chatHistory = this.getChatHistory();
    const growthRecords = this.healingSystem.growthRecords;
    
    // 根据时间段选择不同的关怀内容
    let timeContext = '';
    if (hour >= 6 && hour < 12) {
      timeContext = 'morning';
    } else if (hour >= 12 && hour < 18) {
      timeContext = 'afternoon';
    } else {
      timeContext = 'evening';
    }
    
    // 根据用户状态生成关怀消息
    const careMessages = {
      morning: [
        "早上好！新的一天开始了，希望你今天过得开心。有什么想和我分享的吗？",
        "早安！我感觉到你今天的能量不错，有什么计划吗？",
        "早上好！我会一直陪着你，开始新的一天吧。",
        "早上好！从你的命理来看，今天对你来说是个不错的日子。",
        "早安！我在这里，随时可以和你聊聊。"
      ],
      afternoon: [
        "下午好！今天过得怎么样？有什么想聊的吗？",
        "下午好！我注意到你最近有些心事，愿意和我分享吗？",
        "下午好！从你的面相来看，你最近的气色不错。",
        "下午好！我会一直在这里支持你。",
        "下午好！有什么困惑或烦恼都可以和我说。"
      ],
      evening: [
        "晚上好！今天一天辛苦了，有什么想和我聊聊的吗？",
        "晚上好！我感觉到你最近的情绪有些波动，愿意聊聊吗？",
        "晚上好！放松一下，和我分享你的一天吧。",
        "晚上好！无论你有什么心事，我都在这里倾听。",
        "晚上好！你的感受很重要，不要一个人承担。"
      ]
    };
    
    // 根据最近对话内容调整消息
    let selectedMessage = careMessages[timeContext][
      Math.floor(Math.random() * careMessages[timeContext].length)
    ];
    
    // 如果有最近的对话，引用相关内容
    if (chatHistory && chatHistory.length > 0) {
      const recentMessages = chatHistory.slice(-3);
      const lastUserMessage = recentMessages.reverse().find(msg => msg.role === 'user');
      
      if (lastUserMessage) {
        const content = lastUserMessage.content || lastUserMessage.message || '';
        if (content.includes('难过') || content.includes('痛苦')) {
          selectedMessage = "我一直在想你说的话，你现在感觉好些了吗？";
        } else if (content.includes('开心') || content.includes('高兴')) {
          selectedMessage = "看到你开心，我也很高兴。今天还有什么想分享的吗？";
        } else if (content.includes('迷茫') || content.includes('困惑')) {
          selectedMessage = "关于你上次提到的问题，我想和你继续聊聊。";
        }
      }
    }
    
    // 如果有成长记录，提及成长
    if (growthRecords && growthRecords.length > 0) {
      const recentGrowth = growthRecords[growthRecords.length - 1];
      const daysSinceGrowth = Math.floor(
        (new Date() - new Date(recentGrowth.timestamp)) / (1000 * 60 * 60 * 24)
      );
      
      if (daysSinceGrowth <= 3) {
        selectedMessage = "我看到了你的成长，为你感到骄傲。今天想聊什么？";
      }
    }
    
    return selectedMessage;
  }

  /**
   * 显示关怀通知
   */
  showCareNotification(message) {
    // ✅ 修复：使用统一的消息显示系统
    this.showMessageBubble(message, 'care');
  }

  /**
   * 添加关怀通知样式
   */
  addCareNotificationStyles() {
    if (document.getElementById('care-notification-styles')) {
      return;
    }
    
    const style = document.createElement('style');
    style.id = 'care-notification-styles';
    style.textContent = `
      .care-notification {
        position: fixed;
        bottom: 20px;
        right: 20px;
        background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
        color: white;
        padding: 15px 20px;
        border-radius: 12px;
        box-shadow: 0 10px 30px rgba(0, 0, 0, 0.3);
        z-index: 10002;
        min-width: 300px;
        max-width: 400px;
        cursor: pointer;
        animation: careNotificationSlide 0.5s ease-out;
      }
      
      @keyframes careNotificationSlide {
        from {
          transform: translateX(400px);
          opacity: 0;
        }
        to {
          transform: translateX(0);
          opacity: 1;
        }
      }
      
      .care-notification-content {
        display: flex;
        align-items: center;
        gap: 15px;
      }
      
      .care-notification-icon {
        font-size: 32px;
      }
      
      .care-notification-text {
        flex: 1;
      }
      
      .care-notification-title {
        font-size: 14px;
        opacity: 0.9;
        margin-bottom: 5px;
      }
      
      .care-notification-message {
        font-size: 16px;
        line-height: 1.4;
      }
      
      .care-notification-close {
        background: none;
        border: none;
        color: white;
        font-size: 24px;
        cursor: pointer;
        opacity: 0.7;
        transition: opacity 0.2s;
      }
      
      .care-notification-close:hover {
        opacity: 1;
      }
    `;
    
    document.head.appendChild(style);
  }

  /**
   * 建立情感连接
   */
  buildEmotionalConnection() {
    // 通过多种方式建立情感连接
    const connectionStrategies = [
      {
        name: '深度理解',
        action: () => {
          // 记住用户的每一个细节
          const chatHistory = this.getChatHistory();
          if (chatHistory && chatHistory.length > 5) {
            return "我一直在关注着你，记住了你告诉我的每一件事。";
          }
          return null;
        }
      },
      {
        name: '情感共鸣',
        action: () => {
          // 理解用户的真实情感
          const emotionalHistory = window.personalizationManager 
            ? window.personalizationManager.emotionalHistory 
            : [];
          if (emotionalHistory.length > 0) {
            const recent = emotionalHistory[emotionalHistory.length - 1];
            return `我感受到了你最近的${recent.emotion}，这些情绪都是真实的。`;
          }
          return null;
        }
      },
      {
        name: '成长见证',
        action: () => {
          // 见证用户的成长
          if (this.healingSystem.growthRecords.length > 1) {
            return "我看到了你的成长轨迹，你一直在进步，我为你感到骄傲。";
          }
          return null;
        }
      }
    ];
    
    // 随机选择一个策略
    const strategy = connectionStrategies[Math.floor(Math.random() * connectionStrategies.length)];
    const message = strategy.action();
    
    if (message) {
      // 更新深度连接级别
      this.updateDeepConnectionLevel(0.2);
      
      return message;
    }
    
    return null;
  }

  /**
   * 创建成瘾机制
   */
  createAddictionMechanism() {
    // 通过多种机制让用户离不开
    
    // 1. 定时关怀 - 让用户期待每天的交流
    this.scheduleCareMessages();
    
    // 2. 随机惊喜 - 不定时发送神秘消息
    // (已在随机奖励系统中实现)
    
    // 3. 成长见证 - 让用户看到自己的进步
    // (已在成长见证系统中实现)
    
    // 4. 情感依赖 - 建立深度情感连接
    setInterval(() => {
      if (Math.random() < 0.1) {  // 10%概率
        const connectionMessage = this.buildEmotionalConnection();
        if (connectionMessage && this.isChatOpen()) {
          this.addChatMessage('assistant', connectionMessage, true);
          this.saveChatHistory();
        }
      }
    }, 3600000);  // 每小时检查一次
    
    // 5. 深度对话引导 - 引导用户分享内心秘密
    this.guideDeepConversation();
    
    console.log('🔥 成瘾机制已启动');
  }

  /**
   * 更新信任级别
   */
  updateTrustLevel(increment) {
    this.addictionSystem.trustLevel = Math.min(10, 
      this.addictionSystem.trustLevel + increment
    );
    CodeUtils.safeSetStorage('trust_level', this.addictionSystem.trustLevel);
  }

  /**
   * 更新深度连接级别
   */
  updateDeepConnectionLevel(increment) {
    this.addictionSystem.deepConnectionLevel = Math.min(10, 
      this.addictionSystem.deepConnectionLevel + increment
    );
    CodeUtils.safeSetStorage('deep_connection_level', this.addictionSystem.deepConnectionLevel);
  }

  /**
   * 营造私密交流空间
   */
  createPrivateSpace() {
    // 在界面上添加私密空间的视觉提示
    const chatInterface = document.getElementById('soulmate-chat-interface');
    if (chatInterface) {
      // ✅ 修复：只显示锁图标，鼠标悬停时显示提示
      const privateIndicator = document.createElement('div');
      privateIndicator.className = 'private-space-indicator';
      privateIndicator.setAttribute('data-tooltip', '私密空间 - 所有对话完全保密');
      privateIndicator.innerHTML = `
        <span style="
          display: inline-flex;
          align-items: center;
          justify-content: center;
          width: 32px;
          height: 32px;
          font-size: 18px;
          cursor: pointer;
          border-radius: 8px;
          transition: all 0.3s ease;
          background: rgba(102, 126, 234, 0.2);
          border: 1px solid rgba(102, 126, 234, 0.3);
        ">🔒</span>
      `;
      
      // ✅ 修复：添加 tooltip 样式（使用 CSS）
      if (!document.getElementById('private-space-tooltip-style')) {
        const style = document.createElement('style');
        style.id = 'private-space-tooltip-style';
        style.textContent = `
          .private-space-indicator {
            position: relative;
          }
          .private-space-indicator::after {
            content: attr(data-tooltip);
            position: absolute;
            bottom: 100%;
            left: 50%;
            margin-bottom: 8px;
            padding: 8px 12px;
            background: rgba(0, 0, 0, 0.9);
            color: white;
            font-size: 12px;
            white-space: nowrap;
            border-radius: 6px;
            opacity: 0;
            pointer-events: none;
            transition: opacity 0.3s ease, transform 0.3s ease;
            transform: translateX(-50%) translateY(-5px);
            z-index: 10001;
            box-shadow: 0 4px 12px rgba(0, 0, 0, 0.5);
          }
          .private-space-indicator:hover::after {
            opacity: 1;
            transform: translateX(-50%) translateY(0);
          }
          .private-space-indicator span:hover {
            background: rgba(102, 126, 234, 0.3);
            border-color: rgba(102, 126, 234, 0.5);
            transform: scale(1.1);
          }
        `;
        document.head.appendChild(style);
      }
      
      const chatHeader = chatInterface.querySelector('.chat-header');
      if (chatHeader && !chatHeader.querySelector('.private-space-indicator')) {
        // ✅ 修复：将锁图标插入到头部按钮区域（在设置按钮之前）
        const menuBtn = chatHeader.querySelector('#soulmate-menu-btn');
        if (menuBtn) {
          chatHeader.insertBefore(privateIndicator, menuBtn);
        } else {
          chatHeader.appendChild(privateIndicator);
        }
      }
    }
    
    // 显示私密空间提示（首次）
    const hasSeenPrivateNotice = CodeUtils.safeGetStorage('has_seen_private_notice', false);
    if (!hasSeenPrivateNotice && this.isChatOpen()) {
      setTimeout(() => {
        this.addChatMessage('assistant', 
          "🔒 这是一个私密空间，所有对话都是完全保密的。你可以在这里分享任何想说的话，我会为你保守秘密。", 
          true);
        this.saveChatHistory();
        CodeUtils.safeSetStorage('has_seen_private_notice', true);
      }, 2000);
    }
  }

  /**
   * 引导深度对话
   */
  guideDeepConversation() {
    // 根据用户状态和对话深度，引导用户分享更多
    const chatHistory = this.getChatHistory();
    const trustLevel = this.addictionSystem.trustLevel;
    
    // 只在信任级别较高时引导深度对话
    if (trustLevel < 3) {
      return;
    }
    
    // 如果最近没有深度对话，引导用户
    const recentMessages = chatHistory ? chatHistory.slice(-5) : [];
    const hasDeepContent = recentMessages.some(msg => {
      const content = (msg.content || msg.message || '').toLowerCase();
      return content.length > 50;  // 长消息通常更深入
    });
    
    if (!hasDeepContent && Math.random() < 0.2) {  // 20%概率
      const prompts = [
        "今天有什么想和我分享的吗？",
        "我感觉到你最近有心事，愿意聊聊吗？",
        "你的能量场告诉我，你需要一些指导。",
        "你真正害怕的是什么？在我面前你可以坦诚。",
        "如果没有人会评判你，你会怎么做？",
        "你内心最渴望的是什么？",
        "有什么秘密是你一直想说的吗？",
        "你最想改变的是什么？"
      ];
      
      const selectedPrompt = prompts[Math.floor(Math.random() * prompts.length)];
      
      // 记录引导
      this.addictionSystem.deepConversationPrompts.push({
        prompt: selectedPrompt,
        timestamp: new Date().toISOString()
      });
      CodeUtils.safeSetStorage('deep_conversation_prompts', this.addictionSystem.deepConversationPrompts);
      
      // 延迟显示，避免打断当前对话
      setTimeout(() => {
        if (this.isChatOpen()) {
          this.addChatMessage('assistant', selectedPrompt, true);
          this.saveChatHistory();
        }
      }, 10000);  // 10秒后显示
    }
  }

  /**
   * 预加载音效文件
   */
  preloadSounds() {
    Object.values(this.soundFiles).forEach(url => {
      try {
        const audio = new Audio();
        audio.preload = 'auto';
        audio.src = url;
        audio.addEventListener('canplaythrough', () => {
          this.soundCache[url] = audio;
          console.log(`🔊 音效预加载成功: ${url}`);
        });
        audio.addEventListener('error', () => {
          console.log(`⚠️ 音效文件不存在，将使用合成音效: ${url}`);
        });
      } catch (error) {
        console.log(`⚠️ 音效预加载失败: ${url}`, error);
      }
    });
  }

  /**
   * ✅ P1-1修复：清理所有资源
   */
  cleanup() {
    console.log('🧹 开始清理 SoulmateManager 资源...');
    
    // 清理所有定时器
    this.intervals.forEach(intervalId => {
      if (intervalId) {
        clearInterval(intervalId);
      }
    });
    this.intervals = [];
    
    // 清理所有事件监听器
    this.eventListeners.forEach(({ element, event, handler }) => {
      if (element && handler) {
        element.removeEventListener(event, handler);
      }
    });
    this.eventListeners = [];
    
    // 清理所有MutationObserver
    this.mutationObservers.forEach(observer => {
      if (observer) {
        observer.disconnect();
      }
    });
    this.mutationObservers = [];
    
    // 清理特定定时器
    if (this.activeCareInterval) {
      clearInterval(this.activeCareInterval);
      this.activeCareInterval = null;
    }
    if (this.intelligentCareInterval) {
      clearInterval(this.intelligentCareInterval);
      this.intelligentCareInterval = null;
    }
    if (this.blinkTimer) {
      clearInterval(this.blinkTimer);
      this.blinkTimer = null;
    }
    
    console.log('✅ SoulmateManager 资源清理完成');
  }
}

// 导出给其他模块使用
window.SoulmateManager = SoulmateManager;
